import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Play, Pause, Upload, ListMusic, Mic, MicOff, X, Volume2, VolumeX, Loader2,
  Timer, Trophy, StopCircle, RotateCcw, Filter, BookOpen, ChevronDown,
  HelpCircle, Keyboard, Music, Bell, Zap, Target, Sparkles, Users, Layers,
  LogIn, LogOut, Cloud, Info, SkipBack, Shield
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ChrisTurnerMode from "@/components/ChrisTurnerMode";
import MusicPicker from "@/components/MusicPicker";
import AdminBeats from "@/components/AdminBeats";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mode definitions
const MODES = {
  freestyle: { id: "freestyle", label: "Freestyle", description: "Mot seul" },
  perso: { id: "perso", label: "Perso", description: "Ta liste personnelle" },
  rhymes: { id: "rhymes", label: "Rimes", description: "4 rimes suggérées" },
  semantic: { id: "semantic", label: "Sémantique", description: "4 mots du même champ" },
  context: { id: "context", label: "Contexte", description: "4 mots décrivant qui a choisi ce mot" },
  punchline: { id: "punchline", label: "Punch-line", description: "Rime au micro en temps réel" },
  talking: { id: "talking", label: "Flow", description: "Parle le plus longtemps possible" },
  banques: { id: "banques", label: "Banques", description: "Apprends les 25 banques de sons" },
};

// 25 sound banks for freestyle
const BANQUES_DATA = [
  { id: 31, name: "-é / -er / -ai", mots1: ["blé", "thé", "clé", "fée", "né", "pré", "ré", "dé", "gré", "nez", "chez", "les", "mes", "mai", "vrai", "paix", "pied", "lait", "frais", "quai"], mots2: ["aimer", "chanter", "rêver", "parler", "penser", "donner", "trouver", "frapper", "voler", "bouger", "passer", "changer", "passé", "percé", "fumé", "donné", "aimé", "raté", "brisé", "foncé", "panier", "papier", "fierté", "beauté", "clarté", "été", "pitié", "jamais", "français", "succès"], mots3: ["exister", "arriver", "recommandé", "captivé", "exilé", "décidé", "convoqué", "compliqué", "liberté", "vérité", "dégainé", "enflammé"] },
  { id: 1, name: "-tion / -sion", mots1: [], mots2: ["fiction", "pulsion", "mission", "friction", "faction", "passion", "gestion", "option", "motion", "section"], mots3: ["composition", "dépression", "acclamation", "destination", "concertation", "transformation", "affectation", "improvisation", "dissociation", "émancipation"] },
  { id: 2, name: "-age", mots1: ["mage", "rage", "cage", "sage", "gage", "plage", "nage", "page", "âge", "stage"], mots2: ["glanage", "mixage", "partage", "brassage", "ombrage", "otage", "tissage", "zonage", "bagage", "orage"], mots3: ["vernissage", "embrouillage", "toilettage", "métissage", "déréglage", "papotage", "décalage", "sauvetage", "étalage", "arrosage"] },
  { id: 3, name: "-ique", mots1: ["brick", "chic", "clic", "flic", "pic"], mots2: [], mots3: ["sismique", "tunique", "sonique", "graphique", "nautique", "logique", "magique", "critique", "mystique", "tactique", "apoplectique", "prolifique", "analogique", "aquatique", "énigmatique", "élastique", "systématique", "exotique", "authentique", "fantastique"] },
  { id: 4, name: "-eur", mots1: ["fleur", "coeur", "soeur", "tueur", "sueur", "pleur", "joueur", "peur", "lueur", "heure"], mots2: ["chasseur", "candeur", "erreur", "horreur", "vecteur", "stupeur", "meneur", "bonheur", "malheur", "douceur", "bienfaiteur", "splendeur"], mots3: ["provocateur", "éclaireur", "randonneur", "auditeur", "intérieur", "sénateur", "rédempteur", "profondeur"] },
  { id: 5, name: "-ance / -ence", mots1: ["lance", "chance", "danse", "transe", "france"], mots2: ["fragrance", "jouvence", "démence", "latence", "brillance", "présence", "errance", "cadence", "prudence", "sentence"], mots3: ["obéissance", "interdépendance", "luminescence", "surabondance", "véhémence", "exigence", "descendance", "déficience", "ascendance", "conséquence"] },
  { id: 6, name: "-ment", mots1: ["fragment", "segment", "ciment", "tourment", "moment", "serment", "vraiment", "comment", "paiement"], mots2: ["firmament", "tremblement"], mots3: ["soulignement", "surclassement", "enchantement", "regroupement", "acharnement", "effondrement", "balancement", "enseignement", "rapprochement"] },
  { id: 7, name: "-al", mots1: ["bal", "mal", "cal", "val", "dal", "royal"], mots2: ["spectral", "brutal", "cristal", "rural", "vocal", "signal", "métal", "cheval", "canal", "mondial"], mots3: ["animal", "illégal", "antisocial", "libéral", "inaugural", "tropical", "vertical", "principal", "arsenal"] },
  { id: 8, name: "-ère / -aire", mots1: ["faire", "ère", "chère", "taire", "mère", "maire", "père", "frère", "terre", "verre"], mots2: ["bergère", "précaire", "affaire", "repère", "colère", "misère", "lumière", "poussière", "salaire", "contraire", "dernière"], mots3: ["réverbère", "bénéficiaire", "arbitraire", "secondaire", "révolutionnaire", "salutaire", "élémentaire", "atmosphère", "caractère"] },
  { id: 9, name: "-ure", mots1: ["mur", "dur", "sur", "pur", "cure"], mots2: ["rainure", "droiture", "teinture", "verdure", "texture", "facture", "voilure", "mesure", "blessure", "capture", "sculpture"], mots3: ["envergure", "aventure", "imposture", "architecture", "progéniture", "enluminure", "couverture", "nourriture", "ouverture"] },
  { id: 10, name: "-ème / -ême", mots1: ["même", "blême", "thème", "crème", "sème", "poème", "diadème"], mots2: ["barème", "système", "suprême", "extrême", "emblème", "baptême", "bohème", "problème", "carême", "septième", "cinquième", "troisième", "deuxième", "neuvième", "quatrième", "millième"], mots3: ["écosystème", "anathème"] },
  { id: 11, name: "-i / -ie / -is", mots1: ["pli", "cri", "prix", "bris", "nid", "lit", "vie", "pluie", "nuit", "bruit"], mots2: ["défi", "pari", "génie", "envie", "magie", "folie", "survie", "merci", "poésie"], mots3: ["alibi", "paradis", "appesanti", "converti", "mélancolie", "cartographie", "nostalgie", "philosophie", "prophétie", "anarchie", "galaxie"] },
  { id: 12, name: "-oir / -oire", mots1: ["voir", "gloire", "croire", "soir", "noir", "choir", "boire"], mots2: ["miroir", "prévoir", "devoir", "vouloir", "pouvoir", "avoir", "espoir", "mémoire", "victoire", "histoire"], mots3: ["répertoire", "trajectoire", "réservoir", "moratoire", "oratoire", "dérisoire", "recevoir", "auditoire", "territoire", "observatoire"] },
  { id: 13, name: "-isme", mots1: ["prisme", "schisme", "séisme"], mots2: ["lyrisme", "cynisme", "charisme", "civisme", "mutisme", "laxisme", "réalisme", "sadisme", "tourisme", "stoïcisme"], mots3: ["brutalisme", "surréalisme", "populisme", "urbanisme", "hypnotisme", "fanatisme", "optimisme", "narcissisme", "conformisme"] },
  { id: 14, name: "-able", mots1: ["table", "fable", "sable", "fiable", "viable"], mots2: ["palpable", "coupable", "capable", "notable", "comptable", "durable", "rentable", "aimable"], mots3: ["irresponsable", "insoutenable", "inacceptable", "insondable", "insurmontable", "impitoyable", "irréprochable", "favorable", "redoutable", "inévitable"] },
  { id: 15, name: "-iste", mots1: ["piste", "triste", "liste", "pianiste"], mots2: ["artiste", "existe", "styliste", "altruiste", "puriste", "fleuriste", "juriste", "réaliste"], mots3: ["utopiste", "illusionniste", "protagoniste", "marionnettiste", "optimiste", "alchimiste", "activiste", "finaliste", "opportuniste"] },
  { id: 16, name: "-on", mots1: ["ton", "front", "fond", "rond", "don", "son", "bond", "pont", "long", "nom"], mots2: ["poumon", "maison", "grillon", "plafond", "talon", "prison", "raison", "saison", "garçon", "chanson"], mots3: ["cargaison", "papillon", "trahison", "abandon", "caméléon", "tourbillon", "hérisson", "forgeron", "échelon", "horizon"] },
  { id: 17, name: "-an / -ant / -en", mots1: ["sang", "grand", "quand", "rang", "gang", "clan", "vent", "temps", "dent", "cent", "géant"], mots2: ["courant", "orient", "garant", "vibrant", "instant", "vivant", "enfant", "errant", "volcan"], mots3: ["étincelant", "aveuglant", "évanescent", "agonisant", "caressant", "commandant", "conquérant", "fascinant", "intrigant", "surprenant"] },
  { id: 18, name: "-ou", mots1: ["trou", "flou", "tout", "doux", "bout", "clou", "coup", "fou", "goût", "loup", "voyou"], mots2: ["écrou", "filou", "atout", "genou", "bijou", "verrou", "caillou", "tabou", "bambou"], mots3: ["aigre-doux", "loup-garou", "contrecoup", "garde-fou", "rendez-vous", "manitou", "caribou"] },
  { id: 19, name: "-aine / -ène", mots1: ["plaine", "pleine", "gène", "haine", "veine", "scène", "graine", "reine", "chaîne", "traîne"], mots2: ["urbaine", "soudaine", "arène", "mécène", "semaine", "fontaine", "certaine", "prochaine", "centaine", "dizaine", "migraine"], mots3: ["porcelaine", "phénomène", "hétérogène", "quarantaine", "indigène", "souterraine", "contemporaine", "oxygène", "capitaine"] },
  { id: 20, name: "-ace", mots1: ["place", "race", "trace", "face", "glace", "grâce"], mots2: ["espace", "rosace", "vorace", "surface", "fugace", "audace", "palace", "sagace", "menace", "grimace", "tenace", "coriace", "prédace"], mots3: ["dédicace", "carapace", "interface", "efficace", "populace", "perspicace", "contumace", "boniface"] },
  { id: 21, name: "-ine", mots1: ["mine", "fine", "dîne", "rine", "bine"], mots2: ["sourdine", "cuisine", "routine", "glycine", "combine", "sanguine", "machine", "vitrine", "usine", "colline"], mots3: ["limousine", "discipline", "figurine", "orpheline", "sérotonine", "illumine", "trampoline", "adrénaline", "clandestine", "cristalline"] },
  { id: 22, name: "-ose", mots1: ["chose", "dose", "ose", "prose", "rose"], mots2: ["nécrose", "hypnose", "osmose", "morose", "éclose", "glucose", "viscose", "névrose", "dispose", "symbiose", "virtuose", "grandiose", "arthrose"], mots3: ["cellulose", "apothéose", "métamorphose", "anamorphose", "ecchymose", "tuberculose", "overdose"] },
  { id: 23, name: "-ame / -âme", mots1: ["rame", "trame", "drame", "blâme", "âme", "lame", "flamme", "diaphragme"], mots2: ["sésame", "paname", "programme", "réclame", "madame", "infâme", "entame", "vacarme"], mots3: ["mélodrame", "amalgame", "télégramme", "anagramme", "panorama", "psychodrame"] },
  { id: 24, name: "-ide", mots1: ["vide", "guide", "ride", "bride", "slide"], mots2: ["acide", "liquide", "sordide", "rapide", "solide", "timide", "perfide", "candide", "limpide", "valide"], mots3: ["chrysalide", "intrépide", "apatride", "translucide", "fratricide", "pyramide", "parricide", "insecticide", "génocide", "homicide"] },
  { id: 25, name: "-ette / -ête", mots1: ["tête", "fête", "bête", "dette", "quête", "crête", "faîte", "prête", "nette", "chouette", "violette"], mots2: ["facette", "baguette", "casquette", "navette", "recette", "planète", "raquette", "trompette", "fourchette", "silhouette", "banquette", "pirouette"], mots3: ["vaguelette", "marionnette", "clarinette", "maisonnette", "bicyclette", "amulette"] },
  { id: 26, name: "-a", mots1: ["bas", "cas", "tas", "pas", "gras", "bras", "chat", "rat", "plat", "mât", "drap", "las", "ça", "là", "va"], mots2: ["tabac", "combat", "débat", "contrat", "climat", "repas", "soldat", "verglas", "appât", "mandat"], mots3: ["chocolat", "attentat", "candidat", "syndicat", "pyjama", "cinéma", "opéra", "résultat", "odorat", "embarras"] },
  { id: 27, name: "-il", mots1: ["il", "cil", "fil", "mil", "vil", "bile", "file", "pile", "style", "île", "ville", "mille", "gril", "Nil"], mots2: ["exil", "avril", "péril", "civil", "profil", "Brésil", "utile", "hostile", "agile", "facile"], mots3: ["imbécile", "difficile", "volatile", "domicile", "automobile", "crocodile", "projectile", "puérile"] },
  { id: 28, name: "-ende / -ande", mots1: ["bande", "lande", "grande", "mande", "rende", "tende", "fende", "pende", "vende"], mots2: ["commande", "demande", "légende", "amande", "amende", "offrande", "guirlande", "friande", "viande", "attende", "gourmande", "marchande"], mots3: ["allemande", "propagande", "réprimande", "contrebande", "mémorande"] },
  { id: 29, name: "-estre", mots1: ["être", "maître", "hêtre", "prêtre", "traître", "naître"], mots2: ["équestre", "pédestre", "terrestre", "sylvestre", "alpestre", "semestre", "trimestre", "orchestre", "séquestre", "ancestre", "campestre", "bimestre"], mots3: [] },
  { id: 30, name: "-erle", mots1: ["merle", "perle", "berle", "gerle", "ferle"], mots2: ["déferle", "emperle"], mots3: [] },
];

// Rap syllable counting (synérèse by default).
// Counts vowel clusters (consecutive vowels = 1 syllable),
// with silent final "-e" when preceded by a consonant,
// and with hiatus splitting when é/ê/è is followed by another strong vowel.
// Examples:
//   lion -> 1 (ion after consonant l)
//   pion -> 1
//   hier -> 1
//   action -> 2 (a + tion)
//   nation -> 2
//   tueur -> 1 (ueu cluster)
//   joueur -> 1
//   table -> 1 (silent final e after consonant)
//   amie -> 2 (a + ie, no decrement since previous char is vowel)
//   vie -> 1
//   création -> 3 (cré + a + tion, hiatus on é+a)
function rapSyllabes(mot) {
  if (!mot || typeof mot !== 'string') return 0;
  let w = mot.toLowerCase().trim();
  w = w.replace(/[^a-zàâäéèêëîïôöùûüÿœæç]/g, '');
  if (!w) return 0;
  // Silent "u" in "qu" and "gu" digraphs when followed by e/i/y (incl. accents).
  // Examples: "sismique" → "sismiqe" (2 syl), "guide" → "gide" (1 syl).
  // "qua/quo" are NOT silent (aquatique /akwatik/), so we only strip before e/i/y.
  w = w.replace(/qu([eiyéèêë])/g, 'q$1').replace(/gu([eiyéèêë])/g, 'g$1');
  const vowels = 'aeiouyàâäéèêëîïôöùûüÿœæ';
  // Strong vowels that create hiatus with the next vowel (é, è, ê) when followed by another vowel
  const hiatusBreakers = 'éèê';
  let count = 0;
  let inVowelGroup = false;
  let prevChar = '';
  for (const ch of w) {
    const isVowel = vowels.includes(ch);
    if (isVowel) {
      if (!inVowelGroup) {
        count++;
        inVowelGroup = true;
      } else if (hiatusBreakers.includes(prevChar) || hiatusBreakers.includes(ch)) {
        // Hiatus: é/è/ê creates a syllable split with any adjacent vowel
        // (poème = po-ème, création = cré-a-tion, réel = ré-el)
        count++;
      }
    } else {
      inVowelGroup = false;
    }
    prevChar = ch;
  }
  // Silent final "e" when preceded by a consonant (table -> 1, fable -> 1)
  // or by é/è/ê (idée -> 2, poème -> 2, année -> 2).
  // NOT silent when preceded by other vowels (amie -> 2, vie -> 1).
  if (count > 1 && w.endsWith('e')) {
    const prev = w.slice(-2, -1);
    if (!vowels.includes(prev) || 'éèê'.includes(prev)) count--;
  }
  return Math.max(1, count);
}

// Pronunciation hints for speech synthesis
const PHONEME_PRONUNCIATION = {
  "-tion / -sion": "sion, sion",
  "-age": "âge",
  "-ique": "ique",
  "-eur": "eurre",
  "-ance / -ence": "ance, ence",
  "-ment": "ment",
  "-al": "al",
  "-ère / -aire": "ère, ère",
  "-ure": "ure",
  "-ème / -ême": "emme",
  "-i / -ie / -is": "i, ie",
  "-oir / -oire": "oire, oire",
  "-isme": "isme",
  "-able": "âble",
  "-iste": "iste",
  "-on": "on",
  "-an / -ant / -en": "an, en",
  "-ou": "ou",
  "-aine / -ène": "aine, ène",
  "-ace": "asse",
  "-ine": "inne",
  "-ose": "oze",
  "-ame / -âme": "âme",
  "-ide": "ide",
  "-ette / -ête": "ette, ête",
  "-a": "âh",
  "-il": "ile",
  "-ende / -ande": "ende, ande",
  "-estre": "estre",
  "-erle": "erle",
  "-é / -er / -ai": "é, é",
};

// Session/Challenge presets
const SESSION_DURATIONS = [60, 120, 180, 300]; // 1min, 2min, 3min, 5min
const CHALLENGE_DURATION = 60; // 60 seconds for challenge mode

// Default beat
const DEFAULT_BEAT = {
  name: "Midnight Sirens - G-Funk Beat",
  url: "https://customer-assets.emergentagent.com/job_freestyle-words/artifacts/zwp1zgjx_Hard%20West%20Coast%20Type%20Beat%20%20Midnight%20Sirens%20_%20Dark%20G-Funk%20Rap%20Instrumental.mp3"
};

// Default filler phrases
const DEFAULT_FILLERS = [
  "tu vois ce que je veux dire",
  "c'est comme ça que",
  "au bout du compte",
  "laisse-moi te dire que",
  "écoute bien ce que je dis",
  "dans le fond tu sais que",
  "je te parle de",
  "c'est pas pour rien que",
  "regarde autour de toi",
  "on est là pour ça",
  "à la fin de la journée",
  "faut que tu comprennes que",
  "moi je viens te dire que",
  "c'est la vérité quand",
  "tu peux pas nier que",
  "je pose ça là",
  "attends un peu que",
  "c'est le moment de",
  "y'a pas de doute que",
  "je suis là pour te montrer",
];

// Built-in beats library organized by BPM range
const BUILT_IN_BEATS = [
  { name: "Lo-fi Chill", bpm: 50, url: "/beats/lofi-chill-50bpm.mp3", style: "Lo-fi" },
  { name: "70BPM BEAT", bpm: 70, url: "/beats/hard-devil-70bpm.mp3", style: "Hard Type" },
  { name: "80BPM BEAT", bpm: 80, url: "/beats/80bpm.mp3", style: "Hip-Hop" },
  { name: "Boom Bap Lent", bpm: 75, url: "/beats/boom-bap-75bpm.mp3", style: "Boom Bap" },
  { name: "Classic Hip-Hop", bpm: 85, url: "/beats/classic-hiphop-85bpm.mp3", style: "Boom Bap" },
  { name: "Boom Bap Rapide", bpm: 95, url: "/beats/boom-bap-95bpm.mp3", style: "Boom Bap" },
  { name: "Trap", bpm: 110, url: "/beats/trap-110bpm.mp3", style: "Trap" },
];

// Find the closest built-in beat for a given BPM
const findClosestBeat = (targetBpm) => {
  let closest = BUILT_IN_BEATS[0];
  let minDiff = Math.abs(targetBpm - closest.bpm);
  for (const beat of BUILT_IN_BEATS) {
    const diff = Math.abs(targetBpm - beat.bpm);
    if (diff < minDiff) {
      minDiff = diff;
      closest = beat;
    }
  }
  return closest;
};

function App() {
  // ============ AUTH STATE ============
  const [authUser, setAuthUser] = useState(null); // { user_id, email, name, picture }
  const [authLoading, setAuthLoading] = useState(true);

  // Handle OAuth callback (session_id in URL hash) + check existing session
  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    let cancelled = false;

    // Check for session_id in hash OR pending from a StrictMode re-mount
    const hash = window.location.hash;
    let sessionId = null;
    if (hash && hash.includes("session_id=")) {
      const params = new URLSearchParams(hash.substring(1));
      sessionId = params.get("session_id");
      // Save for potential StrictMode re-mount, then clear hash
      if (sessionId) localStorage.setItem("ff_pending_session", sessionId);
      window.history.replaceState(null, "", window.location.pathname);
    } else {
      sessionId = localStorage.getItem("ff_pending_session");
    }

    const processAuth = async () => {
      if (sessionId) {
        console.log("[Auth] Exchanging session_id, length:", sessionId.length);
        try {
          const resp = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          if (!cancelled) {
            localStorage.removeItem("ff_pending_session");
            const { token, ...userData } = resp.data;
            localStorage.setItem("ff_auth_token", token);
            setAuthUser(userData);
            toast.success(`Connecté en tant que ${userData.name}`);
          }
        } catch (e) {
          if (!cancelled) {
            localStorage.removeItem("ff_pending_session");
            console.error("[Auth] Exchange failed:", e?.response?.status, e?.response?.data);
            toast.error("Échec de la connexion Google. Réessayez.");
          }
        }
        if (!cancelled) setAuthLoading(false);
        return;
      }
      // Check existing session via stored token
      const storedToken = localStorage.getItem("ff_auth_token");
      if (storedToken) {
        try {
          const resp = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          if (!cancelled) setAuthUser(resp.data);
        } catch {
          if (!cancelled) localStorage.removeItem("ff_auth_token");
        }
      }
      if (!cancelled) setAuthLoading(false);
    };
    processAuth();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("ff_auth_token");
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch {}
    localStorage.removeItem("ff_auth_token");
    setAuthUser(null);
    toast.info("Déconnecté");
  };

  // Word state
  const [words, setWords] = useState([]);
  const [currentWord, setCurrentWord] = useState("");
  const [wordSource, setWordSource] = useState("default");
  const [wordKey, setWordKey] = useState(0);

  // Personal words state
  const [personalWords, setPersonalWords] = useState(() => {
    try {
      const saved = localStorage.getItem('freestyleflow_perso_words');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showPersoEditor, setShowPersoEditor] = useState(false);
  const [persoEditorText, setPersoEditorText] = useState("");
  const [persoActive, setPersoActive] = useState(false);
  const [persoPhase, setPersoPhase] = useState("idle"); // idle, countdown, playing
  const [persoBeat, setPersoBeat] = useState(0); // 1-4 visual beat counter
  const [persoCountdown, setPersoCountdown] = useState(0); // 4,3,2,1
  const persoBeatRef = useRef(0);
  const persoBeatIntervalRef = useRef(null);
  const persoPhaseRef = useRef("idle");

  // Freestyle Rythmique state
  const [freestyleRhythmic, setFreestyleRhythmic] = useState(false);
  const [freestyleRhActive, setFreestyleRhActive] = useState(false);
  const [freestyleRhPhase, setFreestyleRhPhase] = useState("idle"); // idle, countdown, playing
  const [freestyleRhBeat, setFreestyleRhBeat] = useState(0); // 1-8 visual beat counter
  const [freestyleRhCountdown, setFreestyleRhCountdown] = useState(0); // 4,3,2,1
  const freestyleRhBeatRef = useRef(0);
  const freestyleRhIntervalRef = useRef(null);
  const freestyleRhStartDelayRef = useRef(null);
  const freestyleRhPhaseRef = useRef("idle");
  const [freestyleRhNextWord, setFreestyleRhNextWord] = useState("");
  const [freestyleRhLines, setFreestyleRhLines] = useState([]); // array of 4 words for visual display
  const [freestyleRhScrollKey, setFreestyleRhScrollKey] = useState(0);
  const [freestyleRhSubdiv, setFreestyleRhSubdiv] = useState("noire"); // noire, croche, double-croche

  // Freestyle Assisté state (one-shot cycle: TTS → countdown 4 → 8 beats → done)
  const [assisteSelected, setAssisteSelected] = useState(false);
  const [libreMotSelected, setLibreMotSelected] = useState(false);
  const [chrisTurnerSelected, setChrisTurnerSelected] = useState(false);
  const libreMotLastTapRef = useRef(0);
  const [assisteActive, setAssisteActive] = useState(false);
  const [assistePhase, setAssistePhase] = useState("idle"); // idle, speaking, countdown, playing, done
  const [assisteBeat, setAssisteBeat] = useState(0); // 1-8
  const [assisteCountdown, setAssisteCountdown] = useState(0); // 4,3,2,1
  const assisteBeatRef = useRef(0);
  const assisteIntervalRef = useRef(null);
  const assisteTimeoutRef = useRef(null);
  const assistePhaseRef = useRef("idle");

  // Rime Guidée state
  const [rimeGuideeActive, setRimeGuideeActive] = useState(false);
  const [rimeGuideePhase, setRimeGuideePhase] = useState("idle"); // idle, countdown, playing
  const rimeGuideePhaseRef = useRef("idle");
  const [rimeGuideePairs, setRimeGuideePairs] = useState([]); // array of {word1, word2} pairs
  const [rimeGuideeCurrentPair, setRimeGuideeCurrentPair] = useState(0); // which pair (0 or 1)
  const [rimeGuideeCurrentLine, setRimeGuideeCurrentLine] = useState(0); // 0 = first line of pair, 1 = second
  const [rimeGuideeTranscript, setRimeGuideeTranscript] = useState("");
  const [rimeGuideeValidated, setRimeGuideeValidated] = useState([]); // array of booleans per line
  const [rimeGuideeBeat, setRimeGuideeBeat] = useState(0);
  const rimeGuideeBeatRef = useRef(0);
  const rimeGuideeIntervalRef = useRef(null);
  const rimeGuideeStartDelayRef = useRef(null);
  const rimeGuideeRecognitionRef = useRef(null);
  const rimeGuideeTranscriptRef = useRef("");
  const rimeGuideeBaseTranscriptRef = useRef("");
  const [rimeGuideeLastWord, setRimeGuideeLastWord] = useState("");
  const [rimeGuideeScrollKey, setRimeGuideeScrollKey] = useState(0);
  const [rimeGuideeSelected, setRimeGuideeSelected] = useState(false); // toggle selected

  // Filler mode state
  const [fillerSelected, setFillerSelected] = useState(false);
  const [fillerPhrases, setFillerPhrases] = useState(() => {
    try {
      const saved = localStorage.getItem('ff_filler_phrases');
      return saved ? JSON.parse(saved) : DEFAULT_FILLERS;
    } catch { return DEFAULT_FILLERS; }
  });
  const [fillerDialogOpen, setFillerDialogOpen] = useState(false);
  const [fillerEditorText, setFillerEditorText] = useState("");
  const [currentFiller, setCurrentFiller] = useState(""); // current filler phrase displayed
  const [adminBeatsOpen, setAdminBeatsOpen] = useState(false);

  // Cloud sync: load personal words from server when user logs in
  useEffect(() => {
    if (!authUser) return;
    const token = localStorage.getItem("ff_auth_token");
    if (!token) return;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
    const syncWords = async () => {
      try {
        const resp = await axios.get(`${API}/user/words`, authHeaders);
        const cloudWords = resp.data.words || [];
        // Read local words directly from localStorage (avoid stale closure)
        let localWords = [];
        try {
          const saved = localStorage.getItem('freestyleflow_perso_words');
          if (saved) localWords = JSON.parse(saved);
        } catch {}

        if (cloudWords.length > 0) {
          // Cloud has words → use them (cloud is source of truth)
          setPersonalWords(cloudWords);
          localStorage.setItem('freestyleflow_perso_words', JSON.stringify(cloudWords));
          toast.success(`${cloudWords.length} mot${cloudWords.length > 1 ? 's' : ''} chargé${cloudWords.length > 1 ? 's' : ''} depuis le cloud`);
        } else if (localWords.length > 0) {
          // Cloud empty, local has words → push to cloud
          await axios.post(`${API}/user/words`, { words: localWords }, authHeaders);
          toast.success(`${localWords.length} mot${localWords.length > 1 ? 's' : ''} synchronisé${localWords.length > 1 ? 's' : ''} vers le cloud`);
        }
      } catch (e) {
        console.error("Cloud sync failed:", e);
        toast.error("Échec de la synchronisation cloud");
      }
    };
    syncWords();
  }, [authUser]);

  // Cloud sync: load filler phrases when user logs in
  useEffect(() => {
    if (!authUser) return;
    const token = localStorage.getItem("ff_auth_token");
    if (!token) return;
    const syncFillers = async () => {
      try {
        const resp = await axios.get(`${API}/user/fillers`, { headers: { Authorization: `Bearer ${token}` } });
        const cloudFillers = resp.data.phrases || [];
        if (cloudFillers.length > 0) {
          setFillerPhrases(cloudFillers);
        }
      } catch (e) {
        console.log("Filler sync skipped:", e.message);
      }
    };
    syncFillers();
  }, [authUser]);

  // Cloud sync: load custom banque words when user logs in
  useEffect(() => {
    if (!authUser) return;
    const token = localStorage.getItem("ff_auth_token");
    if (!token) return;
    const syncBanqueWords = async () => {
      try {
        const resp = await axios.get(`${API}/user/banque-words`, { headers: { Authorization: `Bearer ${token}` } });
        if (resp.data.banque_words && Object.keys(resp.data.banque_words).length > 0) {
          // Check cloud data version — if user has old corrupted data, clear it
          const cloudVersion = resp.data.version;
          if (cloudVersion !== 'v2') {
            // Old data — don't merge, let defaults apply
            // Clear the old cloud data by sending empty for each banque
            for (const banqueId of Object.keys(resp.data.banque_words)) {
              try {
                await axios.post(`${API}/user/banque-words`, { banque_id: parseInt(banqueId), words: [] }, { headers: { Authorization: `Bearer ${token}` } });
              } catch(e) {}
            }
            console.log("Cleared old cloud banque data");
            return;
          }
          const local = JSON.parse(localStorage.getItem('ff_banque_custom_words') || '{}');
          const merged = { ...local, ...resp.data.banque_words };
          setBanquesCustomWords(merged);
          localStorage.setItem('ff_banque_custom_words', JSON.stringify(merged));
        }
      } catch (e) {
        console.log("Banque words sync skipped:", e.message);
      }
    };
    syncBanqueWords();
  }, [authUser]);

  // Save custom banque words — localStorage + cloud
  const saveBanqueWordsSync = useCallback(async (banqueId, words) => {
    setBanquesCustomWords(prev => {
      const newCustom = { ...prev };
      if (words.length > 0) {
        newCustom[String(banqueId)] = words;
      } else {
        delete newCustom[String(banqueId)];
      }
      localStorage.setItem('ff_banque_custom_words', JSON.stringify(newCustom));
      return newCustom;
    });

    if (authUser) {
      const token = localStorage.getItem("ff_auth_token");
      if (token) {
        try {
          await axios.post(`${API}/user/banque-words`, { banque_id: banqueId, words }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success(`Banque sauvegardée dans le cloud (${words.length} mots)`);
          return;
        } catch (e) {
          console.error("Failed to save banque words:", e);
          toast.error("Erreur cloud — mots sauvegardés localement");
          return;
        }
      }
    }
    toast.info(`Connecte-toi pour synchroniser. (${words.length} mots sauvegardés localement)`);
  }, [authUser]);

  // Save filler phrases to cloud
  const saveFillerPhrasesSync = useCallback(async (newPhrases) => {
    setFillerPhrases(newPhrases);
    localStorage.setItem('ff_filler_phrases', JSON.stringify(newPhrases));
    if (authUser) {
      const token = localStorage.getItem("ff_auth_token");
      if (token) {
        try {
          await axios.post(`${API}/user/fillers`, { phrases: newPhrases }, { headers: { Authorization: `Bearer ${token}` } });
          toast.success(`${newPhrases.length} filler${newPhrases.length > 1 ? 's' : ''} sauvegardé${newPhrases.length > 1 ? 's' : ''}`);
          return;
        } catch (e) {
          console.error("Failed to save fillers to cloud:", e);
          toast.error("Erreur de sauvegarde cloud. Fillers conservés localement.");
          return;
        }
      }
    }
    toast.info(`Connecte-toi pour sauvegarder dans le cloud. (${newPhrases.length} fillers conservés localement)`);
  }, [authUser]);

  // Helper: save personal words both locally and to cloud
  const savePersonalWordsSync = useCallback(async (newWords) => {
    setPersonalWords(newWords);
    localStorage.setItem('freestyleflow_perso_words', JSON.stringify(newWords));
    if (authUser) {
      const token = localStorage.getItem("ff_auth_token");
      if (token) {
        try {
          await axios.post(`${API}/user/words`, { words: newWords }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success(`${newWords.length} mot${newWords.length > 1 ? 's' : ''} sauvegardé${newWords.length > 1 ? 's' : ''} dans le cloud`);
          return;
        } catch (e) {
          console.error("Failed to save words to cloud:", e);
          toast.error("Sauvegarde locale OK, mais la synchronisation cloud a échoué");
          return;
        }
      }
    }
    toast.success(`${newWords.length} mot${newWords.length > 1 ? 's' : ''} sauvegardé${newWords.length > 1 ? 's' : ''} (local uniquement)`);
  }, [authUser]);

  // Mode state
  const [currentMode, setCurrentMode] = useState("freestyle");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const suggestionsCache = useRef({});

  // Context image state
  const [contextImage, setContextImage] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageCache = useRef([]);

  // Session state
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(120); // Default 2 min
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);
  const [sessionWordCount, setSessionWordCount] = useState(0);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const sessionIntervalRef = useRef(null);

  // Challenge state
  const [challengeActive, setChallengeActive] = useState(false);
  const [challengeTimeLeft, setChallengeTimeLeft] = useState(CHALLENGE_DURATION);
  const [challengeScore, setChallengeScore] = useState(0);
  const [challengeHighScore, setChallengeHighScore] = useState(() => {
    const saved = localStorage.getItem("freestyleHighScore");
    return saved ? parseInt(saved) : 0;
  });
  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [showChallengeResult, setShowChallengeResult] = useState(false);
  const challengeIntervalRef = useRef(null);
  const challengeScoreRef = useRef(0); // Ref to track score for end callback

  // Metronome state
  const [bpm, setBpm] = useState(70);
  const [metronomeActive, setMetronomeActive] = useState(false);
  const [beatActive, setBeatActive] = useState(false);
  const metronomeIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  // Dashboard auto-hide
  const [dashboardCollapsed, setDashboardCollapsed] = useState(false);
  const dashboardHideTimerRef = useRef(null);
  const dashboardHoveredRef = useRef(false);
  // When true, the global window listeners do NOT reset the auto-hide.
  // Allows sub-modes (e.g. Chris TURNER) to keep the dashboard collapsed
  // while the user taps on the central content area to advance cards.
  const dashboardAutoHideLockRef = useRef(false);
  const DASHBOARD_AUTO_HIDE_MS = 10000;
  const resetDashboardHide = useCallback(() => {
    if (dashboardHideTimerRef.current) clearTimeout(dashboardHideTimerRef.current);
    setDashboardCollapsed(false);
    if (dashboardHoveredRef.current) return; // pause auto-hide while hovered
    dashboardHideTimerRef.current = setTimeout(() => setDashboardCollapsed(true), DASHBOARD_AUTO_HIDE_MS);
  }, []);
  const handleDashboardEnter = useCallback(() => {
    dashboardHoveredRef.current = true;
    if (dashboardHideTimerRef.current) {
      clearTimeout(dashboardHideTimerRef.current);
      dashboardHideTimerRef.current = null;
    }
    setDashboardCollapsed(false);
  }, []);
  const handleDashboardLeave = useCallback(() => {
    dashboardHoveredRef.current = false;
    if (dashboardHideTimerRef.current) clearTimeout(dashboardHideTimerRef.current);
    dashboardHideTimerRef.current = setTimeout(() => setDashboardCollapsed(true), DASHBOARD_AUTO_HIDE_MS);
  }, []);
  useEffect(() => {
    // Initial schedule + listeners on global interactions
    resetDashboardHide();
    const onActivity = () => {
      if (dashboardAutoHideLockRef.current) return;
      resetDashboardHide();
    };
    window.addEventListener('keydown', onActivity);
    window.addEventListener('mousedown', onActivity);
    window.addEventListener('touchstart', onActivity);
    return () => {
      if (dashboardHideTimerRef.current) clearTimeout(dashboardHideTimerRef.current);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('touchstart', onActivity);
    };
  }, [resetDashboardHide]);

  const [rhythmSource, setRhythmSource] = useState("beat"); // "metronome", "beat", "both"

  // Independent metronome toggle (default OFF), available on top of music in all freestyle submodes
  // Audio source: "music" | "metronome" | "silent". Default "music".
  // Music and metronome are mutually exclusive; silent stops both.
  const [audioSource, setAudioSource] = useState("music");
  const audioSourceRef = useRef("music");
  useEffect(() => { audioSourceRef.current = audioSource; }, [audioSource]);

  // MP3 state - load auto-selected built-in beat by default
  const [mp3File, setMp3File] = useState(() => {
    const beat = findClosestBeat(80);
    return { name: `${beat.name} (${beat.bpm} BPM)`, url: beat.url };
  });
  const [mp3Playing, setMp3Playing] = useState(false);
  const [mp3Progress, setMp3Progress] = useState(0);
  const [mp3Duration, setMp3Duration] = useState(0);
  const [mp3Muted, setMp3Muted] = useState(false);
  const [useBuiltInBeats, setUseBuiltInBeats] = useState(true);
  const [currentBuiltInBeat, setCurrentBuiltInBeat] = useState(() => findClosestBeat(80));
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const userUploadedBeatRef = useRef(null); // store user's uploaded beat when switching to built-in

  // Pause/resume the music beat while the TTS voice is speaking. We use
  // pause()/play() instead of volume-ducking because iOS Safari/Chrome ignore
  // programmatic changes to `audio.volume`. Pause/resume is reliable on every
  // platform and gives an even cleaner "voice solo" feeling. We also restore
  // volume to 1.0 each time to clean up state from previous ducking attempts.
  const beatWasPlayingRef = useRef(false);
  // Persistent music volume (0..1). Defaults to 100%. Sliders update this and
  // the value is applied via a Web Audio API GainNode (so it works on iOS too).
  const [musicVolume, setMusicVolume] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("ff_music_volume"));
      return (Number.isFinite(v) && v >= 0 && v <= 1) ? v : 1;
    } catch { return 1; }
  });
  // Web Audio API graph: <audio> → MediaElementSource → GainNode → destination
  // Created EAGERLY at mount (with the context in "suspended" state) so the
  // MediaElementSource is attached BEFORE any .play() call. Creating the
  // source after play has started is a known cause of stutter/loop glitches
  // on iOS Safari and Chrome.
  const audioCtxRef = useRef(null);
  const audioGainRef = useRef(null);
  const audioSourceNodeRef = useRef(null);
  // Keep a ref to musicVolume so the mount-time effect doesn't capture stale state
  const musicVolumeRef = useRef(musicVolume);
  useEffect(() => { musicVolumeRef.current = musicVolume; }, [musicVolume]);
  useEffect(() => {
    const a = audioRef.current;
    if (!a || audioCtxRef.current) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const src = ctx.createMediaElementSource(a);
      const gain = ctx.createGain();
      gain.gain.value = musicVolumeRef.current;
      src.connect(gain).connect(ctx.destination);
      audioCtxRef.current = ctx;
      audioGainRef.current = gain;
      audioSourceNodeRef.current = src;
    } catch { /* unsupported or already attached */ }
  }, []);
  // Resume/suspend the AudioContext in lockstep with the <audio> element:
  // - On 'play': resume the context (no-op if already running)
  // - On 'pause' or 'ended': suspend the context after a short delay
  //
  // Why suspend on pause? When the beat is paused, the Web Audio graph keeps
  // pushing a silent buffer to the destination, which keeps the Bluetooth
  // A2DP channel open. Some Bluetooth speakers interpret this digital silence
  // as micro-glitches (the user-reported "disque rayé" effect). Suspending
  // the context cleanly closes the audio output until the user plays again.
  //
  // The 200ms delay before suspend prevents flapping during fast pause/play
  // sequences (e.g. Chris TURNER ducking).
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    let suspendTimer = null;
    const cancelSuspend = () => {
      if (suspendTimer) { clearTimeout(suspendTimer); suspendTimer = null; }
    };
    const onPlay = () => {
      cancelSuspend();
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state === "suspended") {
        try { ctx.resume(); } catch { /* noop */ }
      }
    };
    const scheduleSuspend = () => {
      cancelSuspend();
      suspendTimer = setTimeout(() => {
        const ctx = audioCtxRef.current;
        if (ctx && ctx.state === "running" && audioRef.current && audioRef.current.paused) {
          try { ctx.suspend(); } catch { /* noop */ }
        }
      }, 200);
    };
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", scheduleSuspend);
    a.addEventListener("ended", scheduleSuspend);
    return () => {
      cancelSuspend();
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", scheduleSuspend);
      a.removeEventListener("ended", scheduleSuspend);
    };
  }, []);
  // Apply current volume to the gain node whenever it changes
  useEffect(() => {
    if (audioGainRef.current) {
      try { audioGainRef.current.gain.value = musicVolume; } catch { /* noop */ }
    }
    try { localStorage.setItem("ff_music_volume", String(musicVolume)); } catch { /* noop */ }
  }, [musicVolume]);

  const setBeatDuck = useCallback((duckOn) => {
    const a = audioRef.current;
    if (!a) return;
    if (duckOn) {
      // Only pause if the beat was actually playing — don't accidentally
      // resume it later if the user had paused manually.
      beatWasPlayingRef.current = !a.paused;
      if (!a.paused) {
        try { a.pause(); } catch { /* noop */ }
      }
    } else if (beatWasPlayingRef.current) {
      // Resume only if we paused it ourselves
      beatWasPlayingRef.current = false;
      try {
        const p = a.play();
        if (p && typeof p.catch === "function") p.catch(() => { /* user-gesture lost */ });
      } catch { /* noop */ }
    }
  }, []);

  // Dictionary filter state
  const [dictionaryAvailable, setDictionaryAvailable] = useState(false);
  const [dictionaryThemes, setDictionaryThemes] = useState([]);
  const [dictionaryTotal, setDictionaryTotal] = useState(0);
  const [activeTheme, setActiveTheme] = useState("ALL");
  const [activeSyllabes, setActiveSyllabes] = useState(0);
  const [wordCount, setWordCount] = useState(1);
  const [extraWords, setExtraWords] = useState([]);

  // Help dialog
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState(0);
  const [banquesInfoOpen, setBanquesInfoOpen] = useState(false);

  // Punchline mode state
  const [punchlinePhase, setPunchlinePhase] = useState("idle"); // idle, waiting, countdown, verse1, verse2
  const [punchlineResult, setPunchlineResult] = useState(null); // null, "success", "fail", "empty"
  const [transcript, setTranscript] = useState("");
  const [verse1Text, setVerse1Text] = useState("");
  const [punchlineBeat, setPunchlineBeat] = useState(0); // 0=not counting, 1-8 during strophe
  const [countdownNumber, setCountdownNumber] = useState(0); // 4,3,2,1 during countdown
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(() => {
    const saved = localStorage.getItem("freestyleBestStreak");
    return saved ? parseInt(saved) : 0;
  });
  const [isListening, setIsListening] = useState(false);
  const [lastSpokenWord, setLastSpokenWord] = useState("");
  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const punchlinePhaseRef = useRef("idle");
  const punchlineBeatRef = useRef(0);
  const beatIntervalRef = useRef(null);
  const verse1SnapshotRef = useRef("");
  const cooldownRef = useRef(false);
  const baseTranscriptRef = useRef(""); // Accumulated text from previous recognition sessions
  const punchlineResultTimerRef = useRef(null);
  const bestStreakRef = useRef(0);
  const handlePunchlineEvalRef = useRef(null);
  const startPunchlineSequenceRef = useRef(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [micError, setMicError] = useState(null);
  const [vuMeterActive, setVuMeterActive] = useState(false);
  const punchlineAudioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Talking mode state
  const [talkingPhase, setTalkingPhase] = useState("idle"); // idle, listening, done
  const [talkingTime, setTalkingTime] = useState(0); // elapsed ms
  const [talkingBest, setTalkingBest] = useState(() => {
    const saved = localStorage.getItem("freestyleTalkingBest");
    return saved ? parseInt(saved) : 0;
  });
  const [talkingTranscript, setTalkingTranscript] = useState("");
  const talkingRecognitionRef = useRef(null);
  const talkingTimerRef = useRef(null);
  const talkingStartTimeRef = useRef(0);
  const talkingLastSpeechRef = useRef(0);
  const talkingSilenceRef = useRef(null);
  const talkingPhaseRef = useRef("idle");
  const talkingTranscriptRef = useRef("");
  const talkingBaseTranscriptRef = useRef("");

  // Banques mode state
  const [banquesPhase, setBanquesPhase] = useState("idle"); // idle, discovery, recall, freestyle, infinite
  const [banquesGroup, setBanquesGroup] = useState(1); // 1=Prioritaires(1-10), 2=Intermédiaires(11-15), 3=Toutes(1-25)
  const [banquesSelected, setBanquesSelected] = useState(() => BANQUES_DATA.slice(0, 10).map(b => b.id)); // selected banque ids
  const [banquesPickerOpen, setBanquesPickerOpen] = useState(false);
  const banquesPickerRef = useRef(null);
  const banquesPickerRefActive = useRef(null);
  const banquesPickerDropdownRef = useRef(null);
  const [banquesSyllabes, setBanquesSyllabes] = useState(0); // 0=indifférent, 1, 2, 3
  const [banquesCustomWords, setBanquesCustomWords] = useState(() => {
    try {
      const saved = localStorage.getItem('ff_banque_custom_words');
      const version = localStorage.getItem('ff_banque_data_version');
      // Invalidate old custom words from before the data cleanup (v2)
      if (version !== 'v2') {
        localStorage.removeItem('ff_banque_custom_words');
        localStorage.setItem('ff_banque_data_version', 'v2');
        return {};
      }
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [banquesEditOpen, setBanquesEditOpen] = useState(false);
  const [banquesEditText, setBanquesEditText] = useState("");
  const [banquesEditingId, setBanquesEditingId] = useState(null);
  const [banquesIndex, setBanquesIndex] = useState(0); // index in current group
  const [banquesRevealed, setBanquesRevealed] = useState(false); // for recall phase
  const [banquesVisibleWords, setBanquesVisibleWords] = useState([]); // progressive reveal indices
  const [banquesMatched, setBanquesMatched] = useState([]); // words matched by voice
  const [banquesInfinitePaused, setBanquesInfinitePaused] = useState(false);
  const banquesInfinitePausedRef = useRef(false);
  const [banquesMastered, setBanquesMastered] = useState(() => {
    const saved = localStorage.getItem("freestyleBanquesMastered");
    return saved ? JSON.parse(saved) : [];
  });
  const [banquesTranscript, setBanquesTranscript] = useState("");
  const [banquesWordDelay, setBanquesWordDelay] = useState(1.5); // seconds between words in discovery
  // Infinite mode state
  const [infiniteRunning, setInfiniteRunning] = useState(false);
  const infiniteRunningRef = useRef(false);
  const [infiniteDelay, setInfiniteDelay] = useState(() => {
    const saved = localStorage.getItem("freestyleInfiniteDelay_v2");
    const n = saved ? parseFloat(saved) : 3.0;
    return (Number.isFinite(n) && n >= 0.1 && n <= 3) ? n : 3.0;
  });
  const infiniteDelayRef = useRef(3.0);
  useEffect(() => { infiniteDelayRef.current = infiniteDelay; localStorage.setItem("freestyleInfiniteDelay_v2", String(infiniteDelay)); }, [infiniteDelay]);
  const [infiniteCurrentWord, setInfiniteCurrentWord] = useState("");
  const [banquesWordLimit, setBanquesWordLimit] = useState(() => {
    const saved = localStorage.getItem("freestyleBanquesWordLimit");
    const n = saved ? parseInt(saved, 10) : 10;
    return (Number.isFinite(n) && n >= 3 && n <= 20) ? n : 10;
  });
  const [banquesTimer, setBanquesTimer] = useState(0);
  const banquesRecognitionRef = useRef(null);
  const banquesTranscriptRef = useRef("");
  const banquesBaseTranscriptRef = useRef("");
  const banquesTimerRef = useRef(null);
  const banquesPhaseRef = useRef("idle");
  const nextBanqueRef = useRef(null);

  const getBanquesForGroup = useCallback(() => {
    return BANQUES_DATA.filter(b => banquesSelected.includes(b.id));
  }, [banquesSelected]);

  // Dialog state
  const [wordDialogOpen, setWordDialogOpen] = useState(false);
  const [customWordsText, setCustomWordsText] = useState("");

  // Load words on mount
  useEffect(() => {
    checkDictionary();
    loadWords();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload words when filters or mode change
  useEffect(() => {
    if (dictionaryAvailable) {
      loadWords();
    }
  }, [activeTheme, activeSyllabes, dictionaryAvailable, currentMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkDictionary = async () => {
    try {
      const response = await axios.get(`${API}/dictionary/themes`);
      if (response.data.available) {
        setDictionaryAvailable(true);
        setDictionaryThemes(response.data.themes);
        setDictionaryTotal(response.data.total);
      }
    } catch (e) {
      console.log("Dictionary not available yet");
    }
  };

  const loadWords = async () => {
    try {
      let wordList = [];
      let source = "default";

      if (dictionaryAvailable) {
        // Use the rich dictionary with filters
        const params = new URLSearchParams();
        if (activeTheme && activeTheme !== "ALL") params.set("theme", activeTheme);
        if (activeSyllabes > 0) params.set("syllabes", activeSyllabes);
        if (currentMode === "talking") params.set("word_type", "nom");
        params.set("limit", "300");

        const response = await axios.get(`${API}/dictionary/batch?${params}`);
        wordList = response.data.words;
        source = "dictionary";
      } else {
        // Fallback to classic word list
        const response = await axios.get(`${API}/words`);
        wordList = response.data.words;
        source = response.data.source;
      }

      setWords(wordList);
      setWordSource(source);
      if (wordList.length > 0) {
        const randomIndex = Math.floor(Math.random() * wordList.length);
        const initialWord = wordList[randomIndex];
        setCurrentWord(initialWord);
        if (currentMode !== "freestyle") {
          prefetchSuggestions(initialWord, currentMode);
        }
      }
    } catch (e) {
      console.error("Failed to load words:", e);
      toast.error("Erreur lors du chargement des mots");
    }
  };

  // Fetch context image from buffer - now with inline base64 for instant display
  const fetchContextImage = useCallback(async () => {
    console.log("fetchContextImage called, local cache size:", imageCache.current.length);
    
    // Check local cache first
    if (imageCache.current.length > 0) {
      const cached = imageCache.current.shift();
      console.log("Using cached image");
      setContextImage(cached);
      setIsLoadingImage(false);
      // Prefetch more images
      prefetchContextImages();
      return;
    }
    
    setIsLoadingImage(true);
    console.log("Fetching image from API...");
    try {
      // Use the new endpoint that returns base64 data directly
      const response = await axios.get(`${API}/context-image-with-data`);
      console.log("API response received, has image:", !!response.data.image_base64);
      if (response.data.image_base64) {
        setContextImage({
          base64: response.data.image_base64,
          id: response.data.image_id
        });
      } else {
        setContextImage(null);
      }
      // Prefetch more images
      prefetchContextImages();
    } catch (e) {
      console.error("Failed to fetch context image:", e);
      setContextImage(null);
    } finally {
      console.log("Setting isLoadingImage to false");
      setIsLoadingImage(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefetch images into local cache (with base64 data)
  const prefetchContextImages = useCallback(async () => {
    const targetCache = 3;
    if (imageCache.current.length >= targetCache) return;
    
    try {
      for (let i = imageCache.current.length; i < targetCache; i++) {
        const response = await axios.get(`${API}/context-image-with-data`);
        if (response.data.image_base64) {
          imageCache.current.push({
            base64: response.data.image_base64,
            id: response.data.image_id
          });
        }
      }
      console.log(`Prefetched images, cache size: ${imageCache.current.length}`);
    } catch (e) {
      console.error("Failed to prefetch images:", e);
    }
  }, []);

  // Pre-fetch suggestions from API
  const prefetchSuggestions = useCallback(async (word, mode) => {
    if (mode === "freestyle" || !word) return;
    
    const cacheKey = `${mode}:${word}`;
    if (suggestionsCache.current[cacheKey]) {
      // Already cached - just show them for context mode
      if (mode === "context") {
        setSuggestions(suggestionsCache.current[cacheKey]);
        setShowSuggestions(true);
        fetchContextImage();
      }
      return;
    }
    
    setIsLoadingSuggestions(true);
    try {
      const apiMode = mode === "rhymes" ? "rhymes" : mode === "semantic" ? "semantic" : "context";
      const response = await axios.post(`${API}/generate-words`, {
        word: word,
        mode: apiMode
      });
      suggestionsCache.current[cacheKey] = response.data.suggestions;
      
      // For context mode, show suggestions immediately and fetch image
      if (mode === "context") {
        setSuggestions(response.data.suggestions);
        setShowSuggestions(true);
        fetchContextImage();
      }
    } catch (e) {
      console.error("Failed to fetch suggestions:", e);
      suggestionsCache.current[cacheKey] = [];
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [fetchContextImage]);

  // Get cached suggestions
  const getCachedSuggestions = useCallback((word, mode) => {
    const cacheKey = `${mode}:${word}`;
    return suggestionsCache.current[cacheKey] || [];
  }, []);

  // Get next random word
  const getNextWord = useCallback((countForSession = true) => {
    const activeWords = currentMode === "perso" ? personalWords : words;
    if (activeWords.length === 0) return;
    let newWord;
    do {
      const randomIndex = Math.floor(Math.random() * activeWords.length);
      newWord = activeWords[randomIndex];
    } while (newWord === currentWord && activeWords.length > 1);
    
    setCurrentWord(newWord);
    setWordKey((k) => k + 1);

    // Pick extra words if wordCount > 1
    if (wordCount > 1 && activeWords.length > wordCount) {
      const extras = [];
      const used = new Set([newWord]);
      while (extras.length < wordCount - 1) {
        const w = activeWords[Math.floor(Math.random() * activeWords.length)];
        if (!used.has(w)) {
          extras.push(w);
          used.add(w);
        }
      }
      setExtraWords(extras);
    } else {
      setExtraWords([]);
    }
    
    // For context mode, check if we have cached suggestions to show immediately
    if (currentMode === "context") {
      const cached = suggestionsCache.current[`context:${newWord}`];
      if (cached) {
        setSuggestions(cached);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    
    // Count word for session/challenge
    if (countForSession) {
      if (sessionActive) {
        setSessionWordCount((c) => c + 1);
      }
      if (challengeActive) {
        setChallengeScore((s) => {
          const newScore = s + 1;
          challengeScoreRef.current = newScore;
          return newScore;
        });
      }
    }
    
    // Pre-fetch suggestions for new word
    if (currentMode !== "freestyle" && currentMode !== "perso") {
      prefetchSuggestions(newWord, currentMode);
    }
  }, [words, personalWords, currentWord, currentMode, prefetchSuggestions, sessionActive, challengeActive, wordCount]);

  // Ref to always hold the latest getNextWord (avoids stale closures in intervals)
  const getNextWordRef = useRef(getNextWord);
  useEffect(() => { getNextWordRef.current = getNextWord; }, [getNextWord]);

  // Helper: pick a random word different from the given one (for next-word preview)
  const wordsRef = useRef(words);
  useEffect(() => { wordsRef.current = words; }, [words]);
  const pickRandomWord = useCallback((excludeWord) => {
    const pool = wordsRef.current;
    if (!pool || pool.length === 0) return "";
    if (pool.length === 1) return pool[0];
    let w;
    do { w = pool[Math.floor(Math.random() * pool.length)]; } while (w === excludeWord);
    return w;
  }, []);

  // ============ FREESTYLE RYTHMIQUE SEQUENCE ============
  const stopFreestyleRhSequence = useCallback(() => {
    if (freestyleRhStartDelayRef.current) {
      clearTimeout(freestyleRhStartDelayRef.current);
      freestyleRhStartDelayRef.current = null;
    }
    if (freestyleRhIntervalRef.current) {
      clearInterval(freestyleRhIntervalRef.current);
      freestyleRhIntervalRef.current = null;
    }
    // Stop beat if playing
    if (audioRef.current && mp3Playing) {
      audioRef.current.pause();
      setMp3Playing(false);
    }
    setFreestyleRhActive(false);
    setFreestyleRhPhase("idle");
    freestyleRhPhaseRef.current = "idle";
    setFreestyleRhBeat(0);
    freestyleRhBeatRef.current = 0;
    setFreestyleRhCountdown(0);
    setFreestyleRhNextWord("");
    setFreestyleRhLines([]);
    setFreestyleRhScrollKey(0);
  }, [mp3Playing]);

  const startFreestyleRhSequence = useCallback(() => {
    if (freestyleRhIntervalRef.current || freestyleRhStartDelayRef.current) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const wantsClicks = rhythmSource === "metronome" || rhythmSource === "both";
    const wantsBeat = audioSource === "music" && (rhythmSource === "beat" || rhythmSource === "both");

    const playClick = (freq, type, vol = 0.3) => {
      if (audioSourceRef.current !== "metronome") return;
      try {
        const ctx = audioContextRef.current;
        if (!ctx || ctx.state === "closed") return;
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(vol, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.06);
      } catch(e) {}
    };

    // Start beat MP3 if source requires it
    if (wantsBeat && mp3File && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      setMp3Playing(true);
    }

    // Generate 4 words for the visual line display
    const w1 = pickRandomWord("");
    const w2 = pickRandomWord(w1);
    const w3 = pickRandomWord(w2);
    const w4 = pickRandomWord(w3);
    setFreestyleRhLines([w1, w2, w3, w4]);
    setCurrentWord(w1);
    setWordKey(k => k + 1);
    setFreestyleRhNextWord(w2);
    setFreestyleRhScrollKey(0);

    // Pick initial filler if in filler mode
    if (fillerSelected && fillerPhrases.length > 0) {
      setCurrentFiller(fillerPhrases[Math.floor(Math.random() * fillerPhrases.length)]);
    }

    setFreestyleRhActive(true);
    setFreestyleRhBeat(0);
    freestyleRhBeatRef.current = 0;

    const beatMs = (60 / bpm) * 1000;
    const subdivFactor = freestyleRhSubdiv === "croche" ? 2 : freestyleRhSubdiv === "double-croche" ? 4 : 1;
    const tickMs = beatMs / subdivFactor;
    let tickCount = 0; // total subdivision ticks

    // In Filler mode: skip the 4-beat countdown and the 1s startup delay.
    // Start directly in "playing" phase, synced with the music.
    if (fillerSelected) {
      setFreestyleRhPhase("playing");
      freestyleRhPhaseRef.current = "playing";
      setFreestyleRhCountdown(0);
      setFreestyleRhBeat(1);
      freestyleRhBeatRef.current = 1;
      // First beat click immediately
      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);
      playClick(1200, "sine");

      let beatInCycle = 1;
      freestyleRhIntervalRef.current = setInterval(() => {
        tickCount++;
        const isMainBeat = (tickCount % subdivFactor) === 0;
        if (!isMainBeat) {
          playClick(600, "sine", 0.12);
          return;
        }
        beatInCycle = (beatInCycle % 8) + 1;
        setFreestyleRhBeat(beatInCycle);
        freestyleRhBeatRef.current = beatInCycle;
        setBeatActive(true);
        setTimeout(() => setBeatActive(false), 100);

        if (beatInCycle === 1) {
          playClick(1200, "sine");
          // Scroll lines at beat 1
          setFreestyleRhLines(prev => {
            const newWord = pickRandomWord(prev[prev.length - 1]);
            const newLines = [...prev.slice(1), newWord];
            setCurrentWord(newLines[0]);
            setWordKey(k => k + 1);
            setFreestyleRhNextWord(newLines[1] || "");
            return newLines;
          });
          setFreestyleRhScrollKey(k => k + 1);
        } else if (beatInCycle === 5) {
          playClick(1200, "sine");
        } else {
          playClick(1000, "sine");
        }
      }, tickMs);
      return;
    }

    // Rythmique mode (non-filler): 16-beat countdown, music starts simultaneously.
    setFreestyleRhPhase("countdown");
    freestyleRhPhaseRef.current = "countdown";
    setFreestyleRhCountdown(16);

    // Start first countdown beat immediately (synced with music which already started above)
    setBeatActive(true);
    setTimeout(() => setBeatActive(false), 100);
    playClick(800, "triangle");

    freestyleRhIntervalRef.current = setInterval(() => {
      tickCount++;
      const isMainBeat = (tickCount % subdivFactor) === 0;
      const mainBeatCount = Math.floor(tickCount / subdivFactor);

      if (!isMainBeat) {
        // Subdivision click — softer, higher pitch
        playClick(600, "sine", 0.12);
        return;
      }

      // Main beat logic
      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);

      if (mainBeatCount <= 14) {
        // Countdown beats 2..15 → show 15..1
        setFreestyleRhCountdown(16 - mainBeatCount);
        // Last 3 countdown ticks use a higher "get ready" click
        if (mainBeatCount >= 13) playClick(1000, "triangle");
        else playClick(800, "triangle");
      } else {
        // mainBeatCount >= 16 → playing phase
        if (mainBeatCount === 16) {
          setFreestyleRhCountdown(0);
          setFreestyleRhPhase("playing");
          freestyleRhPhaseRef.current = "playing";
        }
        const beatInCycle = ((mainBeatCount - 16) % 8) + 1;
        setFreestyleRhBeat(beatInCycle);
        freestyleRhBeatRef.current = beatInCycle;

        if (beatInCycle === 1) {
          playClick(1200, "sine");
          // Scroll lines at beat 1 (but not the very first cycle — lines already set)
          if (mainBeatCount > 16) {
            setFreestyleRhLines(prev => {
              const newWord = pickRandomWord(prev[prev.length - 1]);
              const newLines = [...prev.slice(1), newWord];
              setCurrentWord(newLines[0]);
              setWordKey(k => k + 1);
              setFreestyleRhNextWord(newLines[1] || "");
              return newLines;
            });
            setFreestyleRhScrollKey(k => k + 1);
          }
        } else if (beatInCycle === 5) {
          playClick(1200, "sine");
        } else {
          playClick(1000, "sine");
        }
      }
    }, tickMs);
  }, [bpm, pickRandomWord, freestyleRhSubdiv, rhythmSource, mp3File, fillerSelected, fillerPhrases, audioSource]);

  // Cleanup freestyle rhythmic on mode change
  useEffect(() => {
    if (currentMode !== "freestyle") {
      stopFreestyleRhSequence();
    }
  }, [currentMode, stopFreestyleRhSequence]);

  // ============ FREESTYLE ASSISTÉ (one-shot cycle) ============
  const stopAssisteSequence = useCallback(() => {
    if (assisteTimeoutRef.current) {
      clearTimeout(assisteTimeoutRef.current);
      assisteTimeoutRef.current = null;
    }
    if (assisteIntervalRef.current) {
      clearInterval(assisteIntervalRef.current);
      assisteIntervalRef.current = null;
    }
    try { window.speechSynthesis?.cancel(); } catch(e) {}
    if (audioRef.current && mp3Playing) {
      audioRef.current.pause();
      setMp3Playing(false);
    }
    setAssisteActive(false);
    setAssistePhase("idle");
    assistePhaseRef.current = "idle";
    setAssisteBeat(0);
    assisteBeatRef.current = 0;
    setAssisteCountdown(0);
  }, [mp3Playing]);

  const startAssisteSequence = useCallback(() => {
    if (assisteIntervalRef.current || assisteTimeoutRef.current) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const wantsClicks = rhythmSource === "metronome" || rhythmSource === "both";
    const wantsBeat = audioSource === "music" && (rhythmSource === "beat" || rhythmSource === "both");

    const playClick = (freq, type, vol = 0.3) => {
      if (audioSourceRef.current !== "metronome") return;
      try {
        const ctx = audioContextRef.current;
        if (!ctx || ctx.state === "closed") return;
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(vol, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.06);
      } catch(e) {}
    };

    // Pick a new word for this cycle
    const newWord = pickRandomWord(currentWord || "");
    setCurrentWord(newWord);
    setWordKey(k => k + 1);

    setAssisteActive(true);
    setAssistePhase("speaking");
    assistePhaseRef.current = "speaking";
    setAssisteBeat(0);
    assisteBeatRef.current = 0;
    setAssisteCountdown(0);

    // 1. Speak the word aloud, then small delay, then 4-beat countdown, then 8-beat play
    const launchBeats = () => {
      // Start beat MP3 if requested
      if (wantsBeat && mp3File && audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
        setMp3Playing(true);
      }

      setAssistePhase("countdown");
      assistePhaseRef.current = "countdown";
      setAssisteCountdown(4);
      const beatMs = (60 / bpm) * 1000;
      let mainBeat = 0; // 1..4 countdown, then 5..12 playing (we map back to 1..8)

      // First tick immediately
      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);
      playClick(800, "triangle");

      assisteIntervalRef.current = setInterval(() => {
        mainBeat++;
        setBeatActive(true);
        setTimeout(() => setBeatActive(false), 100);

        if (mainBeat <= 2) {
          // Countdown: 4 is shown at tick 0, then 3 at mainBeat 1, 2 at 2
          setAssisteCountdown(4 - mainBeat);
          playClick(800, "triangle");
        } else if (mainBeat === 3) {
          setAssisteCountdown(1);
          playClick(1000, "triangle");
        } else {
          // Playing phase: mainBeat 4..11 = beats 1..8
          if (mainBeat === 4) {
            setAssisteCountdown(0);
            setAssistePhase("playing");
            assistePhaseRef.current = "playing";
          }
          const beatInCycle = mainBeat - 3; // 1..8
          setAssisteBeat(beatInCycle);
          assisteBeatRef.current = beatInCycle;

          if (beatInCycle === 1 || beatInCycle === 5) playClick(1200, "sine");
          else playClick(1000, "sine");

          if (beatInCycle >= 8) {
            // End the cycle
            if (assisteIntervalRef.current) {
              clearInterval(assisteIntervalRef.current);
              assisteIntervalRef.current = null;
            }
            if (audioRef.current && mp3Playing) {
              audioRef.current.pause();
              setMp3Playing(false);
            }
            setAssistePhase("done");
            assistePhaseRef.current = "done";
          }
        }
      }, beatMs);
    };

    // Speak the word first
    const synth = window.speechSynthesis;
    if (synth) {
      try { synth.cancel(); } catch(e) {}
      const utt = new SpeechSynthesisUtterance(newWord);
      utt.lang = 'fr-FR';
      utt.rate = 0.95;
      utt.onend = () => {
        // Small delay (~700ms) then launch countdown beats
        assisteTimeoutRef.current = setTimeout(() => {
          assisteTimeoutRef.current = null;
          launchBeats();
        }, 700);
      };
      utt.onerror = () => {
        assisteTimeoutRef.current = setTimeout(() => {
          assisteTimeoutRef.current = null;
          launchBeats();
        }, 700);
      };
      synth.speak(utt);
    } else {
      // Fallback: no TTS — go straight to countdown after short delay
      assisteTimeoutRef.current = setTimeout(() => {
        assisteTimeoutRef.current = null;
        launchBeats();
      }, 400);
    }
  }, [bpm, pickRandomWord, rhythmSource, mp3File, mp3Playing, currentWord, audioSource]);

  // Cleanup assisté on mode change
  useEffect(() => {
    if (currentMode !== "freestyle") {
      stopAssisteSequence();
    }
  }, [currentMode, stopAssisteSequence]);

  // Restart freestyle rhythmic when BPM or subdivision changes during playback
  useEffect(() => {
    if (freestyleRhActive) {
      stopFreestyleRhSequence();
      setTimeout(() => startFreestyleRhSequence(), 50);
    }
  }, [bpm, freestyleRhSubdiv]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModeChange = (newMode) => {
    const micModes = ["punchline", "talking", "banques"];
    const leavingMicMode = micModes.includes(currentMode) && !micModes.includes(newMode);
    
    // Cleanup perso mode if leaving
    if (currentMode === "perso" && newMode !== "perso") {
      stopPersoSequence();
    }
    // Cleanup freestyle rhythmic if leaving
    if (currentMode === "freestyle" && newMode !== "freestyle") {
      stopFreestyleRhSequence();
    }
    // Cleanup punchline mode if leaving
    if (currentMode === "punchline" && newMode !== "punchline") {
      stopPunchlineListening();
    }
    // Cleanup talking mode if leaving
    if (currentMode === "talking" && newMode !== "talking") {
      stopTalkingMode();
    }
    // Cleanup banques mode if leaving
    if (currentMode === "banques" && newMode !== "banques") {
      stopBanquesRecognition();
      banquesPhaseRef.current = "idle";
    }
    // Release mic stream only when going to a non-mic mode
    if (leavingMicMode) {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (punchlineAudioCtxRef.current) { punchlineAudioCtxRef.current.close().catch(() => {}); punchlineAudioCtxRef.current = null; }
      if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); mediaStreamRef.current = null; }
      analyserRef.current = null;
      setAudioLevel(0);
      setVuMeterActive(false);
    }
    setCurrentMode(newMode);
    setSuggestions([]);
    setShowSuggestions(false);
    setContextImage(null);
    setImageLoaded(false);
    
    if (newMode === "punchline") {
      startPunchlineListening();
    } else if (newMode === "talking") {
      startTalkingListening();
    } else if (newMode === "banques") {
      startBanquesMode();
    } else if (newMode === "perso") {
      // Load first personal word
      if (personalWords.length > 0) {
        const first = personalWords[Math.floor(Math.random() * personalWords.length)];
        setCurrentWord(first);
        setWordKey(k => k + 1);
      } else {
        setCurrentWord("");
      }
    } else if (newMode !== "freestyle" && currentWord) {
      prefetchSuggestions(currentWord, newMode);
    }
  };

  // Handle click on canvas
  // Double-tap detection for filler mode (touch/click)
  const lastTapTimeRef = useRef(0);

  const handleCanvasClick = () => {
    if (fillerSelected && currentMode === "freestyle" && freestyleRhActive) {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapTimeRef.current;
      lastTapTimeRef.current = now;
      if (timeSinceLastTap < 500) {
        // Double tap → stop
        lastTapTimeRef.current = 0;
        stopFreestyleRhSequence();
        return;
      }
      // Single tap → change filler
      if (fillerPhrases.length > 0) {
        setCurrentFiller(fillerPhrases[Math.floor(Math.random() * fillerPhrases.length)]);
      }
      return;
    }
    handleSpacePress();
  };

  // Double-tap for Banques pause/resume
  const lastBanquesTapRef = useRef(0);
  const handleBanquesClick = () => {
    const isRunning = banquesPhase === "discovery" || banquesPhase === "infinite";
    if (isRunning) {
      const now = Date.now();
      const timeSinceLastTap = now - lastBanquesTapRef.current;
      lastBanquesTapRef.current = now;
      if (timeSinceLastTap < 500) {
        // Double tap → toggle pause
        lastBanquesTapRef.current = 0;
        const newVal = !banquesInfinitePaused;
        setBanquesInfinitePaused(newVal);
        banquesInfinitePausedRef.current = newVal;
        if (newVal) {
          window.speechSynthesis?.pause();
        } else {
          window.speechSynthesis?.resume();
        }
        return;
      }
      // Single tap → normal action
      handleSpacePress();
      return;
    }
    handleSpacePress();
  };

  // ============ SESSION LOGIC ============
  const startSession = () => {
    setSessionActive(true);
    setSessionTimeLeft(sessionDuration);
    setSessionWordCount(0);
    setSessionDialogOpen(false);
    
    // Start music if loaded
    if (mp3File && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setMp3Playing(true);
    }
    
    toast.success(`Session démarrée : ${formatTime(sessionDuration)}`);
    
    // Start timer
    sessionIntervalRef.current = setInterval(() => {
      setSessionTimeLeft((prev) => {
        if (prev <= 1) {
          endSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endSession = () => {
    setSessionActive(false);
    if (sessionIntervalRef.current) {
      clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = null;
    }
    // Stop music
    if (audioRef.current) {
      audioRef.current.pause();
      setMp3Playing(false);
    }
    toast.success(`Session terminée ! ${sessionWordCount} mots en ${formatTime(sessionDuration)}`);
  };

  const stopSession = () => {
    const wordsUsed = sessionWordCount;
    const timeUsed = sessionDuration - sessionTimeLeft;
    setSessionActive(false);
    if (sessionIntervalRef.current) {
      clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = null;
    }
    // Stop music
    if (audioRef.current) {
      audioRef.current.pause();
      setMp3Playing(false);
    }
    toast.info(`Session arrêtée : ${wordsUsed} mots en ${formatTime(timeUsed)}`);
  };

  // ============ CHALLENGE LOGIC ============
  // Watch for challenge end
  useEffect(() => {
    if (challengeActive && challengeTimeLeft === 0) {
      finishChallenge();
    }
  }, [challengeTimeLeft, challengeActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const startChallenge = () => {
    setChallengeActive(true);
    setChallengeTimeLeft(CHALLENGE_DURATION);
    setChallengeScore(0);
    challengeScoreRef.current = 0;
    setShowChallengeResult(false);
    setChallengeDialogOpen(false);
    getNextWord(false); // Get first word without counting
    toast.success("Challenge lancé ! GO GO GO !");
    
    // Start countdown
    challengeIntervalRef.current = setInterval(() => {
      setChallengeTimeLeft((prev) => {
        if (prev <= 1) {
          // Stop interval
          if (challengeIntervalRef.current) {
            clearInterval(challengeIntervalRef.current);
            challengeIntervalRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const finishChallenge = () => {
    setChallengeActive(false);
    
    const finalScore = challengeScoreRef.current;
    
    // Check for high score
    if (finalScore > challengeHighScore) {
      setChallengeHighScore(finalScore);
      localStorage.setItem("freestyleHighScore", finalScore.toString());
      toast.success(`🏆 Nouveau record : ${finalScore} mots !`);
    }
    
    setShowChallengeResult(true);
  };

  const endChallenge = () => {
    if (challengeIntervalRef.current) {
      clearInterval(challengeIntervalRef.current);
      challengeIntervalRef.current = null;
    }
    finishChallenge();
  };

  const stopChallenge = () => {
    setChallengeActive(false);
    if (challengeIntervalRef.current) {
      clearInterval(challengeIntervalRef.current);
      challengeIntervalRef.current = null;
    }
    setShowChallengeResult(false);
    toast.info("Challenge annulé");
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (sessionIntervalRef.current) clearInterval(sessionIntervalRef.current);
      if (challengeIntervalRef.current) clearInterval(challengeIntervalRef.current);
    };
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ============ METRONOME LOGIC ============
  const startMetronome = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const interval = (60 / bpm) * 1000;

    const playClick = () => {
      const ctx = audioContextRef.current;
      if (ctx.state === "suspended") {
        ctx.resume();
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1000;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.05);

      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);
    };

    playClick();
    metronomeIntervalRef.current = setInterval(playClick, interval);
  }, [bpm]);

  const stopMetronome = useCallback(() => {
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
  }, []);

  const toggleMetronome = () => {
    if (metronomeActive) {
      stopMetronome();
    } else {
      startMetronome();
    }
    setMetronomeActive(!metronomeActive);
  };

  // Unified rhythm play/pause
  const toggleRhythm = () => {
    const wantsMetronome = audioSource === "metronome";
    const wantsBeat = audioSource === "music" && (rhythmSource === "beat" || rhythmSource === "both");
    const isPlaying = metronomeActive || mp3Playing;

    if (isPlaying) {
      // Stop everything
      if (metronomeActive) { stopMetronome(); setMetronomeActive(false); }
      if (mp3Playing && audioRef.current) { audioRef.current.pause(); setMp3Playing(false); }
    } else {
      // Start based on source
      if (wantsMetronome) { startMetronome(); setMetronomeActive(true); }
      if (wantsBeat && mp3File && audioRef.current) { audioRef.current.play().catch(() => {}); setMp3Playing(true); }
    }
  };

  // When rhythm source changes, adjust what's playing
  const handleRhythmSourceChange = (newSource) => {
    const wasPlaying = metronomeActive || mp3Playing;
    // Stop current
    if (metronomeActive) { stopMetronome(); setMetronomeActive(false); }
    if (mp3Playing && audioRef.current) { audioRef.current.pause(); setMp3Playing(false); }
    setRhythmSource(newSource);
    // Restart if was playing
    if (wasPlaying) {
      const wantsMetronome = newSource === "metronome" || newSource === "both";
      const wantsBeat = newSource === "beat" || newSource === "both";
      if (wantsMetronome) { startMetronome(); setMetronomeActive(true); }
      if (wantsBeat && mp3File && audioRef.current) {
        setTimeout(() => { if (audioRef.current) audioRef.current.play().catch(() => {}); setMp3Playing(true); }, 50);
      }
    }
  };

  useEffect(() => {
    if (metronomeActive) {
      stopMetronome();
      startMetronome();
    }
  }, [bpm, metronomeActive, startMetronome, stopMetronome]);

  // Live toggle of audio source: music / metronome / silent are mutually exclusive.
  // - music: stop standalone metronome (rhythmic-sequence playClicks already silent via ref)
  // - metronome: stop MP3. If no rhythmic sequence is active (= Libre mode only),
  //   start the standalone metronome ticking.
  // - silent: stop both MP3 and standalone metronome.
  useEffect(() => {
    const rhythmicSequenceActive = freestyleRhActive || assisteActive || rimeGuideeActive;
    if (audioSource === "metronome") {
      if (mp3Playing && audioRef.current) {
        audioRef.current.pause();
        setMp3Playing(false);
      }
      if (!rhythmicSequenceActive && !metronomeActive) {
        startMetronome();
        setMetronomeActive(true);
      }
    } else if (audioSource === "silent") {
      if (mp3Playing && audioRef.current) {
        audioRef.current.pause();
        setMp3Playing(false);
      }
      if (metronomeActive) {
        stopMetronome();
        setMetronomeActive(false);
      }
    } else {
      // music
      if (metronomeActive) {
        stopMetronome();
        setMetronomeActive(false);
      }
    }
  }, [audioSource]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      stopMetronome();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopMetronome]);

  // ============ PERSO BEAT SEQUENCE ============
  const stopPersoSequence = useCallback(() => {
    if (persoBeatIntervalRef.current) {
      clearInterval(persoBeatIntervalRef.current);
      persoBeatIntervalRef.current = null;
    }
    setPersoActive(false);
    setPersoPhase("idle");
    persoPhaseRef.current = "idle";
    setPersoBeat(0);
    persoBeatRef.current = 0;
    setPersoCountdown(0);
    stopMetronome();
    setMetronomeActive(false);
  }, [stopMetronome]);

  const startPersoSequence = useCallback(() => {
    if (persoBeatIntervalRef.current) return;
    if (personalWords.length === 0) {
      toast.error("Ajoute des mots à ta liste d'abord !");
      return;
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const playClick = (freq, type) => {
      try {
        const ctx = audioContextRef.current;
        if (!ctx || ctx.state === "closed") return;
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.06);
      } catch(e) {}
    };

    setPersoActive(true);
    setPersoPhase("countdown");
    persoPhaseRef.current = "countdown";
    setPersoCountdown(4);
    setPersoBeat(0);
    persoBeatRef.current = 0;
    setMetronomeActive(true);

    setBeatActive(true);
    setTimeout(() => setBeatActive(false), 100);
    playClick(800, "triangle");

    const beatMs = (60 / bpm) * 1000;
    let count = 0;

    persoBeatIntervalRef.current = setInterval(() => {
      count++;
      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);

      if (count <= 2) {
        // Countdown: 3, 2
        setPersoCountdown(4 - count);
        playClick(800, "triangle");
      } else if (count === 3) {
        // Countdown: 1
        setPersoCountdown(1);
        playClick(1000, "triangle");
      } else {
        // Playing phase
        if (count === 4) {
          setPersoCountdown(0);
          setPersoPhase("playing");
          persoPhaseRef.current = "playing";
        }
        const beatInMeasure = ((count - 4) % 4) + 1;
        setPersoBeat(beatInMeasure);
        persoBeatRef.current = beatInMeasure;

        if (beatInMeasure === 1) {
          // Downbeat: new word + accent click
          playClick(1200, "sine");
          // Pick a new personal word
          setCurrentWord(prev => {
            let next;
            do {
              next = personalWords[Math.floor(Math.random() * personalWords.length)];
            } while (next === prev && personalWords.length > 1);
            return next;
          });
          setWordKey(k => k + 1);
        } else {
          playClick(1000, "sine");
        }
      }
    }, beatMs);
  }, [bpm, personalWords]);

  // ============ FREESTYLE RYTHMIQUE SEQUENCE ============


  // ============ MP3 LOGIC ============
  // Auto-switch beat when BPM changes and built-in beats mode is active
  // BPM ↔ Musique : DÉCOUPLÉS. Le BPM ne contrôle plus que le métronome.
  // L'utilisateur choisit sa musique manuellement via le picker du tableau de bord.
  // (L'ancien useEffect qui changeait automatiquement de beat selon le BPM a été retiré.)

  const toggleBuiltInBeats = () => {
    if (!useBuiltInBeats) {
      // Switch to built-in: save current beat, load closest
      userUploadedBeatRef.current = mp3File;
      const beat = findClosestBeat(bpm);
      setCurrentBuiltInBeat(beat);
      setMp3File({ name: `${beat.name} (${beat.bpm} BPM)`, url: beat.url });
      setUseBuiltInBeats(true);
      toast.success(`Beat auto: ${beat.name} (${beat.bpm} BPM)`);
    } else {
      // Switch back to user's beat
      setUseBuiltInBeats(false);
      setCurrentBuiltInBeat(null);
      setMp3File(userUploadedBeatRef.current || DEFAULT_BEAT);
    }
  };

  const handleMp3Upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      toast.error("Veuillez sélectionner un fichier audio");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 20MB)");
      return;
    }

    const url = URL.createObjectURL(file);
    setMp3File({ name: file.name, url });
    setMp3Playing(false);
    setMp3Progress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/mp3/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Beat uploadé !");
    } catch (e) {
      console.error("Upload error:", e);
      toast.info("Lecture locale activée");
    }
  };

  const toggleMp3 = () => {
    if (!audioRef.current) return;
    if (mp3Playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setMp3Playing(!mp3Playing);
  };

  const removeMp3 = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (mp3File?.url) {
      URL.revokeObjectURL(mp3File.url);
    }
    setMp3File(null);
    setMp3Playing(false);
    setMp3Progress(0);
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      if (audio.duration) {
        setMp3Progress((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      // Loop is handled by the audio element's loop attribute
      setMp3Progress(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [mp3File]);

  // ============ CUSTOM WORDS LOGIC ============
  const saveCustomWords = async () => {
    const wordArray = customWordsText
      .split(/[,\n]/)
      .map((w) => w.trim())
      .filter((w) => w.length > 0);

    if (wordArray.length === 0) {
      toast.error("Veuillez entrer au moins un mot");
      return;
    }

    try {
      await axios.post(`${API}/words/custom`, { words: wordArray });
      toast.success(`${wordArray.length} mots enregistrés !`);
      setWordDialogOpen(false);
      setCustomWordsText("");
      loadWords();
    } catch (e) {
      console.error("Save custom words error:", e);
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const resetToDefaultWords = async () => {
    try {
      await axios.delete(`${API}/words/custom`);
      toast.success("Liste par défaut restaurée");
      setWordDialogOpen(false);
      loadWords();
    } catch (e) {
      console.error("Reset words error:", e);
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  // ============ HELPERS ============
  const getWordSizeClass = (word) => {
    if (!word) return "size-medium";
    return word.length > 10 ? "size-small" : "size-medium";
  };

  const getHintText = () => {
    if (challengeActive) {
      return "VITE ! Appuyez sur ESPACE pour enchaîner les mots !";
    }
    if (currentMode === "punchline") {
      if (punchlinePhase === "waiting") return "Appuie sur ESPACE pour lancer la séquence";
      if (punchlinePhase === "countdown") return "Prépare-toi...";
      if (punchlinePhase === "verse1") return `Vers 1 — Temps ${punchlineBeat}/4`;
      if (punchlinePhase === "verse2") return `Vers 2 — Temps ${punchlineBeat - 4}/4 — Place le mot !`;
      return "Sélectionne le mode Punch-line pour commencer";
    }
    if (currentMode === "talking") {
      if (talkingPhase === "idle") return "Commence à parler sur le thème du mot affiché";
      if (talkingPhase === "listening") return "Continue de parler ! Ne t'arrête pas !";
      if (talkingPhase === "done") return "Appuie sur Rejouer pour un nouveau mot";
      return "Sélectionne le mode Talking pour commencer";
    }
    if (currentMode === "freestyle") {
      if (freestyleRhythmic || fillerSelected) {
        if (freestyleRhPhase === "idle") return fillerSelected ? 'Maintiens ESPACE (1s) pour lancer le Filler' : 'Appuie sur ESPACE pour lancer le rythme';
        if (freestyleRhPhase === "countdown") return "Prépare-toi...";
        return fillerSelected ? 'Tap = changer filler | Double-tap = arrêter' : `Temps ${freestyleRhBeat}/8`;
      }
      if (rimeGuideeSelected) {
        if (!rimeGuideeActive) return "Appuie sur ESPACE pour lancer la Rime Guidée";
        if (rimeGuideePhase === "countdown") return "Prépare-toi...";
        return `Rime Guidée — Temps ${rimeGuideeBeat}/8`;
      }
      if (assisteSelected) {
        if (assistePhase === "idle") return "Appuie sur ESPACE pour lancer le cycle assisté";
        if (assistePhase === "speaking") return "Écoute le mot...";
        if (assistePhase === "countdown") return "Prépare-toi...";
        if (assistePhase === "playing") return `Freestyle ! Temps ${assisteBeat}/8`;
        if (assistePhase === "done") return "Appuie sur ESPACE pour continuer";
        return "";
      }
      return "Appuie sur ESPACE pour lancer / arrêter";
    }
    if (currentMode === "perso") {
      if (personalWords.length === 0) return "Ajoute des mots à ta liste pour commencer";
      if (persoActive) return "ESPACE pour mettre en pause";
      return "ESPACE pour lancer le métronome";
    }
    if (!showSuggestions) {
      return "Appuyez sur ESPACE pour voir les suggestions";
    }
    return "Appuyez sur ESPACE pour un nouveau mot";
  };

  // ============ PUNCHLINE MODE LOGIC ============
  // Extract the phonemic vowel sound of the last syllable (for assonance / rime pauvre)
  const getVowelSound = useCallback((word) => {
    let w = word.toLowerCase();
    // Iteratively strip silent endings (mute e + silent consonants)
    let prev;
    do {
      prev = w;
      if (w.length > 2 && w.endsWith('e')) w = w.slice(0, -1);
      w = w.replace(/[sxztdpcg]$/, '');
    } while (w !== prev && w.length > 1);

    // Nasal vowels (check first)
    if (/oin$/.test(w)) return 'WE';
    if (/(?:ain|ein|aim)$/.test(w)) return 'IN';
    if (/(?:in|im|yn|un|um)$/.test(w)) return 'IN';
    if (/(?:an|en|am|em)$/.test(w)) return 'AN';
    if (/(?:on|om)$/.test(w)) return 'ON';
    // Diphthongs
    if (/oi[re]*$/.test(w)) return 'WA';
    if (/ou[re]*$/.test(w)) return 'U';
    if (/eau$/.test(w) || /au$/.test(w)) return 'O';
    if (/eu[re]*$/.test(w) || /œu/.test(w)) return 'EU';
    if (/ai[re]*$/.test(w) || /ei[re]*$/.test(w)) return 'E';
    // Single vowel — scan backwards
    for (let i = w.length - 1; i >= 0; i--) {
      const c = w[i];
      if (i > 0) {
        const pair = w[i - 1] + c;
        if (pair === 'ou') return 'U';
        if (pair === 'oi') return 'WA';
        if (pair === 'au') return 'O';
        if (pair === 'eu') return 'EU';
        if (pair === 'ai' || pair === 'ei') return 'E';
      }
      if ('aàâ'.includes(c)) return 'A';
      if ('éèêë'.includes(c) || c === 'e') return 'E';
      if ('iîïy'.includes(c)) return 'I';
      if ('oôö'.includes(c)) return 'O';
      if ('uùûü'.includes(c)) return 'Y';
      if (c === 'œ') return 'EU';
    }
    return '';
  }, []);

  const checkRhymeLocal = useCallback((spokenText, targetWord) => {
    if (!spokenText || !targetWord) return { rhymes: false, lastWord: "" };
    const cleanText = spokenText.trim().replace(/[.,!?;:'"()]/g, '');
    const wordsArr = cleanText.split(/\s+/).filter(w => w.length > 0);
    const lastWord = wordsArr.length > 0 ? wordsArr[wordsArr.length - 1].toLowerCase() : "";
    if (!lastWord) return { rhymes: false, lastWord: "" };
    const target = targetWord.toLowerCase();
    if (lastWord === target) return { rhymes: true, lastWord, exact: true };

    // LAYER 1: Vowel sound matching (assonance / rime pauvre)
    const v1 = getVowelSound(lastWord);
    const v2 = getVowelSound(target);
    if (v1 && v2 && v1 === v2) return { rhymes: true, lastWord, match: v1 };

    // LAYER 2: Suffix matching (backup for edge cases)
    const normAccent = (w) => w
      .replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a')
      .replace(/[ôö]/g, 'o').replace(/[ùûü]/g, 'u').replace(/[îï]/g, 'i')
      .replace(/ç/g, 'c').replace(/œu/g, 'eu').replace(/œ/g, 'e');
    const stripEnd = (w) => w.replace(/[sxztdpcg]$/, '');
    const a = stripEnd(normAccent(lastWord));
    const b = stripEnd(normAccent(target));
    if (a.length >= 2 && b.length >= 2 && a.slice(-2) === b.slice(-2)) return { rhymes: true, lastWord, match: a.slice(-2) };

    return { rhymes: false, lastWord };
  }, [getVowelSound]);

  // Generate a rhyming pair from the word list
  const generateRhymePair = useCallback((excludeWords = []) => {
    // Pick a random banque and two words from it — guaranteed to rhyme
    const shuffledBanques = [...BANQUES_DATA].sort(() => Math.random() - 0.5);
    for (const banque of shuffledBanques) {
      const allMots = [...(banque.mots1 || []), ...(banque.mots2 || []), ...(banque.mots3 || [])];
      const available = allMots.filter(w => !excludeWords.includes(w));
      if (available.length >= 2) {
        const shuffled = available.sort(() => Math.random() - 0.5);
        return { word1: shuffled[0], word2: shuffled[1] };
      }
    }
    // Fallback
    return { word1: "action", word2: "passion" };
  }, []);

  // ============ RIME GUIDÉE MODE ============
  const stopRimeGuidee = useCallback(() => {
    if (rimeGuideeIntervalRef.current) {
      clearInterval(rimeGuideeIntervalRef.current);
      rimeGuideeIntervalRef.current = null;
    }
    if (rimeGuideeStartDelayRef.current) {
      clearTimeout(rimeGuideeStartDelayRef.current);
      rimeGuideeStartDelayRef.current = null;
    }
    if (rimeGuideeRecognitionRef.current) {
      try { rimeGuideeRecognitionRef.current.stop(); } catch(e) {}
      rimeGuideeRecognitionRef.current = null;
    }
    // Stop beat if playing
    if (audioRef.current && mp3Playing) {
      audioRef.current.pause();
      setMp3Playing(false);
    }
    setRimeGuideeActive(false);
    setRimeGuideePhase("idle");
    rimeGuideePhaseRef.current = "idle";
    setRimeGuideePairs([]);
    setRimeGuideeCurrentPair(0);
    setRimeGuideeCurrentLine(0);
    setRimeGuideeTranscript("");
    rimeGuideeTranscriptRef.current = "";
    rimeGuideeBaseTranscriptRef.current = "";
    setRimeGuideeValidated([]);
    setRimeGuideeBeat(0);
    rimeGuideeBeatRef.current = 0;
    setRimeGuideeLastWord("");
    setRimeGuideeScrollKey(0);
  }, [mp3Playing]);

  const startRimeGuideeSTT = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (rimeGuideeRecognitionRef.current) {
      try { rimeGuideeRecognitionRef.current.stop(); } catch(e) {}
    }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';
    recognition.onresult = (event) => {
      let full = '', interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) full += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      const sessionText = (full + interim).trim();
      const combined = rimeGuideeBaseTranscriptRef.current + (rimeGuideeBaseTranscriptRef.current && sessionText ? ' ' : '') + sessionText;
      setRimeGuideeTranscript(combined);
      rimeGuideeTranscriptRef.current = combined;
    };
    recognition.onerror = () => {};
    recognition.onend = () => {
      if (rimeGuideePhaseRef.current === "playing" && rimeGuideeRecognitionRef.current === recognition) {
        rimeGuideeBaseTranscriptRef.current = rimeGuideeTranscriptRef.current;
        try { recognition.start(); } catch(e) {}
      }
    };
    rimeGuideeRecognitionRef.current = recognition;
    try { recognition.start(); } catch(e) {}
  }, []);

  const startRimeGuidee = useCallback(() => {
    if (rimeGuideeIntervalRef.current || rimeGuideeStartDelayRef.current) return;
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const wantsClicks = rhythmSource === "metronome" || rhythmSource === "both";
    const wantsBeat = audioSource === "music" && (rhythmSource === "beat" || rhythmSource === "both");

    const playClick = (freq, type, vol = 0.3) => {
      if (audioSourceRef.current !== "metronome") return;
      try {
        const ctx = audioContextRef.current;
        if (!ctx || ctx.state === "closed") return;
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(vol, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.06);
      } catch(e) {}
    };

    // Start beat MP3 if source requires it
    if (wantsBeat && mp3File && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
      setMp3Playing(true);
    }

    const pair1 = generateRhymePair([]);
    const pair2 = generateRhymePair([pair1.word1, pair1.word2]);
    setRimeGuideePairs([pair1, pair2]);
    setRimeGuideeCurrentPair(0);
    setRimeGuideeCurrentLine(0);
    setRimeGuideeValidated([false, false, false, false]);
    setRimeGuideeTranscript("");
    rimeGuideeTranscriptRef.current = "";
    rimeGuideeBaseTranscriptRef.current = "";
    setRimeGuideeLastWord("");
    setRimeGuideeScrollKey(0);
    setRimeGuideeActive(true);
    setRimeGuideePhase("countdown");
    rimeGuideePhaseRef.current = "countdown";
    setRimeGuideeBeat(0);
    rimeGuideeBeatRef.current = 0;
    const beatMs = (60 / bpm) * 1000;
    let count = 0;
    rimeGuideeStartDelayRef.current = setTimeout(() => {
      rimeGuideeStartDelayRef.current = null;
      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);
      playClick(800, "triangle");
      rimeGuideeIntervalRef.current = setInterval(() => {
        count++;
        setBeatActive(true);
        setTimeout(() => setBeatActive(false), 100);
        if (count <= 2) {
          playClick(800, "triangle");
        } else if (count === 3) {
          playClick(1000, "triangle");
        } else {
          if (count === 4) {
            setRimeGuideePhase("playing");
            rimeGuideePhaseRef.current = "playing";
            startRimeGuideeSTT();
          }
          const beatInCycle = ((count - 4) % 8) + 1;
          setRimeGuideeBeat(beatInCycle);
          rimeGuideeBeatRef.current = beatInCycle;
          if (beatInCycle === 1) {
            playClick(1200, "sine");
          } else if (beatInCycle === 5) {
            playClick(1200, "sine");
          } else {
            playClick(1000, "sine");
          }
        }
      }, beatMs);
    }, 1000);
  }, [bpm, generateRhymePair, startRimeGuideeSTT, rhythmSource, mp3File, audioSource]);

  // Check rhyme in real time for rime guidée
  useEffect(() => {
    if (rimeGuideePhaseRef.current !== "playing" || rimeGuideePairs.length === 0) return;
    const currentPairIdx = rimeGuideeCurrentPair;
    const currentLineIdx = rimeGuideeCurrentLine;
    const pair = rimeGuideePairs[currentPairIdx];
    if (!pair) return;
    const targetWord = currentLineIdx === 0 ? pair.word1 : pair.word2;
    const result = checkRhymeLocal(rimeGuideeTranscript, targetWord);
    setRimeGuideeLastWord(result.lastWord);
    if (result.rhymes) {
      const globalLineIdx = currentPairIdx * 2 + currentLineIdx;
      setRimeGuideeValidated(prev => {
        const next = [...prev];
        next[globalLineIdx] = true;
        return next;
      });
      // Immediately clear transcript and stop STT
      rimeGuideeBaseTranscriptRef.current = "";
      rimeGuideeTranscriptRef.current = "";
      setRimeGuideeTranscript("");
      setRimeGuideeLastWord("");
      if (rimeGuideeRecognitionRef.current) {
        try { rimeGuideeRecognitionRef.current.stop(); } catch(e) {}
        rimeGuideeRecognitionRef.current = null;
      }
      // Move to next line after a brief delay, then restart STT
      setTimeout(() => {
        if (currentLineIdx === 0) {
          setRimeGuideeCurrentLine(1);
        } else {
          if (currentPairIdx < rimeGuideePairs.length - 1) {
            setRimeGuideeCurrentPair(p => p + 1);
            setRimeGuideeCurrentLine(0);
          } else {
            const usedWords = rimeGuideePairs.flatMap(p => [p.word1, p.word2]);
            const newPair1 = generateRhymePair(usedWords);
            const newPair2 = generateRhymePair([...usedWords, newPair1.word1, newPair1.word2]);
            setRimeGuideePairs([newPair1, newPair2]);
            setRimeGuideeCurrentPair(0);
            setRimeGuideeCurrentLine(0);
            setRimeGuideeValidated([false, false, false, false]);
            setRimeGuideeScrollKey(k => k + 1);
          }
        }
        // Restart STT for next line
        startRimeGuideeSTT();
      }, 600);
    }
  }, [rimeGuideeTranscript, rimeGuideeCurrentPair, rimeGuideeCurrentLine, rimeGuideePairs, checkRhymeLocal, generateRhymePair, startRimeGuideeSTT]);

  // Cleanup rime guidée on mode change
  useEffect(() => {
    if (currentMode !== "freestyle") {
      stopRimeGuidee();
    }
  }, [currentMode, stopRimeGuidee]);

  // Restart rime guidée when BPM changes
  useEffect(() => {
    if (rimeGuideeActive) {
      stopRimeGuidee();
      setTimeout(() => startRimeGuidee(), 50);
    }
  }, [bpm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close banques picker dropdown on outside click or Escape
  useEffect(() => {
    if (!banquesPickerOpen) return;
    const handleClickOutside = (e) => {
      const ref1 = banquesPickerRef.current;
      const ref2 = banquesPickerRefActive.current;
      const ref3 = banquesPickerDropdownRef.current;
      if (ref1 && ref1.contains(e.target)) return;
      if (ref2 && ref2.contains(e.target)) return;
      if (ref3 && ref3.contains(e.target)) return;
      setBanquesPickerOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setBanquesPickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [banquesPickerOpen]);

  // Handle spacebar press based on mode
  const handleSpacePress = useCallback(() => {
    // Chris TURNER sub-mode handles its own interactions internally
    if (currentMode === "freestyle" && chrisTurnerSelected) {
      return;
    }
    if (currentMode === "punchline") {
      if (punchlinePhase === "waiting") {
        startPunchlineSequenceRef.current?.();
      }
      return;
    }
    if (currentMode === "talking") {
      if (talkingPhase === "idle") getNextWord(false);
      else if (talkingPhase === "listening") finishTalkingRoundRef.current?.();
      else if (talkingPhase === "done") restartTalkingRoundRef.current?.();
      return;
    }
    if (currentMode === "banques") {
      // Infinite: SPACE toggles the fast random-word loop
      if (banquesPhase === "infinite") {
        // iOS Chrome/Safari: warm up TTS engine synchronously on user gesture.
        // A no-op `speak("")` here prevents subsequent async speak() calls from
        // being silently ignored (which would freeze our reveal sequence).
        try {
          const synth = window.speechSynthesis;
          if (synth) {
            const u = new SpeechSynthesisUtterance(' ');
            u.volume = 0;
            synth.speak(u);
          }
        } catch (e) {}
        setInfiniteRunning(v => !v);
        return;
      }
      if (banquesPhase === "idle") {
        // Same TTS warmup — must run synchronously inside the user-gesture event.
        try {
          const synth = window.speechSynthesis;
          if (synth) {
            const u = new SpeechSynthesisUtterance(' ');
            u.volume = 0;
            synth.speak(u);
          }
        } catch (e) {}
        setBanquesPhase("discovery");
        banquesPhaseRef.current = "discovery";
        setBanquesPickerOpen(false);
        return;
      }
      if (banquesPhase === "recall" && !banquesRevealed) setBanquesRevealed(true);
      else nextBanqueRef.current?.();
      return;
    }
    if (currentMode === "freestyle" || currentMode === "context" || currentMode === "perso") {
      if (currentMode === "perso") {
        if (personalWords.length === 0) return;
        if (persoActive) stopPersoSequence();
        else startPersoSequence();
        return;
      }
      if (currentMode === "freestyle" && (freestyleRhythmic || fillerSelected)) {
        if (fillerSelected && freestyleRhActive) {
          // Short press in filler mode while playing: cycle to next filler in the list
          if (fillerPhrases.length > 0) {
            setCurrentFiller(prev => {
              const idx = fillerPhrases.indexOf(prev);
              const nextIdx = idx === -1 ? 0 : (idx + 1) % fillerPhrases.length;
              return fillerPhrases[nextIdx];
            });
          }
          return;
        }
        if (freestyleRhActive) stopFreestyleRhSequence();
        else startFreestyleRhSequence();
        return;
      }
      if (currentMode === "freestyle" && rimeGuideeSelected) {
        if (rimeGuideeActive) stopRimeGuidee();
        else startRimeGuidee();
        return;
      }
      if (currentMode === "freestyle" && assisteSelected) {
        // Assisté: one-shot cycle. If idle or done, start a new cycle; if running, stop.
        if (assistePhaseRef.current === "idle" || assistePhaseRef.current === "done") {
          startAssisteSequence();
        } else {
          stopAssisteSequence();
        }
        return;
      }
      // Freestyle Libre + mot: SPACE toggles audio + always rolls a new word
      // Double-tap (2 presses within 300ms) → stop + rewind
      if (currentMode === "freestyle" && libreMotSelected) {
        const now = Date.now();
        const last = libreMotLastTapRef.current || 0;
        if (now - last < 300) {
          // Double-tap → stop everything + rewind
          libreMotLastTapRef.current = 0;
          if (metronomeActive) { stopMetronome(); setMetronomeActive(false); }
          if (mp3Playing && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setMp3Playing(false);
            setMp3Progress(0);
          }
          return;
        }
        libreMotLastTapRef.current = now;

        const isPlaying = metronomeActive || mp3Playing;
        if (!isPlaying) {
          // First press: start audio (if not silent) AND show first random word
          if (audioSource === "metronome") {
            startMetronome();
            setMetronomeActive(true);
          } else if (audioSource === "music" && mp3File && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
            setMp3Playing(true);
          }
          getNextWord();
        } else {
          // Already running: roll a new word, keep audio playing
          getNextWord();
        }
        return;
      }
      // Freestyle Libre (no sub-mode): SPACE toggles audio source (music/metronome/silent)
      if (currentMode === "freestyle") {
        if (audioSource === "silent") return;
        const isPlaying = metronomeActive || mp3Playing;
        if (isPlaying) {
          // Stop + reset
          if (metronomeActive) { stopMetronome(); setMetronomeActive(false); }
          if (mp3Playing && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setMp3Playing(false);
            setMp3Progress(0);
          }
        } else {
          if (audioSource === "metronome") {
            startMetronome();
            setMetronomeActive(true);
          } else if (audioSource === "music" && mp3File && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(() => {});
            setMp3Playing(true);
          }
        }
        return;
      }
      getNextWord();
    } else {
      if (!showSuggestions) {
        const cached = getCachedSuggestions(currentWord, currentMode);
        setSuggestions(cached);
        setShowSuggestions(true);
      } else {
        getNextWord();
      }
    }
  }, [currentMode, punchlinePhase, talkingPhase, banquesPhase, banquesRevealed, showSuggestions, currentWord, getNextWord, getCachedSuggestions, freestyleRhythmic, fillerSelected, fillerPhrases, freestyleRhActive, stopFreestyleRhSequence, startFreestyleRhSequence, rimeGuideeSelected, rimeGuideeActive, stopRimeGuidee, startRimeGuidee, persoActive, personalWords.length, startPersoSequence, stopPersoSequence, assisteSelected, startAssisteSequence, stopAssisteSequence, audioSource, metronomeActive, mp3File, mp3Playing, startMetronome, stopMetronome, libreMotSelected, chrisTurnerSelected]);

  // Long press handler for filler start/stop
  const handleFillerLongPress = useCallback(() => {
    if (!fillerSelected || currentMode !== "freestyle") return;
    if (freestyleRhActive) stopFreestyleRhSequence();
    else startFreestyleRhSequence();
  }, [fillerSelected, currentMode, freestyleRhActive, stopFreestyleRhSequence, startFreestyleRhSequence]);

  // Long press handler for banques pause/resume
  const handleBanquesLongPress = useCallback(() => {
    if (currentMode !== "banques") return;
    const isRunning = banquesPhase === "discovery" || banquesPhase === "infinite";
    if (isRunning) {
      const newVal = !banquesInfinitePaused;
      setBanquesInfinitePaused(newVal);
      banquesInfinitePausedRef.current = newVal;
      if (newVal) {
        window.speechSynthesis?.pause();
      } else {
        window.speechSynthesis?.resume();
      }
    }
  }, [currentMode, banquesPhase, banquesInfinitePaused]);

  // Keyboard listener for spacebar (with long press detection for filler)
  const spaceDownTimerRef = useRef(null);
  const spaceLongFiredRef = useRef(false);

  useEffect(() => {
    const needsLongPress = (fillerSelected && currentMode === "freestyle") || currentMode === "banques";

    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;
      if (e.code === "Space") {
        e.preventDefault();
        if (e.repeat) return;
        if (needsLongPress) {
          spaceLongFiredRef.current = false;
          spaceDownTimerRef.current = setTimeout(() => {
            spaceLongFiredRef.current = true;
            if (fillerSelected && currentMode === "freestyle") {
              handleFillerLongPress();
            } else if (currentMode === "banques") {
              handleBanquesLongPress();
            }
          }, 800);
        } else {
          handleSpacePress();
        }
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === "Space") {
        if (spaceDownTimerRef.current) {
          clearTimeout(spaceDownTimerRef.current);
          spaceDownTimerRef.current = null;
        }
        if (needsLongPress && !spaceLongFiredRef.current) {
          handleSpacePress();
        }
        spaceLongFiredRef.current = false;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      if (spaceDownTimerRef.current) clearTimeout(spaceDownTimerRef.current);
    };
  }, [handleSpacePress, handleFillerLongPress, handleBanquesLongPress, fillerSelected, currentMode]);

  const handlePunchlineEval = useCallback(() => {
    // Called at the end of beat 8
    const fullTranscript = transcriptRef.current;
    const v1 = verse1SnapshotRef.current;
    const v2text = fullTranscript.slice(v1.length).trim() || fullTranscript.trim();
    const result = checkRhymeLocal(v2text, currentWord);
    setLastSpokenWord(result.lastWord);

    if (v2text.length === 0) {
      setPunchlineResult("empty");
    } else if (result.rhymes) {
      setPunchlineResult("success");
      setStreak(s => {
        const ns = s + 1;
        if (ns > bestStreakRef.current) {
          setBestStreak(ns);
          bestStreakRef.current = ns;
          localStorage.setItem("freestyleBestStreak", ns.toString());
        }
        return ns;
      });
    } else {
      setPunchlineResult("fail");
      setStreak(0);
    }

    // Stop beat counter but keep current word visible with the result
    setPunchlineBeat(0);
    punchlineBeatRef.current = 0;

    // After 2s: change word, reset to waiting for next strophe
    if (punchlineResultTimerRef.current) clearTimeout(punchlineResultTimerRef.current);
    punchlineResultTimerRef.current = setTimeout(() => {
      setPunchlineResult(null);
      getNextWord(false);

      setPunchlinePhase("waiting");
      punchlinePhaseRef.current = "waiting";
      setTranscript("");
      transcriptRef.current = "";
      baseTranscriptRef.current = "";
      setVerse1Text("");
      verse1SnapshotRef.current = "";

      cooldownRef.current = true;
      setTimeout(() => { cooldownRef.current = false; }, 600);

      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch(e) {}
      }
    }, 2000);
  }, [currentWord, getNextWord, checkRhymeLocal]);

  // Keep refs in sync
  useEffect(() => { handlePunchlineEvalRef.current = handlePunchlineEval; }, [handlePunchlineEval]);

  // Full punchline sequence: 4 countdown beats + 4 verse1 beats + 4 verse2 beats = 12 beats
  const startPunchlineSequence = useCallback(() => {
    if (beatIntervalRef.current) return; // Already running

    // Ensure AudioContext exists for click sounds
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const playClick = (freq, type) => {
      try {
        const ctx = audioContextRef.current;
        if (!ctx || ctx.state === "closed") return;
        if (ctx.state === "suspended") ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = type;
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.06);
      } catch(e) {}
    };

    // Start countdown phase immediately
    setPunchlinePhase("countdown");
    punchlinePhaseRef.current = "countdown";
    setCountdownNumber(4);
    setPunchlineBeat(0);
    punchlineBeatRef.current = 0;
    setPunchlineResult(null);
    setVerse1Text("");
    verse1SnapshotRef.current = "";
    setTranscript("");
    transcriptRef.current = "";
    baseTranscriptRef.current = "";
    setBeatActive(true);
    setTimeout(() => setBeatActive(false), 100);
    playClick(800, "triangle"); // Countdown click

    const beatMs = (60 / bpm) * 1000;
    let count = 0;

    beatIntervalRef.current = setInterval(() => {
      count++;
      setBeatActive(true);
      setTimeout(() => setBeatActive(false), 100);

      if (count <= 2) {
        // Countdown beats: 3, 2
        setCountdownNumber(4 - count);
        playClick(800, "triangle");
      } else if (count === 3) {
        // Countdown beat: 1
        setCountdownNumber(1);
        playClick(1000, "triangle"); // Higher pitch for last countdown
      } else if (count === 4) {
        // Verse 1 starts!
        setCountdownNumber(0);
        setPunchlinePhase("verse1");
        punchlinePhaseRef.current = "verse1";
        setPunchlineBeat(1);
        punchlineBeatRef.current = 1;
        playClick(1200, "sine"); // Downbeat
      } else if (count <= 7) {
        // Verse 1: beats 2-4
        const beat = count - 3;
        setPunchlineBeat(beat);
        punchlineBeatRef.current = beat;
        playClick(1000, "sine");
      } else if (count === 8) {
        // Verse 2 starts — snapshot verse 1
        verse1SnapshotRef.current = transcriptRef.current;
        setVerse1Text(transcriptRef.current);
        setPunchlinePhase("verse2");
        punchlinePhaseRef.current = "verse2";
        setPunchlineBeat(5);
        punchlineBeatRef.current = 5;
        playClick(1200, "sine"); // Downbeat
      } else if (count <= 11) {
        // Verse 2: beats 6-8
        const beat = count - 3;
        setPunchlineBeat(beat);
        punchlineBeatRef.current = beat;
        playClick(1000, "sine");
      } else if (count === 12) {
        // End of beat 8 — grace period of 1 beat to finish the rhyme
        clearInterval(beatIntervalRef.current);
        beatIntervalRef.current = null;
        setTimeout(() => {
          handlePunchlineEvalRef.current?.();
        }, beatMs);
      }
    }, beatMs);
  }, [bpm]);

  useEffect(() => { startPunchlineSequenceRef.current = startPunchlineSequence; }, [startPunchlineSequence]);

  const startPunchlineListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Reconnaissance vocale non supportée. Utilisez Chrome ou Edge.");
      return;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
    }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';
    recognition.onresult = (event) => {
      let full = '', interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) full += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      const sessionText = (full + interim).trim();
      const combined = baseTranscriptRef.current + (baseTranscriptRef.current && sessionText ? ' ' : '') + sessionText;
      setTranscript(combined);
      transcriptRef.current = combined;
    };
    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        punchlinePhaseRef.current = "idle";
        setMicError("Micro refusé — autorisez l'accès dans les paramètres du navigateur");
        toast.error("Accès au micro refusé. Autorisez-le dans les paramètres du navigateur.");
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error("Speech recognition error:", event.error);
      }
    };
    recognition.onend = () => {
      if (punchlinePhaseRef.current !== "idle" && recognitionRef.current === recognition) {
        baseTranscriptRef.current = transcriptRef.current;
        try { recognition.start(); } catch(e) {}
      } else {
        setIsListening(false);
      }
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setIsListening(true);
      setPunchlinePhase("waiting");
      punchlinePhaseRef.current = "waiting";
      setTranscript("");
      transcriptRef.current = "";
      baseTranscriptRef.current = "";
      setStreak(0);
      setPunchlineResult(null);
      setLastSpokenWord("");
      setMicError(null);
      // Setup audio level meter (optional - degrades gracefully if blocked)
      (async () => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          mediaStreamRef.current = stream;
          const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          punchlineAudioCtxRef.current = audioCtx;
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          analyserRef.current = analyser;
          const source = audioCtx.createMediaStreamSource(stream);
          source.connect(analyser);
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          let lastUpdate = 0;
          const updateLevel = () => {
            if (!analyserRef.current) return;
            const now = Date.now();
            if (now - lastUpdate > 80) {
              analyser.getByteFrequencyData(dataArray);
              const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
              setAudioLevel(Math.min(avg / 80, 1));
              lastUpdate = now;
            }
            animFrameRef.current = requestAnimationFrame(updateLevel);
          };
          updateLevel();
          setVuMeterActive(true);
        } catch(e) {
          // VU meter unavailable (iframe, permissions) — silent fallback to pulse indicator
          console.warn("VU meter unavailable, using pulse indicator:", e.message);
        }
      })();
    } catch(e) {
      console.error("Failed to start speech recognition:", e);
      toast.error("Impossible d'accéder au micro");
    }
  }, []);

  const stopPunchlineListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch(e) {}
      recognitionRef.current = null;
    }
    // Cleanup audio meter
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (punchlineAudioCtxRef.current) {
      punchlineAudioCtxRef.current.close().catch(() => {});
      punchlineAudioCtxRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
    setVuMeterActive(false);
    setMicError(null);
    setIsListening(false);
    setPunchlinePhase("idle");
    punchlinePhaseRef.current = "idle";
    setTranscript("");
    transcriptRef.current = "";
    baseTranscriptRef.current = "";
    setPunchlineResult(null);
    setLastSpokenWord("");
    setPunchlineBeat(0);
    punchlineBeatRef.current = 0;
    setVerse1Text("");
    verse1SnapshotRef.current = "";
    if (beatIntervalRef.current) { clearInterval(beatIntervalRef.current); beatIntervalRef.current = null; }
    if (punchlineResultTimerRef.current) clearTimeout(punchlineResultTimerRef.current);
    cooldownRef.current = false;
  }, []);

  // Keep bestStreakRef in sync
  useEffect(() => { bestStreakRef.current = bestStreak; }, [bestStreak]);

  // Cleanup punchline on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
      if (beatIntervalRef.current) clearInterval(beatIntervalRef.current);
      if (punchlineResultTimerRef.current) clearTimeout(punchlineResultTimerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (punchlineAudioCtxRef.current) { punchlineAudioCtxRef.current.close().catch(() => {}); }
      if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach(t => t.stop()); }
    };
  }, []);

  // ============ TALKING MODE LOGIC ============
  const formatTalkingTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const stopTalkingMode = useCallback(() => {
    // Stop recognition
    if (talkingRecognitionRef.current) {
      try { talkingRecognitionRef.current.stop(); } catch(e) {}
      talkingRecognitionRef.current = null;
    }
    // Stop timer
    if (talkingTimerRef.current) { clearInterval(talkingTimerRef.current); talkingTimerRef.current = null; }
    // Stop silence checker
    if (talkingSilenceRef.current) { clearInterval(talkingSilenceRef.current); talkingSilenceRef.current = null; }
    // Keep media stream alive for other modes to reuse
    setMicError(null);
    setTalkingPhase("idle");
    talkingPhaseRef.current = "idle";
    setTalkingTranscript("");
    talkingTranscriptRef.current = "";
    talkingBaseTranscriptRef.current = "";
    setTalkingTime(0);
  }, []);

  const finishTalkingRound = useCallback(() => {
    // Stop timer and silence checker
    if (talkingTimerRef.current) { clearInterval(talkingTimerRef.current); talkingTimerRef.current = null; }
    if (talkingSilenceRef.current) { clearInterval(talkingSilenceRef.current); talkingSilenceRef.current = null; }
    // Calculate final time
    const elapsed = Date.now() - talkingStartTimeRef.current;
    setTalkingTime(elapsed);
    setTalkingPhase("done");
    talkingPhaseRef.current = "done";
    // Check record
    if (elapsed > talkingBest) {
      setTalkingBest(elapsed);
      localStorage.setItem("freestyleTalkingBest", elapsed.toString());
    }
    // Stop recognition
    if (talkingRecognitionRef.current) {
      try { talkingRecognitionRef.current.stop(); } catch(e) {}
    }
  }, [talkingBest]);

  const finishTalkingRoundRef = useRef(null);
  useEffect(() => { finishTalkingRoundRef.current = finishTalkingRound; }, [finishTalkingRound]);

  const restartTalkingRoundRef = useRef(null);

  // Watch talkingTranscript to detect first speech → start timer + silence checker
  useEffect(() => {
    if (currentMode !== "talking") return;
    if (talkingPhaseRef.current !== "idle") return;
    if (!talkingTranscript || !talkingTranscript.trim()) return;

    // First speech detected → transition to "listening"
    setTalkingPhase("listening");
    talkingPhaseRef.current = "listening";
    talkingStartTimeRef.current = Date.now();
    talkingLastSpeechRef.current = Date.now();

    // Start elapsed-time timer (update every 100ms)
    if (talkingTimerRef.current) clearInterval(talkingTimerRef.current);
    talkingTimerRef.current = setInterval(() => {
      setTalkingTime(Date.now() - talkingStartTimeRef.current);
    }, 100);

    // Start silence checker (every 500ms, check if 3s without speech)
    if (talkingSilenceRef.current) clearInterval(talkingSilenceRef.current);
    talkingSilenceRef.current = setInterval(() => {
      if (talkingPhaseRef.current === "listening" && Date.now() - talkingLastSpeechRef.current > 3000) {
        finishTalkingRoundRef.current?.();
      }
    }, 500);
  }, [talkingTranscript, currentMode]);

  const startTalkingListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Votre navigateur ne supporte pas la reconnaissance vocale.");
      return;
    }
    if (talkingRecognitionRef.current) {
      try { talkingRecognitionRef.current.stop(); } catch(e) {}
      talkingRecognitionRef.current = null;
    }

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';

    recognition.onresult = (event) => {
      let full = '', interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) full += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      const sessionText = (full + interim).trim();
      const combined = talkingBaseTranscriptRef.current + (talkingBaseTranscriptRef.current && sessionText ? ' ' : '') + sessionText;
      setTalkingTranscript(combined);
      talkingTranscriptRef.current = combined;
      talkingLastSpeechRef.current = Date.now();
    };
    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        talkingPhaseRef.current = "done";
        setMicError("Autorise le micro dans les paramètres du navigateur, puis relance le mode Flow.");
        toast.error("Accès au micro refusé. Autorise-le dans les paramètres du navigateur.");
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn("Speech recognition error:", event.error);
      }
    };
    recognition.onend = () => {
      if (talkingPhaseRef.current !== "done" && talkingRecognitionRef.current === recognition) {
        talkingBaseTranscriptRef.current = talkingTranscriptRef.current;
        try { recognition.start(); } catch(e) {}
      }
    };

    talkingRecognitionRef.current = recognition;
    try {
      recognition.start();
      setTalkingPhase("idle");
      talkingPhaseRef.current = "idle";
      setTalkingTranscript("");
      talkingTranscriptRef.current = "";
      talkingBaseTranscriptRef.current = "";
      setTalkingTime(0);
      talkingLastSpeechRef.current = Date.now();
      setMicError(null);
      // VU meter — async, optional, won't block recognition
      if (!mediaStreamRef.current || !mediaStreamRef.current.active) {
        (async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            punchlineAudioCtxRef.current = ctx;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyserRef.current = analyser;
            const source = ctx.createMediaStreamSource(stream);
            source.connect(analyser);
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const updateLevel = () => {
              if (!analyserRef.current) return;
              analyserRef.current.getByteFrequencyData(dataArray);
              const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
              setAudioLevel(Math.min(avg / 80, 1));
              animFrameRef.current = requestAnimationFrame(updateLevel);
            };
            updateLevel();
            setVuMeterActive(true);
          } catch(e) {
            console.warn("VU meter unavailable:", e.message);
          }
        })();
      }
    } catch(e) {
      console.error("Failed to start speech recognition:", e);
      toast.error("Impossible de lancer la reconnaissance vocale");
    }
  }, []);

  const restartTalkingRound = useCallback(() => {
    getNextWord(false);
    setTalkingPhase("idle");
    talkingPhaseRef.current = "idle";
    setTalkingTranscript("");
    talkingTranscriptRef.current = "";
    talkingBaseTranscriptRef.current = "";
    setTalkingTime(0);
    // Restart recognition
    if (talkingRecognitionRef.current) {
      try { talkingRecognitionRef.current.stop(); } catch(e) {}
    }
    // onend handler will restart it, but phase is "idle" so it won't.
    // We need to re-init
    setTimeout(() => startTalkingListening(), 200);
  }, [getNextWord, startTalkingListening]);
  useEffect(() => { restartTalkingRoundRef.current = restartTalkingRound; }, [restartTalkingRound]);

  // ========== BANQUES MODE LOGIC ==========

  const currentBanquesList = useMemo(() => getBanquesForGroup(), [getBanquesForGroup]);
  const currentBanque = currentBanquesList[banquesIndex] || BANQUES_DATA[0];
  const customWordsForBanque = banquesCustomWords[String(currentBanque.id)] || [];

  // Get words based on syllable filter.
  // Uses rapSyllabes() for dynamic classification (handles -ique, -gue digraphs correctly,
  // including reclassification of words hardcoded into the wrong mots1/2/3 bucket).
  // Alphabetical sort (French locale, accent-insensitive)
  const sortAlphaFr = (arr) => [...arr].sort((a, b) =>
    a.localeCompare(b, 'fr', { sensitivity: 'base' })
  );

  const getDefaultBanqueMots = (banque, sylFilter) => {
    // Sort each sub-group alphabetically first, then concatenate by syllable order.
    const m1 = sortAlphaFr(banque.mots1 || []);
    const m2 = sortAlphaFr(banque.mots2 || []);
    const m3 = sortAlphaFr(banque.mots3 || []);
    if (sylFilter === 0) {
      return [...m1, ...m2, ...m3];
    }
    const all = [...m1, ...m2, ...m3];
    return all.filter(w => {
      const n = rapSyllabes(w);
      return sylFilter === 3 ? n >= 3 : n === sylFilter;
    });
  };

  const defaultMots = getDefaultBanqueMots(currentBanque, banquesSyllabes);
  // When custom words are set for this banque, they override the hardcoded list.
  // Apply rap-style syllabification to filter custom words by syllable count.
  // Filter 3 (= 3 syllables) includes words with 3+ syllables.
  const filterCustomBySyllabes = (words) => {
    if (banquesSyllabes === 0) {
      // Group by rap syllable count, alpha-sort within each bucket, then concat.
      const b1 = [], b2 = [], b3 = [];
      words.forEach(w => {
        const n = Math.min(rapSyllabes(w), 3);
        if (n === 1) b1.push(w);
        else if (n === 2) b2.push(w);
        else b3.push(w);
      });
      return [...sortAlphaFr(b1), ...sortAlphaFr(b2), ...sortAlphaFr(b3)];
    }
    const filtered = words.filter(w => {
      const n = rapSyllabes(w);
      return banquesSyllabes === 3 ? n >= 3 : n === banquesSyllabes;
    });
    return sortAlphaFr(filtered);
  };
  const currentBanqueMots = customWordsForBanque.length > 0
    ? filterCustomBySyllabes(customWordsForBanque)
    : defaultMots;
  const banquesNoResults = currentBanqueMots.length === 0;
  const currentBanqueMotsRef = useRef(currentBanqueMots);
  currentBanqueMotsRef.current = currentBanqueMots;

  const stopBanquesRecognition = useCallback(() => {
    banquesPhaseRef.current = "idle";
    if (banquesRecognitionRef.current) {
      try { banquesRecognitionRef.current.stop(); } catch(e) {}
      banquesRecognitionRef.current = null;
    }
    if (banquesTimerRef.current) {
      clearInterval(banquesTimerRef.current);
      banquesTimerRef.current = null;
    }
    // Cancel any ongoing speech
    window.speechSynthesis?.cancel();
    // Keep media stream alive for other modes to reuse
  }, []);

  const startBanquesRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Votre navigateur ne supporte pas la reconnaissance vocale.");
      return;
    }
    // Stop any previous banques recognition only
    if (banquesRecognitionRef.current) {
      banquesPhaseRef.current = "idle";
      try { banquesRecognitionRef.current.stop(); } catch(e) {}
      banquesRecognitionRef.current = null;
    }

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'fr-FR';

    recognition.onresult = (event) => {
      let full = '', interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) full += r[0].transcript + ' ';
        else interim += r[0].transcript;
      }
      const sessionText = (full + interim).trim();
      const combined = banquesBaseTranscriptRef.current + (banquesBaseTranscriptRef.current && sessionText ? ' ' : '') + sessionText;
      setBanquesTranscript(combined);
      banquesTranscriptRef.current = combined;
    };
    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        banquesPhaseRef.current = "idle";
        toast.error("Accès au micro refusé. Autorise-le dans les paramètres du navigateur.");
      } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn("Banques recognition error:", event.error);
      }
    };
    recognition.onend = () => {
      if (banquesPhaseRef.current !== "idle" && banquesRecognitionRef.current === recognition) {
        banquesBaseTranscriptRef.current = banquesTranscriptRef.current;
        try { recognition.start(); } catch(e) {}
      }
    };

    banquesRecognitionRef.current = recognition;
    try {
      recognition.start();
      // VU meter — async, optional
      if (!mediaStreamRef.current || !mediaStreamRef.current.active) {
        (async () => {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            punchlineAudioCtxRef.current = ctx;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyserRef.current = analyser;
            const source = ctx.createMediaStreamSource(stream);
            source.connect(analyser);
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            const updateLevel = () => {
              if (!analyserRef.current) return;
              analyserRef.current.getByteFrequencyData(dataArray);
              const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
              setAudioLevel(Math.min(avg / 80, 1));
              animFrameRef.current = requestAnimationFrame(updateLevel);
            };
            updateLevel();
            setVuMeterActive(true);
          } catch(e) {
            console.warn("VU meter unavailable:", e.message);
          }
        })();
      }
    } catch(e) {
      console.warn("Failed to start recognition:", e);
    }
  }, []);

  // Match spoken words against current bank words (strict matching)
  useEffect(() => {
    if (currentMode !== "banques") return;
    if (!banquesTranscript) return;
    const normalize = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[œ]/g, "oe").replace(/[æ]/g, "ae").toLowerCase().trim();

    // Only check last 12 spoken words (recent speech window)
    const allWords = normalize(banquesTranscript).split(/\s+/).filter(w => w.length > 0);
    const recentWords = allWords.slice(-12);

    // Also build consecutive pairs for compound words (e.g. "ré action" → "réaction")
    const recentPairs = [];
    for (let i = 0; i < recentWords.length - 1; i++) {
      recentPairs.push(recentWords[i] + recentWords[i + 1]);
    }
    const candidates = [...recentWords, ...recentPairs];

    const newMatched = [];
    currentBanqueMots.forEach((mot) => {
      const normMot = normalize(mot);
      for (const sw of candidates) {
        // Exact match
        if (sw === normMot) {
          newMatched.push(mot);
          return;
        }
        // For words > 4 chars: allow max 1 char difference, length must be close
        if (normMot.length > 4 && sw.length > 3 && Math.abs(sw.length - normMot.length) <= 1) {
          let diff = 0;
          const shorter = sw.length <= normMot.length ? sw : normMot;
          const longer = sw.length <= normMot.length ? normMot : sw;
          for (let i = 0; i < shorter.length; i++) {
            if (shorter[i] !== longer[i]) diff++;
          }
          diff += longer.length - shorter.length;
          if (diff <= 1) {
            newMatched.push(mot);
            return;
          }
        }
      }
    });
    if (newMatched.length > 0) {
      setBanquesMatched(prev => {
        const merged = [...new Set([...prev, ...newMatched])];
        return merged;
      });
    }
  }, [banquesTranscript, currentBanque, currentMode, currentBanqueMots]);

  // Discovery phase: progressive word reveal with speech synthesis
  useEffect(() => {
    if (currentMode !== "banques" || banquesPhase !== "discovery") return;
    setBanquesVisibleWords([]);

    let cancelled = false;

    // Robust TTS for iOS Chrome/Safari:
    // - The speech engine often "sleeps" between speaks in a promise chain.
    //   Calling cancel() + resume() before each speak forces it awake.
    // - We resolve on `onend`, `onerror`, OR a duration-based fallback so the
    //   sequence keeps progressing even if `onend` never fires.
    const speakFrench = (text, rate = 0.9) => {
      return new Promise((resolve) => {
        const synth = window.speechSynthesis;
        if (!synth || !text) { resolve(); return; }

        let resolved = false;
        const safeResolve = () => {
          if (!resolved) { resolved = true; resolve(); }
        };

        try {
          // Wake the engine up — known iOS Safari/Chrome workaround.
          // cancel() flushes any pending state; resume() un-pauses if needed.
          try { synth.cancel(); } catch (e) {}
          try { synth.resume(); } catch (e) {}

          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'fr-FR';
          utterance.rate = rate;
          utterance.onend = safeResolve;
          utterance.onerror = safeResolve;
          synth.speak(utterance);
        } catch (e) {
          safeResolve();
          return;
        }

        // iOS fallback: estimate duration (~13 chars/sec at rate=1) + 1s margin.
        const charsPerSec = Math.max(6, 13 * rate);
        const estimatedMs = (text.length / charsPerSec) * 1000 + 1000;
        setTimeout(safeResolve, estimatedMs);
      });
    };

    const waitWhilePaused = async () => {
      while (banquesInfinitePausedRef.current && !cancelled) {
        await new Promise(r => setTimeout(r, 200));
      }
    };

    const revealSequence = async () => {
      await new Promise(r => setTimeout(r, 400));
      if (cancelled) return;
      await waitWhilePaused();

      // Early-skip optimization: if current banque has no words for the active syllable filter,
      // and at least one other banque has words, advance immediately without speaking the phoneme.
      // This avoids the "stuck on tion/sion" feeling when the syllable filter excludes all words.
      const earlyMots = currentBanqueMotsRef.current;
      if (earlyMots.length === 0) {
        const earlyHasOther = currentBanquesList.some((b, idx) => {
          if (idx === banquesIndex) return false;
          const bMots = getDefaultBanqueMots(b, banquesSyllabes);
          return bMots.length > 0;
        });
        if (earlyHasOther) {
          setBanquesIndex(prev => (prev + 1) % currentBanquesList.length);
          return;
        }
      }

      // 1. Pronounce the phoneme
      const phoneme = PHONEME_PRONUNCIATION[currentBanque.name] || currentBanque.name.replace(/\s*\/\s*/g, ', ').replace(/-/g, '');
      await speakFrench(phoneme, 0.85);
      if (cancelled) return;
      await waitWhilePaused();

      // 2. Announce syllable count
      if (banquesSyllabes > 0) {
        await speakFrench(`${banquesSyllabes} syllabe${banquesSyllabes > 1 ? 's' : ''}`, 0.85);
        if (cancelled) return;
        await waitWhilePaused();
      }

      await new Promise(r => setTimeout(r, 600));
      if (cancelled) return;

      // 2. Use hardcoded words (no more async API fetch needed).
      // Keep ascending syllable order (mots1, mots2, mots3 are already concatenated
      // in that order in currentBanqueMots) and cap at user-defined word limit.
      // Indices must match the displayed list (currentBanqueMots) so speech and UI stay in sync.
      const allMots = currentBanqueMotsRef.current;
      if (allMots.length === 0) {
        // No words for this syllable filter — skip to next banque if possible
        // Check if any other banque in the list has words for this filter
        const hasOtherBanque = currentBanquesList.some((b, idx) => {
          if (idx === banquesIndex) return false;
          const bMots = getDefaultBanqueMots(b, banquesSyllabes);
          return bMots.length > 0;
        });
        if (hasOtherBanque) {
          setBanquesIndex(prev => (prev + 1) % currentBanquesList.length);
        } else {
          await speakFrench("Aucun mot trouvé pour ce filtre", 0.85);
          setBanquesPhase("idle");
          banquesPhaseRef.current = "idle";
        }
        return;
      }
      const mots = allMots.slice(0, banquesWordLimit);
      let prevSylGroup = null;
      for (let i = 0; i < mots.length; i++) {
        if (cancelled) return;
        await waitWhilePaused();

        // Announce syllable group at transitions (only when filter = "toutes")
        if (banquesSyllabes === 0) {
          const n = Math.min(rapSyllabes(mots[i]), 3);
          if (n !== prevSylGroup) {
            const label = n === 1 ? "Une syllabe" : n === 2 ? "Deux syllabes" : "Trois syllabes";
            await speakFrench(label, 0.85);
            if (cancelled) return;
            await new Promise(r => setTimeout(r, 350));
            if (cancelled) return;
            await waitWhilePaused();
            prevSylGroup = n;
          }
        }

        setBanquesVisibleWords(prev => [...prev, i]);
        await speakFrench(mots[i], 0.9);
        if (cancelled) return;
        await new Promise(r => setTimeout(r, banquesWordDelay * 1000));
      }

      // 3. Auto-advance to next bank immediately (discovery & infinite)
      if (!cancelled) {
        await waitWhilePaused();
        if (!cancelled) {
          setBanquesIndex(prev => (prev + 1) % currentBanquesList.length);
        }
      }
    };

    revealSequence();

    return () => {
      cancelled = true;
      window.speechSynthesis?.cancel();
    };
  }, [currentMode, banquesPhase, banquesIndex, currentBanque, currentBanquesList, banquesWordDelay, banquesSyllabes, banquesWordLimit]);

  // Preload TTS voices so first utterance has no delay
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    // Trigger voices load (Chrome/Safari lazy-load)
    synth.getVoices();
    const onVoices = () => { synth.getVoices(); };
    synth.addEventListener?.('voiceschanged', onVoices);
    return () => synth.removeEventListener?.('voiceschanged', onVoices);
  }, []);

  // Infinite mode: fast random words from full dictionary, spoken instantly, every X seconds
  useEffect(() => {
    if (currentMode !== "banques" || banquesPhase !== "infinite" || !infiniteRunning) {
      return;
    }
    infiniteRunningRef.current = true;
    let cancelled = false;
    let timeoutId = null;

    const speakFast = (text) => {
      const synth = window.speechSynthesis;
      if (!synth) return;
      try { synth.cancel(); } catch (e) {}
      const utt = new SpeechSynthesisUtterance(text);
      utt.lang = 'fr-FR';
      utt.rate = 1.05;
      synth.speak(utt);
    };

    const tick = () => {
      if (cancelled || !infiniteRunningRef.current) return;
      const word = pickRandomWord(infiniteCurrentWord || "");
      setInfiniteCurrentWord(word);
      speakFast(word);
      timeoutId = setTimeout(tick, infiniteDelayRef.current * 1000);
    };

    // Start immediately (no initial delay, for snappy feel)
    tick();

    return () => {
      cancelled = true;
      infiniteRunningRef.current = false;
      if (timeoutId) clearTimeout(timeoutId);
      try { window.speechSynthesis?.cancel(); } catch (e) {}
    };
  }, [currentMode, banquesPhase, infiniteRunning, pickRandomWord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stop infinite if phase/mode changes
  useEffect(() => {
    if (!(currentMode === "banques" && banquesPhase === "infinite")) {
      if (infiniteRunning) setInfiniteRunning(false);
      if (infiniteCurrentWord) setInfiniteCurrentWord("");
    }
  }, [currentMode, banquesPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  const startBanquesMode = useCallback(() => {
    setBanquesIndex(0);
    setBanquesRevealed(false);
    setBanquesVisibleWords([]);
    setBanquesMatched([]);
    setBanquesTranscript("");
    banquesTranscriptRef.current = "";
    banquesBaseTranscriptRef.current = "";
    setBanquesTimer(0);
    banquesPhaseRef.current = banquesPhase;
    setBanquesInfinitePaused(false);
    banquesInfinitePausedRef.current = false;
    // No microphone needed for Découverte / Test / idle — STT was only for the
    // legacy "Rappel" / "Test rime" sub-modes which have been removed.
  }, [banquesPhase]);

  const nextBanque = useCallback(() => {
    setBanquesIndex(prev => (prev + 1) % currentBanquesList.length);
    setBanquesRevealed(false);
    setBanquesVisibleWords([]);
    setBanquesMatched([]);
    setBanquesTranscript("");
    banquesTranscriptRef.current = "";
    banquesBaseTranscriptRef.current = "";
    setBanquesTimer(0);
    // Stop old recognition cleanly. STT no longer used in Découverte / Test.
    banquesPhaseRef.current = "idle";
    if (banquesRecognitionRef.current) {
      try { banquesRecognitionRef.current.stop(); } catch(e) {}
      banquesRecognitionRef.current = null;
    }
  }, [currentBanquesList]);
  useEffect(() => { nextBanqueRef.current = nextBanque; }, [nextBanque]);

  const toggleBanqueMastered = useCallback((bankId) => {
    setBanquesMastered(prev => {
      const next = prev.includes(bankId) ? prev.filter(id => id !== bankId) : [...prev, bankId];
      localStorage.setItem("freestyleBanquesMastered", JSON.stringify(next));
      return next;
    });
  }, []);

  // Persist banques word limit
  useEffect(() => {
    localStorage.setItem("freestyleBanquesWordLimit", String(banquesWordLimit));
  }, [banquesWordLimit]);

  // Banques Freestyle timer
  useEffect(() => {
    if (currentMode !== "banques" || banquesPhase !== "freestyle") {
      if (banquesTimerRef.current) clearInterval(banquesTimerRef.current);
      return;
    }
    const startTime = Date.now();
    banquesTimerRef.current = setInterval(() => {
      setBanquesTimer(Date.now() - startTime);
    }, 100);
    return () => { if (banquesTimerRef.current) clearInterval(banquesTimerRef.current); };
  }, [currentMode, banquesPhase, banquesIndex]);

  const formatBanquesTime = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  // ========== END BANQUES LOGIC ==========

  return (
    <div className="app-container" data-testid="app-container">
      <Toaster position="top-center" richColors />

      {/* Auth button - top right */}
      <div className="auth-bar" data-testid="auth-bar">
        {authLoading ? null : authUser ? (
          <div className="auth-user" data-testid="auth-user">
            {authUser.picture && (
              <img src={authUser.picture} alt="" className="auth-avatar" referrerPolicy="no-referrer" />
            )}
            <span className="auth-name" data-testid="auth-name">{authUser.name?.split(' ')[0]}</span>
            <Cloud className="h-3.5 w-3.5 auth-cloud-icon" />
            <button onClick={handleLogout} className="auth-logout-btn" data-testid="auth-logout-btn" title="Déconnexion">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button onClick={handleLogin} className="auth-login-btn" data-testid="auth-login-btn">
            <LogIn className="h-3.5 w-3.5" />
            <span>Connexion</span>
          </button>
        )}
      </div>

      {/* Hidden audio element */}
      {mp3File && (
        <audio
          ref={audioRef}
          src={mp3File.url}
          muted={mp3Muted}
          onTimeUpdate={(e) => setMp3Progress(e.currentTarget.currentTime || 0)}
          onLoadedMetadata={(e) => setMp3Duration(e.currentTarget.duration || 0)}
          onDurationChange={(e) => setMp3Duration(e.currentTarget.duration || 0)}
          onEnded={() => {
            // Hold cursor at 100% for 1s, then reset & stop. Audio source is
            // no longer auto-looped: the user must press play (or the rewind
            // button) to restart.
            if (audioRef.current) setMp3Progress(audioRef.current.duration || 0);
            setMp3Playing(false);
            setTimeout(() => {
              if (audioRef.current) {
                audioRef.current.currentTime = 0;
              }
              setMp3Progress(0);
            }, 1000);
          }}
        />
      )}

      {/* Session Timer Bar */}
      {sessionActive && (
        <div className="session-bar" data-testid="session-bar">
          <div className="session-info">
            <Timer className="h-4 w-4" />
            <span className="session-time" data-testid="session-time">{formatTime(sessionTimeLeft)}</span>
            <span className="session-words" data-testid="session-words">{sessionWordCount} mots</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={stopSession}
            data-testid="stop-session-btn"
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <StopCircle className="h-4 w-4 mr-1" />
            Arrêter
          </Button>
        </div>
      )}

      {/* Challenge Timer Bar */}
      {challengeActive && (
        <div className="challenge-bar" data-testid="challenge-bar">
          <div className="challenge-info">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <span className={`challenge-time ${challengeTimeLeft <= 10 ? "text-red-500 animate-pulse" : ""}`} data-testid="challenge-time">
              {challengeTimeLeft}s
            </span>
            <span className="challenge-score" data-testid="challenge-score">
              Score: {challengeScore}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={stopChallenge}
            data-testid="stop-challenge-btn"
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Challenge Result Overlay */}
      {showChallengeResult && (
        <div className="challenge-result-overlay" data-testid="challenge-result">
          <div className="challenge-result-card">
            <Trophy className="h-12 w-12 text-yellow-500 mb-4" />
            <h2 className="challenge-result-title">Challenge terminé !</h2>
            <div className="challenge-result-score" data-testid="final-score">
              {challengeScore} mots
            </div>
            {challengeScore >= challengeHighScore && challengeScore > 0 && (
              <div className="challenge-new-record">🏆 Nouveau record !</div>
            )}
            <div className="challenge-high-score" data-testid="high-score">
              Record : {challengeHighScore} mots
            </div>
            <div className="challenge-result-actions">
              <Button onClick={startChallenge} data-testid="retry-challenge-btn">
                <RotateCcw className="h-4 w-4 mr-2" />
                Rejouer
              </Button>
              <Button variant="outline" onClick={() => setShowChallengeResult(false)} data-testid="close-result-btn">
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dictionary Filter Bar moved below command bar — see below */}

      {/* Freestyle sub-mode toggle moved to bottom bar */}

      {/* Punchline Mode Bar */}
      {currentMode === "punchline" && punchlinePhase !== "idle" && (
        <div className="punchline-bar" data-testid="punchline-bar">
          <div className="punchline-info">
            <div className="punchline-mic" data-testid="punchline-mic">
              {micError ? (
                <div className="mic-error-badge">
                  <MicOff className="h-4 w-4" />
                  <span>{micError}</span>
                </div>
              ) : vuMeterActive ? (
                <>
                  <Mic className="h-4 w-4" style={{ color: audioLevel > 0.05 ? '#86efac' : '#a3a3a3', transition: 'color 0.2s' }} />
                  <div className={`audio-bars ${audioLevel > 0.05 ? 'active' : ''}`} data-testid="audio-bars">
                    {[0.5, 0.8, 1.0, 0.7, 0.4].map((mult, i) => (
                      <div
                        key={i}
                        className="audio-bar"
                        style={{ height: `${Math.max(3, audioLevel * 22 * mult)}px` }}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="mic-pulse" />
                  <Mic className="h-4 w-4" style={{ color: '#86efac' }} />
                </>
              )}
            </div>
            <span className={`punchline-phase ${punchlinePhase}`} data-testid="punchline-phase">
              {punchlinePhase === "waiting" ? "Appuie sur ESPACE" : punchlinePhase === "countdown" ? "Prêt ?" : punchlinePhase === "verse1" ? "Vers 1" : "Vers 2"}
            </span>
            {/* Countdown number */}
            {punchlinePhase === "countdown" && (
              <span className="punchline-countdown-number" data-testid="countdown-number">{countdownNumber}</span>
            )}
            {/* Beat counter dots */}
            {(punchlinePhase === "verse1" || punchlinePhase === "verse2") && (
              <div className="punchline-beats" data-testid="punchline-beats">
                {[1,2,3,4,5,6,7,8].map(b => (
                  <div
                    key={b}
                    className={`beat-dot ${b <= punchlineBeat ? 'active' : ''} ${b <= 4 ? 'v1' : 'v2'}`}
                    data-testid={`beat-${b}`}
                  />
                ))}
              </div>
            )}
            <div className="punchline-streak" data-testid="punchline-streak">
              <Zap className="h-4 w-4" />
              <span className="punchline-streak-count">{streak}</span>
            </div>
            <span className="punchline-best" data-testid="punchline-best-streak">
              Record : {bestStreak}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { stopPunchlineListening(); handleModeChange("freestyle"); }}
            data-testid="stop-punchline-btn"
          >
            <X className="h-4 w-4 mr-1" />
            Arrêter
          </Button>
        </div>
      )}

      {/* Talking Mode Bar */}
      {currentMode === "talking" && talkingPhase !== "idle" && (
        <div className="punchline-bar talking-bar" data-testid="talking-bar">
          <div className="punchline-info">
            <div className="punchline-mic" data-testid="talking-mic">
              {micError ? (
                <div className="mic-error-badge">
                  <MicOff className="h-4 w-4" />
                  <span>{micError}</span>
                </div>
              ) : vuMeterActive ? (
                <>
                  <Mic className="h-4 w-4" style={{ color: audioLevel > 0.05 ? '#86efac' : '#a3a3a3', transition: 'color 0.2s' }} />
                  <div className={`audio-bars ${audioLevel > 0.05 ? 'active' : ''}`}>
                    {[0.5, 0.8, 1.0, 0.7, 0.4].map((mult, i) => (
                      <div key={i} className="audio-bar" style={{ height: `${Math.max(3, audioLevel * 22 * mult)}px` }} />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="mic-pulse" />
                  <Mic className="h-4 w-4" style={{ color: '#86efac' }} />
                </>
              )}
            </div>
            <span className="talking-timer" data-testid="talking-timer">
              {formatTalkingTime(talkingTime)}
            </span>
            {talkingPhase === "done" && (
              <span className="talking-done-badge" data-testid="talking-done">Terminé !</span>
            )}
            <span className="talking-best" data-testid="talking-best">
              Record : {formatTalkingTime(talkingBest)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {talkingPhase === "done" && (
              <Button variant="ghost" size="sm" onClick={restartTalkingRound} data-testid="talking-restart-btn">
                Rejouer
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { stopTalkingMode(); handleModeChange("freestyle"); }}
              data-testid="stop-talking-btn"
            >
              <X className="h-4 w-4 mr-1" />
              Arrêter
            </Button>
          </div>
        </div>
      )}
      {currentMode === "talking" && talkingPhase === "idle" && (
        <div className="punchline-bar talking-bar" data-testid="talking-bar">
          <div className="punchline-info">
            <div className="punchline-mic" data-testid="talking-mic">
              {micError ? (
                <div className="mic-error-badge">
                  <MicOff className="h-4 w-4" />
                  <span>{micError}</span>
                </div>
              ) : vuMeterActive ? (
                <>
                  <Mic className="h-4 w-4" style={{ color: audioLevel > 0.05 ? '#86efac' : '#a3a3a3', transition: 'color 0.2s' }} />
                  <div className={`audio-bars ${audioLevel > 0.05 ? 'active' : ''}`}>
                    {[0.5, 0.8, 1.0, 0.7, 0.4].map((mult, i) => (
                      <div key={i} className="audio-bar" style={{ height: `${Math.max(3, audioLevel * 22 * mult)}px` }} />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="mic-pulse" />
                  <Mic className="h-4 w-4" style={{ color: '#86efac' }} />
                </>
              )}
            </div>
            <span className="punchline-phase waiting">Commence à parler...</span>
            <span className="talking-best" data-testid="talking-best">
              Record : {formatTalkingTime(talkingBest)}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { stopTalkingMode(); handleModeChange("freestyle"); }}
            data-testid="stop-talking-btn"
          >
            <X className="h-4 w-4 mr-1" />
            Arrêter
          </Button>
        </div>
      )}

      {/* Punchline Flash Overlay */}
      {punchlineResult && currentMode === "punchline" && (
        <div className={`punchline-flash ${punchlineResult}`} data-testid="punchline-flash" />
      )}

      {/* ========== BANQUES MODE UI ========== */}
      {currentMode === "banques" ? (
        <div className="banques-container" data-testid="banques-container" onClick={() => handleBanquesClick()}>
          {banquesPhase === "idle" ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '1.5rem' }}>
                <div style={{ fontSize: '3rem', fontWeight: 800, color: '#1a1a2e' }}>MODE BANQUES</div>
                <div style={{ fontSize: '1.1rem', color: '#737373', textAlign: 'center', maxWidth: '400px' }}>
                  Appuie sur l'écran ou sur ESPACE pour commencer
                </div>
              </div>
            </>
          ) : banquesPhase === "infinite" ? (
            <>
              {/* INFINITE MODE: fast random words from full dictionary */}
              <div className="banques-infinite-zone" data-testid="banques-infinite-zone" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '2rem', minHeight: 0 }}>
                {infiniteCurrentWord ? (
                  <div
                    key={infiniteCurrentWord + Date.now()}
                    data-testid="infinite-current-word"
                    style={{ fontSize: 'clamp(3rem, 11vw, 8.5rem)', fontWeight: 900, color: '#0A0A0A', textAlign: 'center', letterSpacing: '-0.03em', lineHeight: 1, textTransform: 'uppercase', userSelect: 'none' }}
                  >
                    {infiniteCurrentWord}
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '3rem', fontWeight: 800, color: '#1a1a2e' }}>TEST</div>
                    <div style={{ fontSize: '1.1rem', color: '#737373', textAlign: 'center', maxWidth: '420px' }}>
                      Appuie sur ESPACE pour lancer le flux de mots
                    </div>
                  </>
                )}
                <div style={{ fontSize: '0.85rem', color: '#737373' }}>
                  Intervalle : <strong>{infiniteDelay.toFixed(1)}s</strong> — {infiniteRunning ? 'en cours' : 'en pause'}
                </div>
              </div>
            </>
          ) : (
          <>
          {/* Bank name */}
          <div className="banques-bank-name" data-testid="banques-bank-name">
            {currentBanque.name.replace(/-([a-zA-ZÀ-ÿœæ]+)/g, '"$1"')}
          </div>

          {/* VU meter indicator — hidden in discovery phase */}
          {banquesPhase !== "discovery" && (
          <div className="banques-mic-indicator" data-testid="banques-mic">
            {vuMeterActive ? (
              <div className="banques-vu">
                <Mic className="h-4 w-4" style={{ color: audioLevel > 0.05 ? '#86efac' : 'rgba(255,255,255,0.5)', transition: 'color 0.2s' }} />
                <div className={`audio-bars ${audioLevel > 0.05 ? 'active' : ''}`}>
                  {[0.5, 0.8, 1.0, 0.7, 0.4].map((mult, i) => (
                    <div key={i} className="audio-bar" style={{ height: `${Math.max(3, audioLevel * 22 * mult)}px` }} />
                  ))}
                </div>
                <span className="banques-mic-label">En écoute</span>
              </div>
            ) : (
              <div className="banques-vu">
                <div className="mic-pulse" />
                <Mic className="h-4 w-4" style={{ color: '#86efac' }} />
                <span className="banques-mic-label">Micro actif</span>
              </div>
            )}
          </div>
          )}

          {/* Phase-specific content */}
          {banquesPhase === "discovery" && (
            <div className="banques-discovery-zone" data-testid="banques-discovery-zone">
              {/* Word delay slider */}
              <div className="banques-delay-control" data-testid="banques-delay-control">
                <label className="banques-delay-label">
                  Vitesse : <strong>{banquesWordDelay.toFixed(1)}s</strong>
                </label>
                <div className="banques-delay-slider-row">
                  <span className="banques-delay-icon">&#x1F407;</span>
                  <Slider
                    data-testid="banques-delay-slider"
                    min={0.3}
                    max={5}
                    step={0.1}
                    value={[banquesWordDelay]}
                    onValueChange={([val]) => setBanquesWordDelay(val)}
                    className="banques-delay-slider"
                  />
                  <span className="banques-delay-icon">&#x1F422;</span>
                </div>
              </div>
              {/* Pause removed — double-tap to pause/resume */}
              <div className="banques-words-grid" data-testid="banques-words-grid">
                {banquesNoResults ? (
                  <div data-testid="banques-no-words-msg" style={{ flexBasis: '100%', textAlign: 'center', color: '#737373', fontStyle: 'italic', padding: '1.5rem 0', fontSize: '0.95rem' }}>
                    Aucun mot à {banquesSyllabes} syllabe{banquesSyllabes > 1 ? 's' : ''} pour cette terminaison.
                    <br />Passage à la banque suivante…
                  </div>
                ) : (() => {
                  const elements = [];
                  let prevSyl = null;
                  currentBanqueMots.forEach((mot, i) => {
                    if (banquesSyllabes === 0) {
                      const n = Math.min(rapSyllabes(mot), 3);
                      if (n !== prevSyl) {
                        const label = n === 1 ? "1 syllabe" : n === 2 ? "2 syllabes" : "3+ syllabes";
                        elements.push(
                          <div key={`sep-${n}`} className="banques-syllabe-separator" data-testid={`banques-syllabe-separator-${n}`}>
                            <span className="banques-syllabe-label">{label}</span>
                          </div>
                        );
                        prevSyl = n;
                      }
                    }
                    elements.push(
                      <span
                        key={mot}
                        className={`banques-word ${banquesVisibleWords.includes(i) ? "visible" : ""} ${banquesMatched.includes(mot) ? "matched" : ""}`}
                        data-testid={`banques-word-${i}`}
                      >
                        {mot}
                      </span>
                    );
                  });
                  return elements;
                })()}
              </div>
            </div>
          )}

          {banquesPhase === "recall" && (
            <div className="banques-recall-zone" data-testid="banques-recall-zone">
              {!banquesRevealed ? (
                <>
                  <div className="banques-recall-prompt">
                    Récite les 8 mots de mémoire...
                  </div>
                  <div className="banques-matched-count">
                    {banquesMatched.length > 0 && (
                      <span>{banquesMatched.length} / 8 détectés</span>
                    )}
                  </div>
                  <div className="banques-words-grid recall-hidden">
                    {currentBanqueMots.map((mot, i) => (
                      <span
                        key={mot}
                        className={`banques-word ${banquesMatched.includes(mot) ? "visible matched" : "hidden-word"}`}
                        data-testid={`banques-recall-word-${i}`}
                      >
                        {banquesMatched.includes(mot) ? mot : "???"}
                      </span>
                    ))}
                  </div>
                  <div className="banques-hint">ESPACE pour révéler</div>
                </>
              ) : (
                <div className="banques-words-grid">
                  {currentBanqueMots.map((mot, i) => (
                    <span
                      key={mot}
                      className={`banques-word visible ${banquesMatched.includes(mot) ? "matched" : "missed"}`}
                      data-testid={`banques-revealed-word-${i}`}
                    >
                      {mot}
                    </span>
                  ))}
                </div>
              )}
              <div className="banques-score">
                {banquesMatched.length} / 8
              </div>
              {banquesTranscript && (
                <div className="banques-transcript" data-testid="banques-recall-transcript">
                  {banquesTranscript}
                </div>
              )}
            </div>
          )}

          {banquesPhase === "freestyle" && (
            <div className="banques-freestyle-zone" data-testid="banques-freestyle-zone">
              <div className="banques-freestyle-timer">{formatBanquesTime(banquesTimer)}</div>
              <div className="banques-freestyle-hint">Improvise 4 mesures avec le son {currentBanque.name.replace(/-([a-zA-ZÀ-ÿœæ]+)/g, '"$1"')}</div>
              {banquesTranscript && (
                <div className="banques-transcript" data-testid="banques-transcript">
                  {banquesTranscript}
                </div>
              )}
            </div>
          )}

          {/* Mastered toggle + Edit words */}
          <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'center', alignItems: 'center' }}>
            <button
              className={`banques-master-btn ${banquesMastered.includes(currentBanque.id) ? "mastered" : ""}`}
              data-testid="banques-master-toggle"
              onClick={(e) => {
                e.stopPropagation();
                toggleBanqueMastered(currentBanque.id);
              }}
            >
              {banquesMastered.includes(currentBanque.id) ? "Maîtrisée" : "Marquer maîtrisée"}
            </button>
          </div>

          <div className="banques-footer-hint">
            {banquesPhase === "infinite" 
              ? (banquesInfinitePaused ? "En pause — Double-tap ou maintenir ESPACE pour reprendre" : "Double-tap ou maintenir ESPACE = pause")
              : banquesPhase === "discovery"
              ? (banquesInfinitePaused ? "En pause — Double-tap ou maintenir ESPACE pour reprendre" : "Double-tap ou maintenir ESPACE = pause")
              : <>Tap → {banquesPhase === "recall" && !banquesRevealed ? "Révéler" : "Suivante"}</>
            }
          </div>
          </>
          )}
        </div>
      ) : (currentMode === "freestyle" && chrisTurnerSelected) ? (
        /* ========== CHRIS TURNER SUB-MODE (under Freestyle) ========== */
        <ChrisTurnerMode
          backendApi={API}
          isAuthenticated={!!authUser}
          setDashboardCollapsed={setDashboardCollapsed}
          dashboardAutoHideLockRef={dashboardAutoHideLockRef}
          audioPlaying={metronomeActive || mp3Playing}
          setBeatDuck={setBeatDuck}
        />
      ) : (
      /* ========== NORMAL WORD CANVAS ========== */
      <div className="word-canvas" data-testid="word-canvas" onClick={handleCanvasClick}>
        {/* Perso beat bar */}
        {currentMode === "perso" && persoActive && (
          <div className="punchline-bar" data-testid="perso-beat-bar" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
            <div className="punchline-info">
              {persoPhase === "countdown" ? (
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fbbf24' }}>
                  Décompte : {persoCountdown}
                </span>
              ) : (
                <>
                  <div className="punchline-beats">
                    {[1, 2, 3, 4].map(b => (
                      <div key={b} className={`punchline-beat ${persoBeat === b ? 'active' : ''} ${persoBeat === b && b === 1 ? 'downbeat' : ''}`}>
                        {b}
                      </div>
                    ))}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    {bpm} BPM — nouveau mot tous les 4 temps
                  </span>
                </>
              )}
              <button
                data-testid="perso-stop-btn"
                onClick={(e) => { e.stopPropagation(); stopPersoSequence(); }}
                className="punchline-stop-btn"
              >
                Arrêter
              </button>
            </div>
          </div>
        )}
        {currentMode === "freestyle" && freestyleRhActive && false && (
          <div className="punchline-bar" data-testid="freestyle-rh-beat-bar" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
            <div className="punchline-info">
              {freestyleRhPhase === "countdown" ? (
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fbbf24' }}>
                  Décompte : {freestyleRhCountdown}
                </span>
              ) : (
                <>
                  <div className="punchline-beats">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(b => (
                      <div key={b} className={`punchline-beat ${freestyleRhBeat === b ? 'active' : ''} ${freestyleRhBeat === b && b === 1 ? 'downbeat' : ''}`}>
                        {b}
                      </div>
                    ))}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    {bpm} BPM — {fillerSelected ? 'Filler' : 'nouveau mot tous les 8 temps'}
                  </span>
                  <div className="rh-subdiv-selector" data-testid="rh-subdiv-selector">
                    {[
                      { id: "noire", label: "Noire" },
                      { id: "croche", label: "Croche" },
                      { id: "double-croche", label: "Dbl croche" },
                    ].map(s => (
                      <button
                        key={s.id}
                        data-testid={`rh-subdiv-${s.id}`}
                        className={`rh-subdiv-btn ${freestyleRhSubdiv === s.id ? 'rh-subdiv-active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setFreestyleRhSubdiv(s.id); }}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <button
                data-testid="freestyle-rh-stop-btn"
                onClick={(e) => { e.stopPropagation(); stopFreestyleRhSequence(); }}
                className="punchline-stop-btn"
              >
                Arrêter
              </button>
            </div>
          </div>
        )}
        {currentMode === "freestyle" && rimeGuideeActive && false && (
          <div className="punchline-bar" data-testid="rg-beat-bar" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
            <div className="punchline-info">
              {rimeGuideePhase === "countdown" ? (
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fbbf24' }}>
                  Prépare-toi...
                </span>
              ) : (
                <>
                  <div className="punchline-beats">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(b => (
                      <div key={b} className={`punchline-beat ${rimeGuideeBeat === b ? 'active' : ''} ${rimeGuideeBeat === b && b === 1 ? 'downbeat' : ''}`}>
                        {b}
                      </div>
                    ))}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    {bpm} BPM — Rime Guidée
                  </span>
                </>
              )}
              <button
                data-testid="rg-stop-btn"
                onClick={(e) => { e.stopPropagation(); stopRimeGuidee(); }}
                className="punchline-stop-btn"
              >
                Arrêter
              </button>
            </div>
          </div>
        )}
        {currentMode === "freestyle" && assisteSelected && (assisteActive || assistePhase === "done") && false && (
          <div className="punchline-bar" data-testid="assiste-beat-bar" style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
            <div className="punchline-info">
              {assistePhase === "speaking" ? (
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fbbf24' }}>
                  Écoute le mot...
                </span>
              ) : assistePhase === "countdown" ? (
                <span style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fbbf24' }}>
                  Prépare-toi...
                </span>
              ) : assistePhase === "done" ? (
                <span style={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>
                  Cycle terminé — ESPACE pour continuer
                </span>
              ) : (
                <>
                  <div className="punchline-beats">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(b => (
                      <div key={b} className={`punchline-beat ${assisteBeat === b ? 'active' : ''} ${assisteBeat === b && b === 1 ? 'downbeat' : ''}`}>
                        {b}
                      </div>
                    ))}
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                    {bpm} BPM — Assisté
                  </span>
                </>
              )}
              <button
                data-testid="assiste-stop-btn"
                onClick={(e) => { e.stopPropagation(); stopAssisteSequence(); }}
                className="punchline-stop-btn"
              >
                Arrêter
              </button>
            </div>
          </div>
        )}
        {/* Word Source Indicator */}
        <div className="word-source" data-testid="word-source">
          {currentMode === "perso" ? (
            <>Liste perso ({personalWords.length} mots)</>
          ) : dictionaryAvailable ? (
            <>
              Dictionnaire ({words.length} mots
              {activeTheme !== "ALL" && ` - ${dictionaryThemes.find(t => t.id === activeTheme)?.label || activeTheme}`}
              {activeSyllabes > 0 && ` - ${activeSyllabes} syl.`}
              )
            </>
          ) : (
            <>{wordSource === "custom" ? "Liste personnalisée" : "Liste par défaut"} ({words.length} mots)</>
          )}
        </div>

        {/* Main Word Display - with image for context mode */}
        <div className={`word-container ${currentMode === "context" ? "with-image" : ""}`}>
          {/* Context Portrait Image */}
          {currentMode === "context" && !challengeActive && (
            <div className="context-image-container" data-testid="context-image-container">
              {isLoadingImage && (
                <div className="context-image-loading" data-testid="image-loading">
                  <div className="loading-spinner">
                    <Loader2 className="h-10 w-10 animate-spin text-gray-400" />
                  </div>
                  <div className="loading-text">Génération du portrait...</div>
                </div>
              )}
              {contextImage && contextImage.base64 && !isLoadingImage && (
                <img 
                  src={`data:image/jpeg;base64,${contextImage.base64}`}
                  alt="Portrait contextuel"
                  className="context-image"
                  data-testid="context-image"
                />
              )}
              {!contextImage && !isLoadingImage && (
                <div className="context-image-placeholder">
                  <div className="placeholder-icon">👤</div>
                  <div className="placeholder-text">Portrait en attente</div>
                </div>
              )}
            </div>
          )}

          <div className="word-and-suggestions">
            {/* Perso mode: edit button + empty state */}
            {currentMode === "perso" && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                <button
                  data-testid="perso-edit-btn"
                  onClick={() => {
                    setPersoEditorText(personalWords.join('\n'));
                    setShowPersoEditor(true);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '0.4rem 1.2rem',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                >
                  <Sparkles className="h-3.5 w-3.5" /> Éditer ma liste ({personalWords.length} mot{personalWords.length !== 1 ? 's' : ''})
                  {authUser && <Cloud className="h-3 w-3" style={{ marginLeft: '0.2rem', opacity: 0.7 }} />}
                </button>
              </div>
            )}
            {currentMode === "perso" && personalWords.length === 0 && (
              <div style={{ textAlign: 'center', color: '#a3a3a3', padding: '2rem 1rem' }} data-testid="perso-empty">
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📝</div>
                <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Ta liste est vide</div>
                <div style={{ fontSize: '0.85rem' }}>Clique sur « Éditer ma liste » pour ajouter tes mots</div>
                {authUser && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(34,197,94,0.06)', borderRadius: '12px', border: '1px solid rgba(34,197,94,0.15)' }}>
                    <div style={{ fontSize: '0.82rem', color: '#4b5563', marginBottom: '0.5rem' }}>
                      Tu as des mots sur un autre appareil ?
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.8rem' }}>
                      Sauvegarde d'abord ta liste depuis l'appareil où elle existe,<br/>
                      puis reviens ici et clique sur :
                    </div>
                    <button
                      data-testid="perso-force-sync"
                      onClick={async () => {
                        const token = localStorage.getItem("ff_auth_token");
                        if (!token) { toast.error("Non connecté"); return; }
                        try {
                          const resp = await axios.get(`${API}/user/words`, { headers: { Authorization: `Bearer ${token}` } });
                          const cloudWords = resp.data.words || [];
                          if (cloudWords.length > 0) {
                            setPersonalWords(cloudWords);
                            localStorage.setItem('freestyleflow_perso_words', JSON.stringify(cloudWords));
                            toast.success(`${cloudWords.length} mot${cloudWords.length > 1 ? 's' : ''} récupéré${cloudWords.length > 1 ? 's' : ''} du cloud !`);
                          } else {
                            toast.info("Aucun mot trouvé dans le cloud. Sauvegarde ta liste depuis ton desktop d'abord.");
                          }
                        } catch (e) {
                          toast.error("Erreur de synchronisation");
                        }
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                        padding: '0.5rem 1.2rem', borderRadius: '9999px',
                        background: '#22c55e', color: '#fff', fontWeight: 600,
                        fontSize: '0.82rem', border: 'none', cursor: 'pointer',
                      }}
                    >
                      <Cloud className="h-3.5 w-3.5" /> Récupérer depuis le cloud
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Countdown overlay */}
            {currentMode === "punchline" && punchlinePhase === "countdown" && (
              <div className="punchline-countdown-overlay" data-testid="countdown-overlay">
                <span className="countdown-digit" key={countdownNumber}>{countdownNumber}</span>
              </div>
            )}
            {currentMode === "freestyle" && freestyleRhPhase === "countdown" && freestyleRhLines.length === 4 && (
              <div className="punchline-countdown-overlay rh-countdown-over-lines" data-testid="freestyle-rh-countdown-overlay">
                <span className="freestyle-rh-number countdown" key={`cd-${freestyleRhCountdown}`}>{freestyleRhCountdown}</span>
              </div>
            )}
            {/* ===== Freestyle Rythmique 4-line display ===== */}
            {currentMode === "freestyle" && freestyleRhActive && freestyleRhLines.length === 4 ? (
              <div className="rh-lines-container" data-testid="rh-lines-container" key={`scroll-${freestyleRhScrollKey}`}>
                {freestyleRhLines.map((word, lineIdx) => (
                  <div
                    key={`rhline-${freestyleRhScrollKey}-${lineIdx}`}
                    className={`rh-block ${lineIdx === 0 ? 'rh-block-current' : 'rh-block-upcoming'}`}
                    data-testid={`rh-line-${lineIdx}`}
                  >
                    {fillerSelected && lineIdx === 0 && currentFiller && (
                      <div className="filler-phrase" data-testid="filler-phrase">{currentFiller}</div>
                    )}
                    <div className="rh-row">
                      <div className="rh-dots">
                        {[1, 2, 3, 4].map(dot => (
                          <span
                            key={dot}
                            className={`rh-dot ${lineIdx === 0 && freestyleRhPhase === "playing" && dot <= freestyleRhBeat ? 'rh-dot-filled' : ''} ${lineIdx === 0 && freestyleRhPhase === "playing" && dot === freestyleRhBeat ? 'rh-dot-active' : ''}`}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="rh-row">
                      <div className="rh-dots">
                        {[5, 6, 7, 8].map(dot => (
                          <span
                            key={dot}
                            className={`rh-dot ${lineIdx === 0 && freestyleRhPhase === "playing" && dot <= freestyleRhBeat ? 'rh-dot-filled' : ''} ${lineIdx === 0 && freestyleRhPhase === "playing" && dot === freestyleRhBeat ? 'rh-dot-active' : ''}`}
                          />
                        ))}
                      </div>
                      {!fillerSelected && <span className="rh-word">{word}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : currentMode === "freestyle" && rimeGuideeActive && rimeGuideePairs.length > 0 ? (
              <div className="rh-lines-container rg-container" data-testid="rg-container" key={`rg-scroll-${rimeGuideeScrollKey}`}>
                {rimeGuideePhase === "countdown" && (
                  <div className="punchline-countdown-overlay rh-countdown-over-lines" data-testid="rg-countdown-overlay">
                    <span className="freestyle-rh-number countdown">3</span>
                  </div>
                )}
                {rimeGuideePairs.map((pair, pairIdx) => {
                  const globalLine1 = pairIdx * 2;
                  const globalLine2 = pairIdx * 2 + 1;
                  const isCurrentPair = pairIdx === rimeGuideeCurrentPair;
                  const isLine1Active = isCurrentPair && rimeGuideeCurrentLine === 0;
                  const isLine2Active = isCurrentPair && rimeGuideeCurrentLine === 1;
                  const line1Validated = rimeGuideeValidated[globalLine1];
                  const line2Validated = rimeGuideeValidated[globalLine2];
                  const pairOpacity = isCurrentPair ? 1 : 0.3;

                  return (
                    <div key={`rgpair-${rimeGuideeScrollKey}-${pairIdx}`} className="rg-pair" style={{ opacity: pairOpacity }} data-testid={`rg-pair-${pairIdx}`}>
                      {/* Line 1: dots + target word1 */}
                      <div className={`rg-line ${isLine1Active ? 'rg-line-active' : ''} ${line1Validated ? 'rg-line-validated' : ''}`}>
                        <div className="rh-row">
                          <div className="rh-dots">
                            {[1, 2, 3, 4].map(dot => (
                              <span key={dot} className={`rh-dot ${isLine1Active && rimeGuideePhase === "playing" && dot <= rimeGuideeBeat ? 'rh-dot-filled' : ''} ${isLine1Active && rimeGuideePhase === "playing" && dot === rimeGuideeBeat ? 'rh-dot-active' : ''}`} />
                            ))}
                          </div>
                        </div>
                        <div className="rh-row">
                          <div className="rh-dots">
                            {[5, 6, 7, 8].map(dot => (
                              <span key={dot} className={`rh-dot ${isLine1Active && rimeGuideePhase === "playing" && dot <= rimeGuideeBeat ? 'rh-dot-filled' : ''} ${isLine1Active && rimeGuideePhase === "playing" && dot === rimeGuideeBeat ? 'rh-dot-active' : ''}`} />
                            ))}
                          </div>
                          <span className="rh-word">{pair.word1}</span>
                          {line1Validated && <span className="rg-check" data-testid={`rg-check-${globalLine1}`}>&#10003;</span>}
                        </div>
                        {isLine1Active && rimeGuideePhase === "playing" && (
                          <div className="rg-transcript" data-testid="rg-transcript-0">
                            {rimeGuideeTranscript || <span className="rg-transcript-placeholder">Rappe ta phrase...</span>}
                          </div>
                        )}
                      </div>
                      {/* Line 2: dots + target word2 */}
                      <div className={`rg-line ${isLine2Active ? 'rg-line-active' : ''} ${line2Validated ? 'rg-line-validated' : ''}`}>
                        <div className="rh-row">
                          <div className="rh-dots">
                            {[1, 2, 3, 4].map(dot => (
                              <span key={dot} className={`rh-dot ${isLine2Active && rimeGuideePhase === "playing" && dot <= rimeGuideeBeat ? 'rh-dot-filled' : ''} ${isLine2Active && rimeGuideePhase === "playing" && dot === rimeGuideeBeat ? 'rh-dot-active' : ''}`} />
                            ))}
                          </div>
                        </div>
                        <div className="rh-row">
                          <div className="rh-dots">
                            {[5, 6, 7, 8].map(dot => (
                              <span key={dot} className={`rh-dot ${isLine2Active && rimeGuideePhase === "playing" && dot <= rimeGuideeBeat ? 'rh-dot-filled' : ''} ${isLine2Active && rimeGuideePhase === "playing" && dot === rimeGuideeBeat ? 'rh-dot-active' : ''}`} />
                            ))}
                          </div>
                          <span className="rh-word">{pair.word2}</span>
                          {line2Validated && <span className="rg-check" data-testid={`rg-check-${globalLine2}`}>&#10003;</span>}
                        </div>
                        {isLine2Active && rimeGuideePhase === "playing" && (
                          <div className="rg-transcript" data-testid="rg-transcript-1">
                            {rimeGuideeTranscript || <span className="rg-transcript-placeholder">Rappe ta phrase...</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : currentMode === "freestyle" && assisteSelected && (assisteActive || assistePhase === "done") ? (
              <div className="rh-lines-container assiste-container" data-testid="assiste-container">
                {assistePhase === "countdown" && (
                  <div className="punchline-countdown-overlay rh-countdown-over-lines" data-testid="assiste-countdown-overlay">
                    <span className="freestyle-rh-number countdown" key={`assiste-cd-${assisteCountdown}`}>{assisteCountdown}</span>
                  </div>
                )}
                <div className="rh-block rh-block-current" data-testid="assiste-word-block">
                  <div className="rh-row">
                    <div className="rh-dots">
                      {[1, 2, 3, 4].map(dot => (
                        <span
                          key={dot}
                          className={`rh-dot ${assistePhase === "playing" && dot <= assisteBeat ? 'rh-dot-filled' : ''} ${assistePhase === "playing" && dot === assisteBeat ? 'rh-dot-active' : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="rh-row">
                    <div className="rh-dots">
                      {[5, 6, 7, 8].map(dot => (
                        <span
                          key={dot}
                          className={`rh-dot ${assistePhase === "playing" && dot <= assisteBeat ? 'rh-dot-filled' : ''} ${assistePhase === "playing" && dot === assisteBeat ? 'rh-dot-active' : ''}`}
                        />
                      ))}
                    </div>
                    <span className="rh-word">{currentWord}</span>
                  </div>
                </div>
                {assistePhase === "done" && (
                  <div className="assiste-done-message" data-testid="assiste-done-message">
                    Appuie sur ESPACE pour continuer
                  </div>
                )}
              </div>
            ) : (
            <>
            {/* Freestyle Libre mode: no word, just a motivational prompt */}
            {currentMode === "freestyle" && !freestyleRhythmic && !fillerSelected && !rimeGuideeSelected && !assisteSelected && !libreMotSelected ? (
              <div
                className="freestyle-libre-prompt"
                data-testid="freestyle-libre-prompt"
              >
                À toi de jouer !
              </div>
            ) : currentMode === "perso" && personalWords.length === 0 ? null : wordCount > 1 && extraWords.length > 0 ? (
              <div className={`multi-word-grid multi-word-${wordCount}`} data-testid="multi-word-grid">
                <div
                  key={`main-${wordKey}`}
                  className={`word-display word-animate multi-word-item ${challengeActive ? "challenge-active" : ""}`}
                  data-testid="current-word"
                >
                  {currentWord || "..."}
                </div>
                {extraWords.map((w, i) => (
                  <div
                    key={`extra-${wordKey}-${i}`}
                    className={`word-display word-animate multi-word-item ${challengeActive ? "challenge-active" : ""}`}
                    style={{ animationDelay: `${(i + 1) * 0.06}s` }}
                    data-testid={`extra-word-${i}`}
                  >
                    {w}
                  </div>
                ))}
              </div>
            ) : (
              <div
                key={wordKey}
                className={`word-display word-animate ${getWordSizeClass(currentWord)} ${challengeActive ? "challenge-active" : ""} ${currentMode === "punchline" ? "punchline-target" : ""}`}
                data-testid="current-word"
              >
                {currentWord || "..."}
              </div>
            )}
            </>
            )}

            {/* Suggestions (Rhymes, Semantic, or Context) */}
            {currentMode !== "freestyle" && !challengeActive && (
              <div className="suggestions-container" data-testid="suggestions-container">
                {isLoadingSuggestions && !showSuggestions && (
                  <div className="suggestions-loading" data-testid="suggestions-loading">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                )}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="suggestions-list word-animate" data-testid="suggestions-list">
                    {suggestions.map((suggestion, index) => (
                      <span key={index} className="suggestion-word" data-testid={`suggestion-${index}`}>
                        {suggestion}
                      </span>
                    ))}
                  </div>
                )}
                {showSuggestions && suggestions.length === 0 && (
                  <div className="suggestions-empty" data-testid="suggestions-empty">
                    Aucune suggestion disponible
                  </div>
                )}
              </div>
            )}

            {/* Punchline: Result badge */}
            {currentMode === "punchline" && punchlineResult && (
              <div className={`punchline-result-badge ${punchlineResult}`} data-testid="punchline-result">
                {punchlineResult === "success" 
                  ? (lastSpokenWord ? `"${lastSpokenWord}" — Rime OK !` : "Rime OK !")
                  : punchlineResult === "fail"
                    ? (lastSpokenWord ? `"${lastSpokenWord}" ne rime pas avec "${currentWord}"` : "Pas de rime")
                    : "Aucun son capté — parle plus fort ou vérifie le micro"}
              </div>
            )}

            {/* Punchline: Live transcript — verse by verse */}
            {currentMode === "punchline" && (punchlinePhase === "verse1" || punchlinePhase === "verse2") && (
              <div className="punchline-verses" data-testid="punchline-transcript">
                <div className={`punchline-verse ${punchlinePhase === "verse1" ? "active" : "done"}`}>
                  <span className="verse-label">Vers 1</span>
                  <span className="verse-text">{punchlinePhase === "verse1" ? (transcript || "...") : verse1Text || "..."}</span>
                </div>
                {punchlinePhase === "verse2" && (
                  <div className="punchline-verse active">
                    <span className="verse-label">Vers 2</span>
                    <span className="verse-text">{transcript.slice(verse1Text.length).trim() || "..."}</span>
                  </div>
                )}
              </div>
            )}
            {currentMode === "punchline" && punchlinePhase === "waiting" && (
              <div className={`punchline-transcript ${transcript ? "has-text" : ""}`} data-testid="punchline-transcript">
                {transcript || "En écoute..."}
              </div>
            )}

            {/* Talking: Live transcript */}
            {currentMode === "talking" && (talkingPhase === "listening" || talkingPhase === "done") && (
              <div className={`punchline-transcript has-text`} data-testid="talking-transcript">
                {talkingTranscript || "..."}
              </div>
            )}
            {currentMode === "talking" && talkingPhase === "idle" && (
              <div className="punchline-transcript" data-testid="talking-transcript">
                En écoute...
              </div>
            )}
          </div>
        </div>

        {/* Hint Text */}
        <div className={`hint-text ${challengeActive ? "challenge-hint" : ""}`} data-testid="hint-text">
          {getHintText()}
        </div>
      </div>
      )}

      {/* Dashboard (tableau de bord): groups the command bar and the permanent settings bar */}
      {dashboardCollapsed && (
        <button
          className="dashboard-reveal-btn"
          data-testid="dashboard-reveal-btn"
          onClick={() => setDashboardCollapsed(false)}
          aria-label="Afficher le tableau de bord"
        >
          ▲
        </button>
      )}
      <div className={`dashboard ${dashboardCollapsed ? 'dashboard-collapsed' : ''}`} data-testid="dashboard" onMouseDown={resetDashboardHide} onTouchStart={resetDashboardHide} onMouseEnter={handleDashboardEnter} onMouseLeave={handleDashboardLeave}>
      <span className="dashboard-label" data-testid="dashboard-label">Tableau de bord</span>
      {/* Manual collapse button — folds the dashboard immediately instead of
          waiting for the auto-hide timer */}
      {!dashboardCollapsed && (
        <button
          className="dashboard-collapse-btn"
          data-testid="dashboard-collapse-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (dashboardHideTimerRef.current) {
              clearTimeout(dashboardHideTimerRef.current);
              dashboardHideTimerRef.current = null;
            }
            setDashboardCollapsed(true);
          }}
          aria-label="Replier le tableau de bord"
          title="Replier le tableau de bord"
        >
          ▼
        </button>
      )}

      {/* Command Bar */}
      <div className="command-bar" data-testid="command-bar">
        {/* Mode Selector */}
        <div className="control-group mode-selector" data-testid="mode-selector">
          {Object.values(MODES).map((mode) => (
            <Button
              key={mode.id}
              variant={currentMode === mode.id ? "default" : "outline"}
              size="sm"
              onClick={() => handleModeChange(mode.id)}
              data-testid={`mode-btn-${mode.id}`}
              className="focus-visible:ring-2 focus-visible:ring-black"
              disabled={challengeActive}
            >
              {mode.label}
            </Button>
          ))}
        </div>

        {/* Freestyle Sub-modes: Libre / Rythmique / Filler / Rime Guidée */}
        {currentMode === "freestyle" && !challengeActive && !sessionActive && (
          <div className="freestyle-submode-bar" data-testid="freestyle-mode-toggle" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="freestyle-libre-btn"
              className={`freestyle-toggle-btn ${!freestyleRhythmic && !rimeGuideeSelected && !fillerSelected && !assisteSelected && !libreMotSelected && !chrisTurnerSelected ? "active" : ""}`}
              onClick={() => { setFreestyleRhythmic(false); setRimeGuideeSelected(false); setFillerSelected(false); setAssisteSelected(false); setLibreMotSelected(false); setChrisTurnerSelected(false); stopFreestyleRhSequence(); stopRimeGuidee(); stopAssisteSequence(); }}
            >
              Libre
            </button>
            <button
              data-testid="freestyle-libre-mot-btn"
              className={`freestyle-toggle-btn ${libreMotSelected ? "active" : ""}`}
              onClick={() => { setFreestyleRhythmic(false); setRimeGuideeSelected(false); setFillerSelected(false); setAssisteSelected(false); setLibreMotSelected(true); setChrisTurnerSelected(false); stopFreestyleRhSequence(); stopRimeGuidee(); stopAssisteSequence(); }}
            >
              <Sparkles className="h-3.5 w-3.5" /> Libre + mot
            </button>
            <button
              data-testid="freestyle-chris-turner-btn"
              className={`freestyle-toggle-btn ${chrisTurnerSelected ? "active" : ""}`}
              onClick={() => { setFreestyleRhythmic(false); setRimeGuideeSelected(false); setFillerSelected(false); setAssisteSelected(false); setLibreMotSelected(false); setChrisTurnerSelected(true); stopFreestyleRhSequence(); stopRimeGuidee(); stopAssisteSequence(); }}
            >
              <Sparkles className="h-3.5 w-3.5" /> Chris TURNER
            </button>
            <button
              data-testid="freestyle-rythmique-btn"
              className={`freestyle-toggle-btn ${freestyleRhythmic ? "active" : ""}`}
              onClick={() => { setFreestyleRhythmic(true); setRimeGuideeSelected(false); setFillerSelected(false); setAssisteSelected(false); setLibreMotSelected(false); setChrisTurnerSelected(false); stopFreestyleRhSequence(); stopRimeGuidee(); stopAssisteSequence(); }}
            >
              <Music className="h-3.5 w-3.5" /> Rythmique
            </button>
          </div>
        )}

        {/* Banques sub-bar: shows picker + info (idle), or full controls (active) */}
        {currentMode === "banques" && !challengeActive && !sessionActive && (
          <div className="freestyle-submode-bar banques-submode-bar" data-testid="banques-submode-bar" onClick={(e) => e.stopPropagation()}>
            <select
              className="banques-select"
              data-testid="banques-phase-select"
              value={banquesPhase === "idle" ? "discovery" : banquesPhase}
              onChange={(e) => {
                const newPhase = e.target.value;
                setBanquesPhase(newPhase);
                banquesPhaseRef.current = newPhase;
                setBanquesRevealed(false);
                setBanquesMatched([]);
                setBanquesTranscript("");
                banquesTranscriptRef.current = "";
                banquesBaseTranscriptRef.current = "";
                setBanquesTimer(0);
                setBanquesInfinitePaused(false);
                banquesInfinitePausedRef.current = false;
                if (newPhase === "discovery" || newPhase === "infinite") {
                  stopBanquesRecognition();
                }
                // No microphone in remaining sub-modes (Découverte / Test).
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="discovery">Découverte</option>
              <option value="infinite">Test</option>
            </select>
            <div style={{position:'relative'}} ref={banquesPhase === "idle" ? banquesPickerRef : banquesPickerRefActive}>
              <button
                className="banques-select banques-picker-toggle"
                data-testid={banquesPhase === "idle" ? "banques-picker-toggle-idle" : "banques-picker-toggle"}
                onClick={(e) => { e.stopPropagation(); setBanquesPickerOpen(!banquesPickerOpen); }}
              >
                {banquesPhase === "idle"
                  ? <>{banquesSelected.length} banque{banquesSelected.length > 1 ? 's' : ''} <span style={{marginLeft:'0.3rem'}}>&#9660;</span></>
                  : <>{banquesSelected.length}/{BANQUES_DATA.length} <span style={{marginLeft:'0.2rem'}}>&#9660;</span></>}
              </button>
              {banquesPickerOpen && createPortal(
                <div ref={banquesPickerDropdownRef} className="banques-picker-dropdown banques-picker-dropdown-portal" data-testid={banquesPhase === "idle" ? "banques-picker-dropdown" : "banques-picker-dropdown-active"} onClick={(e) => e.stopPropagation()}>
                  <div className="banques-picker-actions">
                    <button className="banques-picker-action" onClick={() => setBanquesSelected(BANQUES_DATA.map(b => b.id))}>Tout</button>
                    <button className="banques-picker-action" onClick={() => setBanquesSelected(BANQUES_DATA.slice(0,10).map(b => b.id))}>1-10</button>
                    <button className="banques-picker-action" onClick={() => setBanquesSelected(BANQUES_DATA.slice(10).map(b => b.id))}>11-31</button>
                    <button className="banques-picker-action" onClick={() => setBanquesSelected([])}>Aucun</button>
                    <button className="banques-picker-action banques-picker-close" data-testid={banquesPhase === "idle" ? "banques-picker-close-idle" : "banques-picker-close-active"} onClick={() => setBanquesPickerOpen(false)}>Fermer</button>
                  </div>
                  {BANQUES_DATA.map((b, idx) => (
                    <div key={b.id} className="banques-picker-item" data-testid={`banques-pick-${b.id}`} style={{ display: 'flex', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={banquesSelected.includes(b.id)}
                          onChange={() => {
                            setBanquesSelected(prev =>
                              prev.includes(b.id) ? prev.filter(x => x !== b.id) : [...prev, b.id]
                            );
                            setBanquesIndex(0);
                            setBanquesRevealed(false);
                            setBanquesMatched([]);
                          }}
                        />
                        <span className="banques-picker-id">{idx + 1}</span>
                        <span className="banques-picker-name">{b.name}</span>
                        {banquesCustomWords[String(b.id)] && (
                          <span className="banques-picker-custom-badge" title="Mots personnalisés">•</span>
                        )}
                      </label>
                      <button
                        type="button"
                        className="banques-picker-edit-btn"
                        data-testid={banquesPhase === "idle" ? `banques-pick-edit-${b.id}` : `banques-pick-edit-active-${b.id}`}
                        title={`Éditer les mots de "${b.name}"`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const existing = banquesCustomWords[String(b.id)];
                          const initial = existing && existing.length > 0
                            ? existing
                            : [...(b.mots1 || []), ...(b.mots2 || []), ...(b.mots3 || [])];
                          setBanquesEditingId(b.id);
                          setBanquesEditText(initial.join("\n"));
                          setBanquesEditOpen(true);
                          setBanquesPickerOpen(false);
                        }}
                      >
                        <Filter className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>,
                document.body
              )}
            </div>
            <select
              className="banques-select"
              data-testid="banques-syllabes-select"
              value={banquesSyllabes}
              onChange={(e) => setBanquesSyllabes(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
            >
              <option value={0}>Syllabes: toutes</option>
              <option value={1}>1 syllabe</option>
              <option value={2}>2 syllabes</option>
              <option value={3}>3 syllabes</option>
            </select>
            {banquesPhase === "infinite" ? (
              <select
                className="banques-select"
                data-testid="banques-infinite-delay-select"
                value={infiniteDelay.toFixed(1)}
                onChange={(e) => setInfiniteDelay(parseFloat(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                title="Intervalle entre deux mots (secondes)"
              >
                {Array.from({ length: 30 }, (_, i) => ((i + 1) / 10)).map(v => (
                  <option key={v} value={v.toFixed(1)}>Délai : {v.toFixed(1)}s</option>
                ))}
              </select>
            ) : (
              <select
                className="banques-select"
                data-testid="banques-word-limit-select"
                value={banquesWordLimit}
                onChange={(e) => setBanquesWordLimit(Number(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                title="Nombre de mots présentés avant de passer à la rime suivante"
              >
                {Array.from({ length: 18 }, (_, i) => i + 3).map(n => (
                  <option key={n} value={n}>Nb. mots : {n}</option>
                ))}
              </select>
            )}
            <button
              className="banques-info-btn"
              data-testid={banquesPhase === "idle" ? "banques-info-btn-idle" : "banques-info-btn"}
              onClick={(e) => { e.stopPropagation(); setBanquesInfoOpen(true); }}
              title="Comment progresser"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Session Control */}
        <div className="control-group" data-testid="session-controls">
          <Dialog open={sessionDialogOpen} onOpenChange={setSessionDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="session-btn"
                className="focus-visible:ring-2 focus-visible:ring-black"
                disabled={sessionActive || challengeActive}
              >
                <Timer className="h-4 w-4 mr-1" />
                Session
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm" data-testid="session-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5" />
                  Mode Session
                </DialogTitle>
                <DialogDescription>
                  Entraînez-vous pendant une durée définie. Le compteur affiche le nombre de mots utilisés.
                </DialogDescription>
              </DialogHeader>
              <div className="session-duration-picker">
                <span className="control-label">Durée</span>
                <div className="duration-buttons">
                  {SESSION_DURATIONS.map((duration) => (
                    <Button
                      key={duration}
                      variant={sessionDuration === duration ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSessionDuration(duration)}
                      data-testid={`duration-${duration}`}
                    >
                      {formatTime(duration)}
                    </Button>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={startSession} data-testid="start-session-btn">
                  <Play className="h-4 w-4 mr-2" />
                  Démarrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Challenge Control */}
        <div className="control-group" data-testid="challenge-controls">
          <Dialog open={challengeDialogOpen} onOpenChange={setChallengeDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="challenge-btn"
                className="focus-visible:ring-2 focus-visible:ring-black challenge-button"
                disabled={sessionActive || challengeActive}
              >
                <Trophy className="h-4 w-4 mr-1" />
                Challenge
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm" data-testid="challenge-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Mode Challenge
                </DialogTitle>
                <DialogDescription>
                  Enchaînez un maximum de mots en 60 secondes ! Battez votre record !
                </DialogDescription>
              </DialogHeader>
              <div className="challenge-stats">
                <div className="challenge-stat">
                  <span className="stat-label">Durée</span>
                  <span className="stat-value">60 secondes</span>
                </div>
                <div className="challenge-stat">
                  <span className="stat-label">Votre record</span>
                  <span className="stat-value highlight">{challengeHighScore} mots</span>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={startChallenge} data-testid="start-challenge-btn" className="challenge-start-btn">
                  <Play className="h-4 w-4 mr-2" />
                  GO !
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Rhythm Source Controls */}
        <div className="control-group rhythm-source-group" data-testid="rhythm-source-controls">
          {/* Play/Pause unified */}
          {!freestyleRhActive && (
            <Button
              variant="outline"
              size="icon"
              onClick={toggleRhythm}
              data-testid="rhythm-toggle"
              className="focus-visible:ring-2 focus-visible:ring-black"
            >
              {(metronomeActive || mp3Playing) ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          )}
          {/* Rewind button (visible when an MP3 is loaded) */}
          {mp3File && !freestyleRhActive && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.currentTime = 0;
                  setMp3Progress(0);
                }
              }}
              data-testid="rhythm-rewind"
              className="focus-visible:ring-2 focus-visible:ring-black"
              title="Retour au début"
            >
              <SkipBack className="h-4 w-4" />
            </Button>
          )}
          {/* Audio progress bar (visible when an MP3 is loaded) */}
          {mp3File && (
            <div className="audio-progress-wrap" data-testid="audio-progress-wrap">
              <div
                className="audio-progress-bar"
                data-testid="audio-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={Math.floor(mp3Duration)}
                aria-valuenow={Math.floor(mp3Progress)}
                onPointerDown={(e) => {
                  if (!audioRef.current || !mp3Duration) return;
                  e.stopPropagation();
                  const seekFromEvent = (clientX) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
                    if (audioRef.current) {
                      audioRef.current.currentTime = pct * mp3Duration;
                      setMp3Progress(audioRef.current.currentTime);
                    }
                  };
                  seekFromEvent(e.clientX);
                  const onMove = (ev) => seekFromEvent(ev.clientX);
                  const onUp = () => {
                    window.removeEventListener('pointermove', onMove);
                    window.removeEventListener('pointerup', onUp);
                  };
                  window.addEventListener('pointermove', onMove);
                  window.addEventListener('pointerup', onUp);
                }}
              >
                <div
                  className="audio-progress-fill"
                  data-testid="audio-progress-fill"
                  style={{ width: `${mp3Duration ? (mp3Progress / mp3Duration) * 100 : 0}%` }}
                />
              </div>
              <span className="audio-progress-time" data-testid="audio-progress-time">
                {formatTime(Math.floor(mp3Progress))} / {formatTime(Math.floor(mp3Duration))}
              </span>
            </div>
          )}
          <div className={`beat-indicator ${beatActive ? "active" : ""}`} data-testid="beat-indicator" />

          {/* BPM display + controls */}
          <div className="bpm-display" data-testid="bpm-display">{bpm}</div>
          <div className="bpm-arrows" data-testid="bpm-arrows">
            <button className="bpm-arrow-btn" data-testid="bpm-up" onClick={() => setBpm(b => Math.min(200, b + 1))} aria-label="Augmenter BPM">&#9650;</button>
            <button className="bpm-arrow-btn" data-testid="bpm-down" onClick={() => setBpm(b => Math.max(40, b - 1))} aria-label="Diminuer BPM">&#9660;</button>
          </div>
          <span className="control-label">BPM</span>
          <div className="slider-container">
            <Slider
              value={[bpm]}
              onValueChange={(v) => setBpm(v[0])}
              min={40}
              max={200}
              step={1}
              data-testid="bpm-slider"
            />
          </div>
        </div>

        {/* Word List Controls */}
        <div className="control-group" data-testid="word-list-controls">
          <Dialog open={wordDialogOpen} onOpenChange={setWordDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="word-list-btn"
                className="focus-visible:ring-2 focus-visible:ring-black"
              >
                <ListMusic className="h-4 w-4 mr-1" />
                Mots
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="word-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mic className="h-5 w-5" />
                  Personnaliser les mots
                </DialogTitle>
                <DialogDescription>
                  Entrez vos mots séparés par des virgules ou des retours à la ligne.
                </DialogDescription>
              </DialogHeader>
              <Textarea
                placeholder="amour, flow, rime, beat, micro..."
                value={customWordsText}
                onChange={(e) => setCustomWordsText(e.target.value)}
                rows={8}
                data-testid="custom-words-textarea"
                className="font-mono text-sm"
              />
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {wordSource === "custom" && (
                  <Button
                    variant="outline"
                    onClick={resetToDefaultWords}
                    data-testid="reset-words-btn"
                  >
                    Réinitialiser
                  </Button>
                )}
                <Button
                  onClick={saveCustomWords}
                  data-testid="save-words-btn"
                >
                  Enregistrer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Permanent Settings Bar (always visible below the command bar) */}
      {dictionaryAvailable && !challengeActive && (
        <div className="filter-bar filter-bar-permanent" data-testid="settings-bar" onClick={(e) => e.stopPropagation()}>
          <div className="filter-group" data-testid="theme-filter">
            <span className="filter-label">Thème</span>
            <Select value={activeTheme} onValueChange={setActiveTheme}>
              <SelectTrigger className="filter-select" data-testid="theme-select">
                <SelectValue placeholder="Tous les thèmes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tous les thèmes</SelectItem>
                {dictionaryThemes.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id}>
                    {theme.label} ({theme.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="filter-group" data-testid="syllable-filter">
            <span className="filter-label">Syllabes</span>
            <Select value={String(activeSyllabes)} onValueChange={(v) => setActiveSyllabes(Number(v))}>
              <SelectTrigger className="filter-select" data-testid="syllable-select">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Toutes</SelectItem>
                <SelectItem value="1">1 syllabe</SelectItem>
                <SelectItem value="2">2 syllabes</SelectItem>
                <SelectItem value="3">3 syllabes</SelectItem>
                <SelectItem value="4">4 syllabes</SelectItem>
                <SelectItem value="5">5+ syllabes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="filter-group" data-testid="wordcount-filter">
            <span className="filter-label">Mots</span>
            <div className="wordcount-selector" data-testid="wordcount-selector">
              {[1, 2, 3, 4].map((n) => (
                <button
                  key={n}
                  className={`wordcount-btn ${wordCount === n ? "active" : ""}`}
                  onClick={() => setWordCount(n)}
                  data-testid={`wordcount-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          {/* Audio source selector — available in ALL modes (music/metronome/silent are independent of the active mode) */}
          <div className="filter-group" data-testid="metronome-filter">
            <span className="filter-label">Source</span>
            <div className="wordcount-selector" data-testid="audio-source-selector" style={{ gap: '0.25rem' }}>
              <button
                className={`wordcount-btn ${audioSource === "music" ? "active" : ""}`}
                onClick={() => setAudioSource("music")}
                data-testid="audio-source-music-btn"
                title="Joue la musique"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', minWidth: 'auto', width: 'auto', padding: '0.35rem 0.7rem', fontSize: '0.78rem' }}
              >
                <Music className="h-3.5 w-3.5" /> Musique
              </button>
              <button
                className={`wordcount-btn ${audioSource === "metronome" ? "active" : ""}`}
                onClick={() => setAudioSource("metronome")}
                data-testid="audio-source-metronome-btn"
                title="Joue le métronome"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', minWidth: 'auto', width: 'auto', padding: '0.35rem 0.7rem', fontSize: '0.78rem' }}
              >
                <Bell className="h-3.5 w-3.5" /> Métronome
              </button>
              <button
                className={`wordcount-btn ${audioSource === "silent" ? "active" : ""}`}
                onClick={() => setAudioSource("silent")}
                data-testid="audio-source-silent-btn"
                title="Pas de musique ni de métronome (a cappella)"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', minWidth: 'auto', width: 'auto', padding: '0.35rem 0.7rem', fontSize: '0.78rem' }}
              >
                <VolumeX className="h-3.5 w-3.5" /> Silence
              </button>
              {/* Music volume slider — only shown when source = music */}
              {audioSource === "music" && (
                <div
                  className="music-volume-control"
                  data-testid="music-volume-control"
                  title={`Volume musique : ${Math.round(musicVolume * 100)}%`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    marginLeft: '0.35rem',
                    padding: '0.25rem 0.6rem',
                    borderRadius: '9999px',
                    background: 'rgba(58, 42, 110, 0.08)',
                    border: '1px solid rgba(58, 42, 110, 0.18)',
                  }}
                >
                  <Volume2 className="h-3.5 w-3.5" style={{ color: '#3a2a6e', flexShrink: 0 }} />
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={musicVolume}
                    onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                    data-testid="music-volume-slider"
                    aria-label="Volume musique"
                    style={{ width: '90px', accentColor: '#3a2a6e', cursor: 'pointer' }}
                  />
                  <span
                    data-testid="music-volume-value"
                    style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3a2a6e', minWidth: '32px', textAlign: 'right' }}
                  >
                    {Math.round(musicVolume * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>
          {/* Music picker — available in all modes when source = music */}
          {audioSource === "music" && (
            <MusicPicker
              backendApi={API}
              isAuthenticated={!!authUser}
              currentMp3={mp3File}
              onSelect={(beat) => {
                setMp3File(beat);
                setUseBuiltInBeats(!!beat?.isBuiltIn);
              }}
            />
          )}
          {/* Admin button — only visible to admin users */}
          {authUser?.is_admin && (
            <button
              type="button"
              className="admin-beats-trigger"
              onClick={() => setAdminBeatsOpen(true)}
              data-testid="admin-beats-open-btn"
              title="Gérer la bibliothèque de beats"
            >
              <Shield className="h-3 w-3" />
              Admin Beats
            </button>
          )}
          {activeTheme !== "ALL" || activeSyllabes > 0 || wordCount > 1 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setActiveTheme("ALL"); setActiveSyllabes(0); setWordCount(1); setExtraWords([]); }}
              data-testid="clear-settings-btn"
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              <X className="h-3 w-3 mr-1" />
              Réinitialiser
            </Button>
          ) : null}
        </div>
      )}
      </div>{/* end .dashboard */}

      {/* Admin Beats Modal — only mounted when admin opens it */}
      {authUser?.is_admin && (
        <AdminBeats
          backendApi={API}
          open={adminBeatsOpen}
          onClose={() => setAdminBeatsOpen(false)}
          onChange={() => {
            /* Beats list refreshed; MusicPicker will re-fetch on next mount/open */
          }}
        />
      )}

      {/* Filler Phrases Editor Dialog */}
      <Dialog open={fillerDialogOpen} onOpenChange={setFillerDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="filler-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Filler Phrases
            </DialogTitle>
            <DialogDescription>
              Une phrase par ligne. Ces phrases de transition s'afficheront au-dessus du mot cible en mode Filler.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={fillerEditorText}
            onChange={(e) => setFillerEditorText(e.target.value)}
            placeholder={"tu vois ce que je veux dire\nc'est comme ça que\nau bout du compte"}
            className="min-h-[200px] font-mono text-sm"
            data-testid="filler-textarea"
          />
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setFillerEditorText(DEFAULT_FILLERS.join("\n")); }}
              data-testid="filler-reset-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Défaut
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const phrases = fillerEditorText.split("\n").map(l => l.trim()).filter(l => l.length > 0);
                saveFillerPhrasesSync(phrases);
                setFillerDialogOpen(false);
              }}
              data-testid="filler-save-btn"
            >
              Sauvegarder ({fillerEditorText.split("\n").filter(l => l.trim()).length} phrases)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Button */}
      <button
        className="help-fab"
        onClick={() => setHelpOpen(true)}
        data-testid="help-btn"
        aria-label="Aide"
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      {/* Banques Edit Words Dialog */}
      <Dialog open={banquesEditOpen} onOpenChange={(open) => { setBanquesEditOpen(open); if (!open) setBanquesEditingId(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="banques-edit-dialog">
          {(() => {
            const editingBanque = (banquesEditingId != null
              ? BANQUES_DATA.find(b => b.id === banquesEditingId)
              : currentBanque) || currentBanque;
            const defaultMots = [...(editingBanque.mots1 || []), ...(editingBanque.mots2 || []), ...(editingBanque.mots3 || [])];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Mots — {editingBanque.name}
                  </DialogTitle>
                  <DialogDescription>
                    Un mot par ligne. Tes mots remplaceront ceux du dictionnaire pour cette banque.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  value={banquesEditText}
                  onChange={(e) => setBanquesEditText(e.target.value)}
                  placeholder={"action\nréaction\npression\ntension"}
                  className="min-h-[200px] font-mono text-sm"
                  data-testid="banques-edit-textarea"
                />
                <DialogFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBanquesEditText(defaultMots.join("\n"))}
                    data-testid="banques-edit-reset-btn"
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Défaut
                  </Button>
                  {banquesCustomWords[String(editingBanque.id)] && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        saveBanqueWordsSync(editingBanque.id, []);
                        setBanquesEditOpen(false);
                        setBanquesEditingId(null);
                      }}
                      data-testid="banques-edit-clear-btn"
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Supprimer mes mots
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      const words = banquesEditText.split("\n").map(l => l.trim()).filter(l => l.length > 0);
                      saveBanqueWordsSync(editingBanque.id, words);
                      setBanquesEditOpen(false);
                      setBanquesEditingId(null);
                    }}
                    data-testid="banques-edit-save-btn"
                  >
                    Sauvegarder ({banquesEditText.split("\n").filter(l => l.trim()).length} mots)
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Banques Info Dialog */}
      <Dialog open={banquesInfoOpen} onOpenChange={setBanquesInfoOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="banques-info-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Guide des Banques de Rimes
            </DialogTitle>
          </DialogHeader>
          <div style={{ fontSize: '0.9rem', lineHeight: '1.6', color: '#333' }}>
            <h4 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Organisation</h4>
            <p>Les 31 banques couvrent toutes les terminaisons courantes du rap français. Elles sont classées par <strong>fréquence d'utilisation</strong> en freestyle :</p>

            <div style={{ background: '#f8f8f8', borderRadius: '8px', padding: '0.8rem', margin: '0.8rem 0' }}>
              <p style={{ fontWeight: 700, marginBottom: '0.3rem' }}>Banques 1 à 10 — Les incontournables</p>
              <p style={{ fontSize: '0.85rem', color: '#555' }}>-é/-er/-ai, -tion, -age, -ique, -eur, -ance, -ment, -al, -ère, -ure</p>
              <p style={{ fontSize: '0.85rem' }}>Ce sont les terminaisons que tu entendras dans <strong>80% des freestyles</strong>. Commence ici.</p>
            </div>

            <div style={{ background: '#f8f8f8', borderRadius: '8px', padding: '0.8rem', margin: '0.8rem 0' }}>
              <p style={{ fontWeight: 700, marginBottom: '0.3rem' }}>Banques 11 à 31 — L'arsenal complet</p>
              <p style={{ fontSize: '0.85rem', color: '#555' }}>-ème, -i, -oir, -isme, -able, -iste, -on, -an, -ou, -aine, -ace, -ine, -ose, -ame, -ide, -ette, -a, -il, -ende, -estre, -erle</p>
              <p style={{ fontSize: '0.85rem' }}>Des terminaisons moins fréquentes mais qui font la différence entre un bon et un excellent freestyler.</p>
            </div>

            <h4 style={{ fontWeight: 700, margin: '1rem 0 0.5rem' }}>Progression recommandée</h4>
            <ol style={{ paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
              <li style={{ marginBottom: '0.4rem' }}><strong>Étape 1 :</strong> Sélectionne <strong>1 banque</strong>. En mode Découverte, écoute et répète les 8 mots jusqu'à les connaître.</li>
              <li style={{ marginBottom: '0.4rem' }}><strong>Étape 2 :</strong> Augmente progressivement le nombre de banques (2, 3, 5...) pour mélanger les sons.</li>
              <li style={{ marginBottom: '0.4rem' }}><strong>Étape 3 :</strong> Quand les 10 premières sont solides, ajoute les banques 11 et suivantes.</li>
              <li style={{ marginBottom: '0.4rem' }}><strong>Étape 4 :</strong> Le mode <strong>Test</strong> enchaîne toutes tes banques — parfait pour la révision quotidienne.</li>
            </ol>

            <h4 style={{ fontWeight: 700, margin: '1rem 0 0.5rem' }}>Contrôles</h4>
            <ul style={{ paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
              <li><strong>Tap / Espace</strong> = mot suivant ou révéler</li>
              <li><strong>Double-tap / Maintenir Espace</strong> = pause / reprendre</li>
            </ul>
          </div>
        </DialogContent>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="help-dialog sm:max-w-2xl max-h-[85vh]" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="help-dialog-title">
              <Sparkles className="h-5 w-5" />
              Guide Freestyle Flow
            </DialogTitle>
            <DialogDescription>
              Tout ce qu'il faut savoir pour devenir un monstre du freestyle.
            </DialogDescription>
          </DialogHeader>

          {/* Section Navigation */}
          <div className="help-nav" data-testid="help-nav">
            {[
              { icon: <Keyboard className="h-3.5 w-3.5" />, label: "Les bases" },
              { icon: <Layers className="h-3.5 w-3.5" />, label: "Les modes" },
              { icon: <Mic className="h-3.5 w-3.5" />, label: "Punch-line" },
              { icon: <BookOpen className="h-3.5 w-3.5" />, label: "Dictionnaire" },
              { icon: <Music className="h-3.5 w-3.5" />, label: "Musique" },
              { icon: <Target className="h-3.5 w-3.5" />, label: "Astuces" },
            ].map((tab, i) => (
              <button
                key={i}
                className={`help-nav-item ${helpSection === i ? "active" : ""}`}
                onClick={() => setHelpSection(i)}
                data-testid={`help-tab-${i}`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Section Content */}
          <div className="help-content" data-testid="help-content">

            {helpSection === 0 && (
              <div className="help-section">
                <h3>Les bases</h3>
                <p>Freestyle Flow affiche un mot au centre de l'écran. Ton objectif : improviser des rimes, des punchlines ou des flows autour de ce mot.</p>

                <div className="help-card">
                  <div className="help-card-title">Contrôles</div>
                  <ul>
                    <li><kbd>Espace</kbd> ou <strong>clic</strong> sur l'écran pour passer au mot suivant</li>
                    <li>Le mot est choisi aléatoirement parmi le dictionnaire actif</li>
                    <li>Utilise la <strong>barre de réglages</strong> en bas pour cibler un thème, un nombre de syllabes ou afficher plusieurs mots</li>
                  </ul>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Mots personnalisés</div>
                  <p>Clique sur <strong>Mots</strong> dans la barre de commande pour importer ta propre liste. Entre tes mots séparés par des virgules ou des retours à la ligne. Tu peux revenir au dictionnaire à tout moment.</p>
                </div>
              </div>
            )}

            {helpSection === 1 && (
              <div className="help-section">
                <h3>Les modes</h3>

                <div className="help-card">
                  <div className="help-card-title">Freestyle</div>
                  <p><strong>Objectif :</strong> Trouver une rime au mot affiché.</p>
                  <p><strong>Fonctionnement :</strong> À chaque pression sur la barre d'espacement, un nouveau mot s'affiche.</p>
                  <p><strong>Tips :</strong> Fonctionne en mode Session ou en mode Challenge. Il est possible de choisir le nombre de syllabes des mots.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Perso</div>
                  <p><strong>Objectif :</strong> S'entraîner avec tes propres mots.</p>
                  <p><strong>Fonctionnement :</strong> Même principe que le Freestyle, mais les mots affichés viennent de ta liste personnelle. Appuie sur Espace pour changer de mot.</p>
                  <p><strong>Tips :</strong> Ajoute les mots qui te posent problème ou ceux que tu veux intégrer dans tes textes. Fonctionne aussi avec Session et Challenge.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Rimes</div>
                  <p><strong>Objectif :</strong> S'entraîner à enchaîner des rimes.</p>
                  <p><strong>Fonctionnement :</strong> Un mot principal s'affiche accompagné de 4 mots qui riment. Appuie sur Espace pour changer de mot.</p>
                  <p><strong>Tips :</strong> Utilise les rimes affichées comme tremplin pour construire des phrases qui relient plusieurs de ces mots.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Sémantique</div>
                  <p><strong>Objectif :</strong> Enrichir ses associations d'idées.</p>
                  <p><strong>Fonctionnement :</strong> Un mot principal s'affiche avec 4 mots du même champ sémantique. Appuie sur Espace pour changer de mot.</p>
                  <p><strong>Tips :</strong> Parfait pour travailler les métaphores et les comparaisons. Essaie de tisser des liens entre les mots affichés.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Contexte</div>
                  <p><strong>Objectif :</strong> Improviser autour d'un personnage fictif.</p>
                  <p><strong>Fonctionnement :</strong> L'IA génère un portrait avec 3 mots-clés. Appuie sur Espace pour découvrir un nouveau personnage.</p>
                  <p><strong>Tips :</strong> Incarne le personnage ou raconte son histoire. Laisse-toi porter par l'image pour inventer des récits.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Punch-line</div>
                  <p><strong>Objectif :</strong> Placer une rime en temps réel au micro.</p>
                  <p><strong>Fonctionnement :</strong> Un mot-cible s'affiche (souligné). Rappe ton vers, l'app évalue si ta dernière syllabe rime. Flash vert = réussi, rouge = raté.</p>
                  <p><strong>Tips :</strong> Active le métronome à 80 BPM et utilise un casque. Essaie de monter ton streak. Voir l'onglet « Punch-line » pour les détails.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Flow</div>
                  <p><strong>Objectif :</strong> Parler le plus longtemps possible sans s'arrêter.</p>
                  <p><strong>Fonctionnement :</strong> Le timer démarre dès que tu parles. Si tu t'arrêtes plus de 3 secondes, c'est terminé. Ton record est sauvegardé.</p>
                  <p><strong>Tips :</strong> Ne réfléchis pas, dis tout ce qui te passe par la tête. Le but est de tenir le flow sans pause.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Banques</div>
                  <p><strong>Objectif :</strong> Maîtriser les 25 familles de sons du rap français.</p>

                  <p><strong>Organisation :</strong> Les 31 banques sont classées par fréquence d'utilisation en freestyle :</p>
                  <ul>
                    <li><strong>Banques 1 à 10 — Les incontournables :</strong> Ce sont les terminaisons que tu entendras (et utiliseras) dans 80% des freestyles. -é/-er/-ai, -tion, -age, -ique, -eur, -ance, -ment, -al, -ère, -ure. Commence par celles-ci.</li>
                    <li><strong>Banques 11 à 31 — L'arsenal complet :</strong> Des terminaisons moins fréquentes mais qui font la différence entre un bon freestyler et un excellent. -ème, -i, -oir, -isme, -able, -iste, -on, -an, -ou, -aine, -ace, -ine, -ose, -ame, -ide, -ette, -a, -il, -ende, -estre, -erle.</li>
                  </ul>

                  <p><strong>Progression recommandée :</strong></p>
                  <ol>
                    <li>Commence avec <strong>1 banque</strong> en mode Découverte. Écoute, répète, mémorise les 8 mots.</li>
                    <li>Augmente progressivement le nombre de banques (2, 3, 5...) pour mélanger les sons.</li>
                    <li>Quand les 10 premières sont solides, ajoute les banques 11+.</li>
                    <li>Le mode <strong>Test</strong> enchaîne toutes tes banques automatiquement — parfait pour la révision.</li>
                  </ol>

                  <p><strong>Contrôles :</strong></p>
                  <ul>
                    <li><strong>Tap / Espace</strong> = mot suivant ou révéler</li>
                    <li><strong>Double-tap / Maintenir Espace</strong> = pause / reprendre</li>
                  </ul>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Session</div>
                  <p><strong>Objectif :</strong> S'entraîner sur une durée définie.</p>
                  <p><strong>Fonctionnement :</strong> Choisis une durée (1, 3, 5, 10 ou 15 min) et lance-toi. Le compteur de mots suit ta progression en temps réel.</p>
                  <p><strong>Tips :</strong> Idéal pour structurer un entraînement régulier. Combine avec un filtre thème pour varier les sessions.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Challenge</div>
                  <p><strong>Objectif :</strong> Scorer un maximum de mots en 60 secondes.</p>
                  <p><strong>Fonctionnement :</strong> Tu as 60 secondes. Chaque pression sur Espace compte un mot. Ton meilleur score est sauvegardé.</p>
                  <p><strong>Tips :</strong> Essaie de battre ton record à chaque session. Augmente le BPM pour corser la difficulté.</p>
                </div>
              </div>
            )}

            {helpSection === 2 && (
              <div className="help-section">
                <h3>Mode Punch-line</h3>
                <p>Le mode le plus interactif. L'application écoute ton freestyle en temps réel via le micro et évalue si tes rimes sont correctes.</p>

                <div className="help-card">
                  <div className="help-card-title">Comment ça marche</div>
                  <ol>
                    <li>Un mot s'affiche : c'est ta <strong>rime cible</strong> (souligné)</li>
                    <li>Tu rappes ton <strong>vers 1</strong> — le dernier mot doit rimer avec le mot affiché</li>
                    <li>Appuie sur <kbd>Espace</kbd> à la fin du vers 1 pour l'évaluation instantanée</li>
                    <li>Flash vert = rime OK, flash rouge = raté</li>
                    <li>Tu rappes ton <strong>vers 2</strong> en plaçant le mot affiché à la fin</li>
                    <li>Appuie sur <kbd>Espace</kbd> pour passer au mot suivant</li>
                  </ol>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Détection automatique</div>
                  <p>L'évaluation se déclenche aussi automatiquement après 1 mesure (4 temps) basée sur le BPM actuel. Active le métronome à 80 BPM pour une expérience optimale. La barre d'espace reste prioritaire pour un contrôle manuel.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Streak</div>
                  <p>Chaque rime réussie incrémente ton compteur de streak. Enchaîne les punch-lines sans erreur pour monter ton record ! Le meilleur streak est sauvegardé.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Prérequis</div>
                  <ul>
                    <li>Navigateur : Chrome ou Edge (reconnaissance vocale)</li>
                    <li>Autorise l'accès au micro quand le navigateur le demande</li>
                    <li>Conseil : utilise un casque pour éviter que la musique interfère avec la reconnaissance</li>
                  </ul>
                </div>
              </div>
            )}

            {helpSection === 3 && (
              <div className="help-section">
                <h3>Le dictionnaire</h3>
                <p>Freestyle Flow embarque un dictionnaire de <strong>plus de 16 000 mots français</strong> classés par thème et par nombre de syllabes.</p>

                <div className="help-card">
                  <div className="help-card-title">20 thèmes disponibles</div>
                  <p>Amour, Argent, Rue, Combat, Gloire, Famille, Trahison, Liberté, Mort, Rêve, Temps, Nuit, Pouvoir, Souffrance, Fierté, Rap, Musique, Vantardise, Mes Origines, Mon Futur.</p>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Réglages</div>
                  <ul>
                    <li>La barre de réglages est <strong>toujours affichée</strong> sous la barre de commande</li>
                    <li><strong>Thème</strong> : restreint les mots à un univers précis</li>
                    <li><strong>Syllabes</strong> : filtre par nombre de syllabes (1 à 5+)</li>
                    <li><strong>Mots</strong> : affiche 1, 2, 3 ou 4 mots simultanément à l'écran</li>
                    <li><strong>Source</strong> (en mode Freestyle) : Musique, Métronome ou Silence</li>
                    <li>Combine les réglages pour un entraînement ultra-ciblé</li>
                    <li><strong>Réinitialiser</strong> pour revenir aux valeurs par défaut</li>
                  </ul>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Astuce</div>
                  <p>Filtre sur 2 syllabes pour travailler la rapidité, ou sur 4-5 syllabes pour les placements techniques. Active 2 ou 3 mots en même temps pour travailler les enchaînements et les associations. Change de thème entre les sessions pour diversifier ton vocabulaire.</p>
                </div>
              </div>
            )}

            {helpSection === 4 && (
              <div className="help-section">
                <h3>Musique</h3>

                <div className="help-card">
                  <div className="help-card-title">Métronome</div>
                  <ul>
                    <li>Clique sur <Play className="inline h-3 w-3" /> pour activer le clic</li>
                    <li>Ajuste le BPM avec le slider (40 à 200 BPM)</li>
                    <li>Le point lumineux pulse sur le tempo pour te guider visuellement</li>
                    <li>Commence à 80-90 BPM pour t'échauffer, monte progressivement</li>
                  </ul>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Beat / MP3</div>
                  <ul>
                    <li>Un beat par défaut est pré-chargé pour démarrer immédiatement</li>
                    <li>Clique sur <strong>Beat</strong> pour uploader ton propre instrumental (MP3, max 20 Mo)</li>
                    <li>Contrôles : lecture/pause, sourdine, barre de progression</li>
                    <li>Le beat tourne en boucle pour un entraînement continu</li>
                  </ul>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Conseil</div>
                  <p>Utilise le métronome seul pour travailler le placement, puis ajoute un beat pour s'entraîner en conditions réelles. Les deux peuvent fonctionner en même temps.</p>
                </div>
              </div>
            )}

            {helpSection === 5 && (
              <div className="help-section">
                <h3>Astuces pour progresser</h3>

                <div className="help-card">
                  <div className="help-card-title">Routine d'entraînement</div>
                  <ol>
                    <li><strong>Échauffement</strong> (5 min) — Mode Freestyle, tous thèmes, 80 BPM</li>
                    <li><strong>Technique</strong> (10 min) — Mode Rimes, filtre 2-3 syllabes, 90 BPM</li>
                    <li><strong>Créativité</strong> (5 min) — Mode Contexte, se laisser porter par les portraits</li>
                    <li><strong>Défi</strong> (5 min) — Mode Challenge, essayer de battre son record</li>
                    <li><strong>Thématique</strong> (10 min) — Choisir un thème et improviser une histoire</li>
                  </ol>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Tips de pro</div>
                  <ul>
                    <li>Ne t'arrête jamais de rapper, même si tu galères sur un mot</li>
                    <li>Utilise les mots du mode Sémantique pour enrichir tes métaphores</li>
                    <li>Entraîne-toi avec des thèmes que tu maîtrises moins</li>
                    <li>Augmente le BPM de 5 en 5 chaque semaine</li>
                    <li>Enregistre-toi et réécoute pour identifier tes axes de progression</li>
                  </ul>
                </div>

                <div className="help-card">
                  <div className="help-card-title">Raccourcis clavier</div>
                  <ul>
                    <li><kbd>Espace</kbd> — Mot suivant</li>
                    <li><kbd>Clic</kbd> — Mot suivant (alternatif)</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Footer */}
          <div className="help-footer">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHelpSection(Math.max(0, helpSection - 1))}
              disabled={helpSection === 0}
              data-testid="help-prev-btn"
            >
              Précédent
            </Button>
            <span className="help-page-indicator">{helpSection + 1} / 6</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHelpSection(Math.min(5, helpSection + 1))}
              disabled={helpSection === 5}
              data-testid="help-next-btn"
            >
              Suivant
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Personal words editor modal */}
      {showPersoEditor && (
        <div
          data-testid="perso-editor-overlay"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => setShowPersoEditor(false)}
        >
          <div
            data-testid="perso-editor-modal"
            style={{
              background: '#fff', borderRadius: '1rem', padding: '1.5rem',
              width: '90%', maxWidth: '480px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', gap: '1rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              Éditer ma liste perso
              {authUser && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.7rem', color: '#22c55e', fontWeight: 500 }}>
                  <Cloud className="h-3 w-3" /> Synchronisée
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#737373' }}>
              Un mot par ligne. Les doublons et lignes vides sont ignorés.
              {!authUser && (
                <span style={{ display: 'block', marginTop: '0.25rem', color: '#f59e0b', fontSize: '0.75rem' }}>
                  Connecte-toi pour sauvegarder ta liste dans le cloud et y accéder partout.
                </span>
              )}
            </div>
            <textarea
              data-testid="perso-editor-textarea"
              value={persoEditorText}
              onChange={(e) => setPersoEditorText(e.target.value)}
              placeholder={"amour\nliberté\npassion\ndestin\nflamme"}
              style={{
                width: '100%', height: '300px', border: '1px solid #e5e5e5', borderRadius: '0.5rem',
                padding: '0.75rem', fontSize: '1rem', fontFamily: 'inherit', resize: 'vertical',
                outline: 'none',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                data-testid="perso-editor-cancel"
                onClick={() => setShowPersoEditor(false)}
                style={{
                  padding: '0.5rem 1.2rem', borderRadius: '9999px', border: '1px solid #e5e5e5',
                  background: '#fff', cursor: 'pointer', fontSize: '0.9rem',
                }}
              >
                Annuler
              </button>
              <button
                data-testid="perso-editor-save"
                onClick={() => {
                  const newWords = [...new Set(
                    persoEditorText
                      .split('\n')
                      .map(w => w.trim())
                      .filter(w => w.length > 0)
                  )];
                  savePersonalWordsSync(newWords);
                  setShowPersoEditor(false);
                  if (newWords.length > 0 && currentMode === "perso") {
                    const first = newWords[Math.floor(Math.random() * newWords.length)];
                    setCurrentWord(first);
                    setWordKey(k => k + 1);
                  }
                }}
                style={{
                  padding: '0.5rem 1.2rem', borderRadius: '9999px', border: 'none',
                  background: 'linear-gradient(135deg, #1a1a2e, #16213e)', color: '#fff',
                  cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
                }}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
